import {
  collection,
  doc,
  getDocs,
  getDoc,
  getDocFromServer,
  getDocsFromServer,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  where,
  arrayUnion,
  arrayRemove,
  increment,
  deleteField,
  FieldPath,
  FieldValue,
  runTransaction,
  writeBatch,
  onSnapshot
} from 'firebase/firestore';
import { db, auth } from './firebase';
import { hydrateQuestions, prepareQuizForStorage, hasAnswerContent, type QuizAnswerKey } from './quizAnswerKey';
import { computeReleasedAverages, removedGradeKeys, sameMarkSetCategories, STUDENT_PII_FIELDS, STUDENT_DROP_FIELDS } from './utils';
import { MERGE_BACK_TO_STUDENT, mergeBackStudentPrivate } from './studentPiiFields';
import { snapshotPiiOffenders } from './deletionSnapshot';
import { assertHistoryCanBeCleared, assertRecordCanBeRestored } from './studentObservationDeletion';
import { savePendingObservationDeletionHistory } from './pendingObservationDeletionHistory';
import { assertNoVaultLeak } from './vault/vaultLeakGuard';
import { VAULTED_IMPORT_FIELDS } from './vault/importRouting';
import { sameCurriculumCourse, DUPLICATE_LINK_MESSAGE } from './curriculumLinks';
import { weightingToRelease } from './releaseStaleness';
import { deriveReaders } from './gradeReaders';
import { splitReleasedGrades } from './releaseEligibility';
import { splitSubmissionPrivate, hasSubmissionPrivate, mergeSubmissionPrivate, unionThread, pruneThread } from './submissionPrivateFields';
import { planGradeWrite } from './gradeWritePlan';
import { stripKinderResults, writeKinderResultsDoc } from './kinderResults';
import {
  normalizeKinderLabels, appendKinderLabel, applyStudentKinderLabel, removeKinderLabel,
  applyStudentKinderYear, type KinderYear,
} from './kinderLabels';
import { normalizeKinderKeys, type KinderKeys } from './kinderKeys';
import { normalizeHiddenModes, normalizeTabLabels } from './navPreferences';
import { normalizeDisplayPrefs } from './displayPrefs';
import { rosterIsMasked, isMaskedStudent } from './maskNames';
import { newKinderId } from './kinderAssessment';
import { purgeStudentResidue, computeResidueUpdates } from './studentPurge';
// Pure + firebase-free so it stays unit-testable without credentials (see the module).
import { diffAttendanceDay } from './attendanceAudit';
export { diffAttendanceDay };
// Re-export so existing callers (App.tsx) can keep importing it from dataService.
export { removedGradeKeys };
import { ClassData, MarkSet, Student, StudentPrivate, Assessment, Grade, DeletionRecord, AssessmentTemplate, CurriculumLink, GradeScale, PathwayTemplate, PublicTemplate, BoardDocument, SchoolDocument, SchoolStaffRecord, SchoolTeacherRecord, InvitationDocument, SchoolAssociation, SisProfile, SchoolStudent, SchoolPeriod, ReportingPeriod, ReportSubmission, ClassMarkSnapshot, ClassTransferDocument, ReportTemplateField, NotificationDocument, Enrollment, SchoolEmail, SchoolEmailTemplate, InternalMessage, Conversation, AttendanceDay, UserProfile, GradeHistoryEntry, AttendanceHistoryEntry, StreamPost, StreamAttachment, PopQuiz, RemovedMember , ClassroomPostSyncState} from '../types';
import { remapAssessments } from './markSetRemap';
import { clearNameStampsIfRenamed } from './vault/nameStamps';
import { planDenormalisedNameRewrite } from './sanitise/sanitiseRoster';
import { stripUndefinedDeep } from './classDuplication';

// Helper to remove undefined values from objects (Firebase doesn't accept undefined)
// Exported for use in other components that directly call updateDoc
export const removeUndefined = (obj: Record<string, any>): Record<string, any> => {
  const cleaned: Record<string, any> = {};
  for (const key in obj) {
    if (obj[key] !== undefined) {
      if (Array.isArray(obj[key])) {
        // Recursively clean objects within arrays
        cleaned[key] = obj[key].map((item: any) =>
          item !== null && typeof item === 'object' ? removeUndefined(item) : item
        );
      } else if (obj[key] instanceof FieldValue) {
        // Preserve Firestore sentinels (deleteField(), increment(), arrayUnion…).
        // Recursing into one returns a plain {} and silently corrupts the write.
        cleaned[key] = obj[key];
      } else if (obj[key] !== null && typeof obj[key] === 'object') {
        cleaned[key] = removeUndefined(obj[key]);
      } else {
        cleaned[key] = obj[key];
      }
    }
  }
  return cleaned;
};

// Generate a random join code.
// We deliberately skip a client-side collision check: Firestore rules now
// restrict listing the classes collection to members + owners, so a "is this
// code in use?" probe would fail. At 32^6 ≈ 1 billion possible 6-char codes
// vs the current scale, collision probability is negligible. If it ever
// matters, move the uniqueness check server-side and have it use Admin SDK.
const generateJoinCode = async (): Promise<string> => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

// Get all classes for a teacher - uses getDocsFromServer to bypass cache
export const getTeacherClasses = async (teacherId: string): Promise<ClassData[]> => {
  // Owned classes + classes shared with this teacher as a co-teacher. Firestore
  // can't OR across fields in one query, so run both and merge (dedupe by id).
  const ownedQ = query(collection(db, 'classes'), where('ownerId', '==', teacherId));
  const coQ = query(collection(db, 'classes'), where('coTeachers', 'array-contains', teacherId));
  const [ownedSnap, coSnap] = await Promise.all([
    getDocsFromServer(ownedQ),
    getDocsFromServer(coQ).catch(() => ({ docs: [] as any[] })), // tolerate if index/field absent
  ]);

  console.log('getTeacherClasses: Fetched', ownedSnap.docs.length, 'owned +', coSnap.docs.length, 'co-taught');

  const byId = new Map<string, ClassData>();
  for (const doc of [...ownedSnap.docs, ...coSnap.docs]) {
    if (!doc.exists()) continue;
    // doc.id LAST so it always wins over any stale `id` field in the doc data (a
    // pre-fix restore/duplicate could have persisted one) — otherwise a class would
    // surface under a wrong/blocklisted id and vanish. Heals such "ghost" docs.
    if (!byId.has(doc.id)) byId.set(doc.id, { ...doc.data(), id: doc.id } as ClassData);
  }
  const validClasses = Array.from(byId.values());

  // Hydrate owner-only student PII (parent contact, OEN, etc.) from each class's
  // private roster doc — it's not in the classmate-readable embedded array.
  await Promise.all(validClasses.map(c => hydrateStudentPrivate(c)));
  // The private half of each submission — thread, confidential note, self-assessment
  // — is not on the class doc either, and the teacher is entitled to all of it.
  await Promise.all(validClasses.map(c => hydrateSubmissionPrivate(c)));
  // Same for per-assignment AI grading instructions (teacher-only).
  await Promise.all(validClasses.map(c => Promise.all([hydrateAiInstructions(c), hydrateQuizAnswerKeys(c)])));

  console.log('getTeacherClasses: Returning', validClasses.length, 'valid classes');
  return validClasses;
};

/**
 * Classes this teacher has JOINED as a participant (a learner in someone else's class — PD /
 * staff space), NOT ones they own or co-teach.
 *
 * This went through `student-class-view` along with the student portal, and for exactly the same
 * reason. It used to run `where('classMembers','array-contains', uid)` and its own comment said
 * the quiet part out loud: "a participant reads only the classmate-visible class doc". That doc
 * carried the host class's live grade map, released grades and released averages for every
 * participant, because Firestore rules cannot filter fields. A teacher sitting in someone else's
 * PD class is a participant, not staff, and had no more business reading their colleagues' marks
 * than a student has reading their classmates'.
 *
 * The endpoint applies the same allowlist and the same refusals (template, archived, own class,
 * not on the roster), so the filter chain that used to run here moved there with it — and is now
 * enforcement rather than advice applied after the data had already arrived.
 */
export const getJoinedClasses = async (_teacherId: string): Promise<ClassData[]> => {
  const { fetchStudentClasses } = await import('./studentClassViewService');
  return fetchStudentClasses().catch(() => []) as Promise<ClassData[]>;
};

/**
 * Live subscription to a teacher's classes for the NOTIFICATION feed only.
 * Runs onSnapshot over both owned + co-taught queries and merges, so the bell's
 * unread count updates in real time as students submit work, reply, or message —
 * no page refresh. Deliberately skips the heavy PII/AI-instruction hydration that
 * getTeacherClasses does: notifications only read `submissions` and `stream`, which
 * live on the class doc itself. Returns an unsubscribe that tears down both listeners.
 */
export const subscribeToTeacherClassActivity = (
  teacherId: string,
  onUpdate: (classes: ClassData[]) => void,
): (() => void) => {
  const ownedQ = query(collection(db, 'classes'), where('ownerId', '==', teacherId));
  const coQ = query(collection(db, 'classes'), where('coTeachers', 'array-contains', teacherId));
  let owned: ClassData[] = [];
  let co: ClassData[] = [];
  let ownedReady = false;
  const emit = () => {
    // Only emit once at least the owned listener has reported, so we never flash an
    // empty list over the initial prop-provided classes.
    if (!ownedReady) return;
    const byId = new Map<string, ClassData>();
    for (const c of [...owned, ...co]) if (!byId.has(c.id)) byId.set(c.id, c);
    onUpdate(Array.from(byId.values()));
  };
  const unsubOwned = onSnapshot(ownedQ, snap => {
    owned = snap.docs.filter(d => d.exists()).map(d => ({ ...d.data(), id: d.id }) as ClassData);
    ownedReady = true;
    emit();
  }, () => { /* listener error — leave ownedReady false so we never emit an empty list
                over the prop-provided classes; the bell keeps using those. */ });
  const unsubCo = onSnapshot(coQ, snap => {
    co = snap.docs.filter(d => d.exists()).map(d => ({ ...d.data(), id: d.id }) as ClassData);
    emit();
  }, () => { /* tolerate missing index/field — owned classes still drive the feed */ });
  return () => { unsubOwned(); unsubCo(); };
};

// Get all classes belonging to a school
export const getSchoolClasses = async (schoolId: string): Promise<ClassData[]> => {
  const q = query(collection(db, 'classes'), where('schoolId', '==', schoolId));
  const snapshot = await getDocs(q);
  // Templates are excluded: they're a teacher's private content shell, not a course
  // the school administers. Listing them let admin surfaces offer roster assignment,
  // attendance and reporting against something that can never have students.
  // Archived classes ARE returned. Filtering them here looked tidy and was
  // wrong twice over: the school-wide Export Grades / Attendance buttons run off
  // this list, so an archived semester would have vanished from a records export
  // with nothing saying so; and archiving would have become a unilateral way for
  // a teacher to drop their class out of admin oversight and the unanswered-
  // question monitor. Consumers that want only live classes filter at the call
  // site, which is what ReportCardAdminPanel and SchoolAttendanceView already do.
  const classes = snapshot.docs
    .filter(d => d.exists() && d.data()?.isTemplate !== true)
    .map(d => ({ ...d.data(), id: d.id })) as ClassData[];
  // School admins are authorized to read the private roster (parent contact, OEN).
  await Promise.all(classes.map(c => hydrateStudentPrivate(c)));
  await Promise.all(classes.map(c => hydrateSubmissionPrivate(c)));
  await Promise.all(classes.map(c => Promise.all([hydrateAiInstructions(c), hydrateQuizAnswerKeys(c)])));
  return classes;
};

// ─── Enrollment Bridge ──────────────────────────────────────────────────────

/** Idempotent enrollment — won't duplicate if already exists */
export const ensureEnrollment = async (
  schoolId: string,
  schoolStudentId: string,
  classId: string,
  source: Enrollment['source'],
  enrolledBy?: string,
): Promise<void> => {
  const q = query(
    collection(db, `schools/${schoolId}/enrollments`),
    where('schoolStudentId', '==', schoolStudentId),
    where('classId', '==', classId),
    where('status', '==', 'active'),
  );
  const existing = await getDocs(q);
  if (!existing.empty) return; // Already enrolled

  await addDoc(
    collection(db, `schools/${schoolId}/enrollments`),
    removeUndefined({
      schoolId,
      schoolStudentId,
      classId,
      status: 'active',
      source,
      enrolledAt: new Date().toISOString(),
      enrolledBy: enrolledBy || 'system',
    } as unknown as Record<string, unknown>)
  );
};

/**
 * Record an enrolment EXIT episode — flips the ACTIVE enrolment for a
 * (student, class) pair to 'dropped' or 'transferred' and stamps droppedAt.
 * This is the exit half of the OnSIS episode (enter = enrolledAt); the record is
 * never deleted, so the full enter/exit history survives. No-op when there is no
 * active enrolment (e.g. a personal, non-school class). Idempotent.
 */
export const exitEnrollment = async (
  schoolId: string,
  schoolStudentId: string,
  classId: string,
  mode: 'dropped' | 'transferred' = 'dropped',
  by?: string,
  reason?: string,
): Promise<void> => {
  const q = query(
    collection(db, `schools/${schoolId}/enrollments`),
    where('schoolStudentId', '==', schoolStudentId),
    where('classId', '==', classId),
    where('status', '==', 'active'),
  );
  const snap = await getDocs(q);
  const now = new Date().toISOString();
  await Promise.all(snap.docs.map(d => updateDoc(d.ref, removeUndefined({
    status: mode,
    droppedAt: now,
    droppedBy: by,
    droppedReason: reason,
  }))));
};

/** Get all enrollments for a school student */
export const getStudentEnrollments = async (
  schoolId: string,
  schoolStudentId: string,
): Promise<Enrollment[]> => {
  const q = query(
    collection(db, `schools/${schoolId}/enrollments`),
    where('schoolStudentId', '==', schoolStudentId),
    where('status', '==', 'active'),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() })) as Enrollment[];
};

/**
 * Get class IDs a student is enrolled in, by email.
 * Optimized: accepts optional schoolIds to avoid scanning all schools.
 */
export const getClassIdsForStudentEmail = async (
  email: string,
  knownSchoolIds?: string[],
): Promise<string[]> => {
  if (!email) return [];
  const normalizedEmail = email.toLowerCase().trim();
  const classIds: string[] = [];

  // If we know which schools to check, use those directly
  let schoolIds = knownSchoolIds || [];

  if (schoolIds.length === 0) {
    // Fallback: scan schools (but limit to first 10 to avoid performance issues)
    const schoolsSnap = await getDocs(query(collection(db, 'schools')));
    schoolIds = schoolsSnap.docs.map(d => d.id).slice(0, 10);
  }

  // Search each school's student directory
  for (const schoolId of schoolIds) {
    try {
      const studentQuery = query(
        collection(db, `schools/${schoolId}/students`),
        where('email', '==', normalizedEmail),
      );
      const studentSnap = await getDocs(studentQuery);
      if (studentSnap.empty) continue;

      for (const studentDoc of studentSnap.docs) {
        const enrollments = await getStudentEnrollments(schoolId, studentDoc.id);
        classIds.push(...enrollments.map(e => e.classId));
      }
    } catch {
      // Skip inaccessible schools
    }
  }

  return [...new Set(classIds)];
};

/**
 * Sync a school directory student into a class roster.
 * Admin→Class direction: adds student to ClassData.students[] with linking fields.
 */
export const syncSchoolStudentToClass = async (
  schoolId: string,
  schoolStudentId: string,
  classId: string,
  enrolledBy?: string,
): Promise<void> => {
  // Load the school student
  const studentDoc = await getDoc(doc(db, `schools/${schoolId}/students/${schoolStudentId}`));
  if (!studentDoc.exists()) return;
  const schoolStudent = studentDoc.data() as SchoolStudent;

  // Load the class
  const classDoc = await getDoc(doc(db, 'classes', classId));
  if (!classDoc.exists()) return;
  const classData = classDoc.data() as ClassData;
  // A template is a content shell with no roster. This path is reached from the
  // school-admin Classes tab, which lists every class with a schoolId — templates
  // included — so without this an admin assigning students to what looks like a
  // course would populate a template, and claim-class-membership would then convert
  // that roster residue into classMembers (full class-doc read) on the students'
  // next sign-in.
  if (classData.isTemplate === true) return;

  // Check if already in class (by schoolStudentId or email)
  const alreadyInClass = classData.students?.some(
    s => s.schoolStudentId === schoolStudentId ||
         (s.email && schoolStudent.email && s.email.toLowerCase() === schoolStudent.email.toLowerCase())
  );
  if (alreadyInClass) {
    // Still ensure enrollment record exists
    await ensureEnrollment(schoolId, schoolStudentId, classId, 'manual', enrolledBy);
    return;
  }

  /**
   * NEVER write a school-directory name onto a NICKNAME OR PRIVACY FIRST class.
   *
   * This copies `schoolStudent.firstName` / `lastName` / `email` straight onto the class document and the
   * guardian details to the private roster — real, plaintext, from the school directory. For a Privacy
   * First class that is precisely the data the mode exists to keep out, and it arrives without the
   * teacher doing anything: a school admin adding the student in the directory, or a timetable publish,
   * is enough. The hazard is already named in `handleDuplicateClass` — "a class badged Privacy First
   * whose roster school admins fill with real names through timetable publish" — and this is the function
   * that would do it.
   *
   * The ENROLMENT is still recorded, so the student can see the class through the enrolment bridge and
   * their timetable is correct. Only the identity is withheld, which is the one thing these classes keep
   * elsewhere. The teacher adds them to the roster themselves, through the vault.
   *
   * Skipped rather than thrown: this runs inside bulk admin flows — a directory import, a timetable
   * publish across a whole school — and throwing would abort the rest of the batch over a class that is
   * working as intended.
   */
  if (classData.useNicknames === true || classData.privacyFirst === true) {
    console.warn(
      `[syncSchoolStudentToClass] class ${classId} keeps its own roster (nicknames/Privacy First); `
      + 'recording the enrolment but not writing the student\'s details.',
    );
    await ensureEnrollment(schoolId, schoolStudentId, classId, 'manual', enrolledBy);
    return;
  }

  // Add to class roster. Parent contact goes to the private roster doc, NOT the
  // classmate-readable embedded array.
  const studentId = `student_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  const newStudent: Student = {
    id: studentId,
    firstName: schoolStudent.firstName,
    lastName: schoolStudent.lastName,
    studentNumber: schoolStudent.studentNumber || '',
    email: schoolStudent.email || '',
    schoolStudentId,
    linkedSchoolId: schoolId,
  };

  /**
   * Backstop. The check above is the real gate; this catches the case it cannot see — a name this
   * browser has already decrypted from the vault arriving back through the school directory.
   */
  assertNoVaultLeak(newStudent, auth.currentUser?.uid ?? '', classId, 'this school-directory student');

  await updateDoc(doc(db, 'classes', classId), {
    students: arrayUnion(newStudent),
  });
  await writeStudentPrivate(classId, studentId, {
    parentName: schoolStudent.parentName || '',
    parentEmail: schoolStudent.parentEmail || '',
    parentPhone: schoolStudent.parentPhone || '',
  });

  // Create enrollment record
  await ensureEnrollment(schoolId, schoolStudentId, classId, 'manual', enrolledBy);
};

// Get all mark sets for a teacher
export const getTeacherMarkSets = async (teacherId: string): Promise<MarkSet[]> => {
  const q = query(collection(db, 'markSets'), where('ownerId', '==', teacherId));
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as MarkSet[];
};

// Create a new class
export const createClass = async (
  teacherId: string,
  classData: Omit<ClassData, 'id' | 'ownerId' | 'joinCode'>
): Promise<string> => {
  const joinCode = await generateJoinCode();

  // Never embed owner-only student PII on the (classmate-readable) class doc — a
  // duplicated class could carry hydrated/merged-back students. Split each into a
  // public part (embedded) + PII (routed to the private roster so it's not lost).
  // A restore-from-history snapshot also carries the HYDRATED private roster
  // (`studentPrivate`) — the only place parent contact / OEN / accommodations /
  // notes live (hydrate merges only a few fields back onto students[]) — so fold it
  // into the per-student PII too, or those fields would be silently lost on restore.
  const hydratedPrivate = ((classData as ClassData).studentPrivate || {}) as Record<string, Record<string, unknown>>;
  const piiByStudent: Record<string, Record<string, unknown>> = {};
  const publicStudents = (classData.students || []).map(s => {
    const sid = (s as { id?: string }).id;
    const { publicPart, pii } = splitStudentPii({ ...(s as Record<string, any>) });
    const merged = { ...(sid ? hydratedPrivate[sid] || {} : {}), ...(pii as Record<string, unknown> || {}) };
    if (sid && Object.keys(merged).length) piiByStudent[sid] = merged;
    return publicPart;
  }) as ClassData['students'];

  // Kinder results/notes are sensitive — strip them off the (classmate-readable)
  // class doc and route them to the private kinder_* subcollection (e.g. the
  // practice class ships with pre-filled demo results).
  const { stripped: strippedKinder, byId: kinderById } = stripKinderResults(classData.kinderAssessments || []);

  // Test answer keys must never land on the classmate-readable class doc — a
  // restore-from-history (or any) snapshot carries hydrated, full-answer tests.
  // Extract each test's key + sanitize its class-doc copy; the key is routed to the
  // new class's private quizKeys doc after the doc is created (same pattern as PII).
  const quizKeyById: Record<string, unknown> = {};
  const publicPopQuizzes = (classData.popQuizzes || []).map((q) => {
    if (q && q.kind === 'test' && Array.isArray(q.questions) && q.questions.some((qn) => hasAnswerContent(qn))) {
      const { sanitized, key } = prepareQuizForStorage(q);
      quizKeyById[q.id] = key;
      const { aiGradingInstructions: _drop, ...rest } = q as PopQuiz & { aiGradingInstructions?: string };
      return { ...rest, questions: sanitized };
    }
    return q;
  }) as ClassData['popQuizzes'];

  // Strip fields that must NEVER be persisted on the (classmate-readable) class doc
  // when a caller passes a full/hydrated ClassData (restore-from-history, duplicate):
  //  - `id`: the Firestore doc id is generated by addDoc below; if a stale id leaked
  //    into the doc DATA, getTeacherClasses (`{ id: doc.id, ...doc.data() }`) would
  //    let it OVERRIDE the real id, so a restored class surfaces under its old
  //    (blocklisted) id and vanishes from the dashboard.
  //  - `studentPrivate`: the owner-only hydrated PII roster (OEN, parent contact,
  //    accommodations, notes, authUids) — a hydrated restore snapshot carries it.
  //    (The embedded students[] array is separately PII-split below.)
  // Mirrors updateClass, which strips the same fields for the same reason.
  const { id: _discardStaleId, studentPrivate: _discardPrivate,
    observationDeletionState: _discardDeletionLock, ...classDataNoId } = classData as ClassData & { observationDeletionState?: string };

  // Collect per-assignment AI grading instructions BEFORE stripping them off the
  // class doc, so they can be routed to the private doc after the class is created
  // (like PII + quiz keys). Without this, stripping would silently DROP a restored/
  // duplicated class's grading instructions.
  const aiInstrByAssessment: Record<string, string> = {};
  for (const a of (classData.assessments || [])) {
    const instr = (a as { aiGradingInstructions?: string }).aiGradingInstructions;
    if (instr && instr.trim()) aiInstrByAssessment[a.id] = instr.trim();
  }

  /**
   * The private half of any carried submissions comes OFF before the doc is
   * created, and goes into the subcollection afterwards.
   *
   * This matters for restore-from-history: the snapshot was taken while threads
   * and confidential notes were still embedded, so re-creating the class from it
   * would put them straight back on the classmate-readable doc — and unlike the
   * live edit paths, nothing here goes through updateClass.
   *
   * readerUids come from the SNAPSHOT'S OWN roster, not from `[]`.
   *
   * They used to be hard-coded empty on the reasoning that linkReaderUid would heal
   * them at next sign-in. It would not: linkReaderUid only fans out when the uid is
   * new or a grades probe is behind, and a restored class has no grade subdocs — so
   * the uid looks known, nothing is behind, and nothing ever reconciles this
   * collection. A student would have been locked out of their own conversations
   * permanently. The restore snapshot carries `authUids` (it is a PII field merged
   * back onto the students), so the correct readers are already here.
   */
  const carriedSubmissionPrivate: Array<{ id: string; studentId: unknown; privatePart: Record<string, unknown> }> = [];
  const publicSubmissions = ((classData.submissions || []) as unknown as Record<string, any>[]).map(row => {
    const { publicPart, privatePart } = splitSubmissionPrivate({ ...row });
    if (privatePart && row.id) carriedSubmissionPrivate.push({ id: String(row.id), studentId: row.studentId, privatePart });
    return publicPart;
  });

  const newClass = removeUndefined({
    ...classDataNoId,
    ownerId: teacherId,
    joinCode,
    students: publicStudents,
    ...(classData.submissions ? { submissions: publicSubmissions as unknown as ClassData['submissions'] } : {}),
    // Per-assignment AI grading instructions are owner-only (can contain solutions /
    // rubric answers) — strip them off the classmate-readable class doc. A hydrated
    // restore/duplicate snapshot carries them merged onto assessments. Same as updateClass.
    assessments: stripAiInstructions(classData.assessments || []) as ClassData['assessments'],
    ...(classData.popQuizzes ? { popQuizzes: publicPopQuizzes } : {}),
    ...(classData.kinderAssessments ? { kinderAssessments: strippedKinder } : {}),
    grades: classData.grades || {},
    releasedGrades: {},  // Initialize empty - grades won't show to students until released
    attendance: classData.attendance || [],
    classMembers: [teacherId],  // Owner is always a member; nickname/email students added on join
    createdAt: new Date().toISOString()
  });

  const docRef = await addDoc(collection(db, 'classes'), newClass);
  /**
   * Mirror any grades this class was created WITH.
   *
   * Restore-from-history rebuilds the whole class doc through here, including a
   * populated grades map, and nothing mirrored it — the comment further up already
   * observed that "a restored class has no grade subdocs". Demo classes do the
   * same. Readers are empty for the same reason as the submission halves: the
   * private roster is not written yet at this point, so they fail closed and heal
   * on the student's next sign-in.
   */
  const carriedGrades = Object.entries((classData.grades || {}) as Record<string, Grade>);
  if (carriedGrades.length) {
    try {
      await Promise.all(carriedGrades.map(([key, g]) => dualWriteGradeDoc(
        docRef.id, key, g as unknown as Parameters<typeof dualWriteGradeDoc>[2], [],
      )));
    } catch (err) {
      console.error(`[createClass] FAILED to mirror restored grades for class ${docRef.id}:`, err);
    }
  }

  if (carriedSubmissionPrivate.length) {
    try {
      await Promise.all(carriedSubmissionPrivate.map(({ id, studentId, privatePart }) => setDoc(
        submissionPrivateRef(docRef.id, id),
        {
          ...privatePart,
          studentId: studentId ?? null,
          readerUids: deriveReaders(piiByStudent[String(studentId)]?.authUids as string[] | undefined),
          updatedAt: new Date().toISOString(),
        },
        { merge: true },
      )));
    } catch (err) {
      /**
       * THROW, because the caller deletes the history record once this resolves.
       *
       * Swallowing it meant a restore reported success, removed the only copy of
       * the snapshot, and left the conversations nowhere — the class doc no longer
       * carries them and the private docs were never written. Failing the restore
       * keeps the record, which IS the way back.
       */
      console.error(`[createClass] FAILED to persist the private half of restored submissions for class ${docRef.id}:`, err);
      throw err;
    }
  }
  if (Object.keys(aiInstrByAssessment).length) {
    // Route the stripped grading instructions to the owner-only private doc (same
    // shape setAssignmentAiInstructions uses). Best-effort — they're owner-only.
    try {
      await setDoc(aiInstructionsRef(docRef.id), aiInstrByAssessment, { merge: true });
    } catch (err) {
      console.error(`[createClass] FAILED to persist AI grading instructions for class ${docRef.id}:`, err);
    }
  }
  if (Object.keys(kinderById).length) {
    try {
      await Promise.all(Object.entries(kinderById).map(([assessmentId, tree]) =>
        writeKinderResultsDoc(docRef.id, assessmentId, tree)));
    } catch (err) {
      console.error(`[createClass] FAILED to persist kinder results to the private subcollection for class ${docRef.id} — demo/imported results may be missing:`, err);
    }
  }
  if (Object.keys(piiByStudent).length) {
    // Don't swallow: a failed roster write here means the PII was stripped from
    // the embedded students but not saved anywhere — surface it loudly.
    try {
      await setDoc(studentPrivateRef(docRef.id), piiByStudent, { merge: true });
    } catch (err) {
      console.error(`[createClass] FAILED to persist student PII to the private roster for class ${docRef.id} — ${Object.keys(piiByStudent).length} student record(s) may have lost parent/contact data:`, err);
    }
  }
  if (Object.keys(quizKeyById).length) {
    // The test answers were stripped from the embedded quizzes — the private key
    // MUST land or those tests become ungradeable. Surface loudly on failure.
    try {
      await setDoc(quizKeysRef(docRef.id), { ...quizKeyById, updatedAt: new Date().toISOString() }, { merge: true });
    } catch (err) {
      console.error(`[createClass] FAILED to persist test answer keys to the private doc for class ${docRef.id} — ${Object.keys(quizKeyById).length} test(s) may be ungradeable:`, err);
    }
  }
  return docRef.id;
};

// Update a class
/**
 * Flag (or clear) one post's Google Classroom sync state.
 *
 * A DOTTED field write on purpose. The obvious alternative — flipping a boolean
 * on the post inside `stream` — means writing the whole array back from a
 * client snapshot, and students write replies into that same array: anything
 * that landed while we were waiting on Google would be deleted. This touches
 * one key and nothing else.
 */
export const setPostClassroomSyncState = async (
  classId: string,
  postId: string,
  state: ClassroomPostSyncState | null,
): Promise<void> => {
  const classRef = doc(db, 'classes', classId);
  // FieldPath, not a dotted string: a post id containing a '.' would silently
  // nest under the wrong key, and one containing ~*/[] throws in the SDK's
  // dot-path parser. Ids are clean today; this costs nothing and removes the
  // question.
  await updateDoc(classRef, new FieldPath('gcPostSync', postId), state === null ? deleteField() : state);
};

export const updateClass = async (classId: string, updates: Partial<ClassData>): Promise<void> => {
  /**
   * VAULT LEAK GUARD. No-ops unless a vault is unlocked for this class in this session, which is the only
   * moment a decrypted name can be in flight. Compares VALUES, not keys: a resolved real name lands in
   * `firstName`, the same field a pseudonym occupies, so a key allowlist sees nothing wrong.
   */
  assertNoVaultLeak(updates, auth.currentUser?.uid ?? '', classId, 'this class update');

  const classRef = doc(db, 'classes', classId);
  // `studentPrivate` is a client-only hydrated field (owner-only PII). It must
  // NEVER be persisted onto the class doc, which classmates can read — strip it
  // here so a caller spreading a full ClassData can't re-leak it.
  const { studentPrivate: _omit, ...safe } = updates as Partial<ClassData>;
  // Per-assignment AI grading instructions are owner-only — never persist them on
  // the class doc (classmate-readable). They live in the private doc instead.
  if (Array.isArray(safe.assessments)) safe.assessments = stripAiInstructions(safe.assessments) as ClassData['assessments'];
  // Owner-only student PII (incl. Stage 0.5 fields) must never be re-embedded on
  // the class doc — a caller may spread hydrated (merged-back) students. Strip
  // here; the values are already isolated in the private roster.
  if (Array.isArray(safe.students)) {
    /**
     * NEVER persist a masked roster.
     *
     * Name masking replaces the names in the data handed to the views, so that
     * every screen is covered without 224 render sites each having to remember.
     * The cost of doing it there is that any writer building a `students` array
     * from what is on screen would save the pseudonyms over the real roster —
     * every name in the class, gone, silently, with no way back.
     *
     * So this refuses. A loud failure while a teacher is projecting marks is
     * recoverable; a quiet one is not, and no legitimate roster edit happens
     * while the names are hidden anyway.
     */
    if (rosterIsMasked(safe.students)) {
      throw new Error(
        'Student names are masked right now, so the roster was not saved. Turn masking off and try again.',
      );
    }
    safe.students = safe.students.map(s => splitStudentPii({ ...(s as Record<string, any>) }).publicPart) as ClassData['students'];
  }
  // Kindergarten results/notes are sensitive (per-child assessment records) and
  // live in the private kinder_* subcollection — never re-embed them on the
  // classmate-readable class doc, even if a caller spreads hydrated assessments.
  if (Array.isArray(safe.kinderAssessments)) {
    safe.kinderAssessments = stripKinderResults(safe.kinderAssessments).stripped;
  }
  // Answer-key privacy: keep TEST answer keys OFF the student-readable class doc.
  // Centralized here so EVERY write path (create, edit, variant editor, release,
  // proctor settings, hold) is covered. For each test being written:
  //   1. write its answer key to the owner-only private doc, AWAITED and NOT
  //      swallowed — it MUST succeed before we strip, or the answers are lost;
  //   2. strip the answer fields off the class-doc copy (sanitizeQuestions).
  // Skips a quiz whose in-memory copy already looks sanitized (correctIndex -1) so a
  // raw-doc/subscription-clobbered copy can't overwrite a good key or double-strip.
  // matchPairs is intentionally NOT stripped yet (matching handled separately).
  if (Array.isArray(safe.popQuizzes)) {
    const quizzes = safe.popQuizzes as PopQuiz[];
    // "Real, unstripped test" = still carries answer content anywhere (not just
    // correctIndex !== -1, which an AI/Word-imported all-written/matching test can
    // legitimately have) — so an already-sanitized copy is skipped and can't
    // overwrite a good key, while every answer-bearing test is keyed + stripped.
    const tests = quizzes.filter(
      (q) => q && q.kind === 'test' && Array.isArray(q.questions) && q.questions.length > 0
        && q.questions.some((qn) => hasAnswerContent(qn)),
    );
    if (tests.length > 0) {
      const keyPayload: Record<string, unknown> = { updatedAt: new Date().toISOString() };
      const sanitizedById = new Map<string, PopQuiz['questions']>();
      for (const q of tests) {
        // ONE pass produces both the private key and the sanitized (answer-free,
        // matching-scrambled) class-doc questions from a shared permutation.
        const { sanitized, key } = prepareQuizForStorage(q);
        keyPayload[q.id] = key;
        sanitizedById.set(q.id, sanitized);
      }
      // Blocking + ordered: throws (aborting the class-doc write) if the private key
      // write fails, so we never publish a stripped test whose key wasn't saved.
      // mergeFields (not merge:true) REPLACES each written quiz's key wholesale so a
      // field the teacher REMOVED on edit (a tolerance, an accepted form, a deleted
      // variant) can't survive from the prior key and get re-credited — while other
      // quizzes' keys in the doc are preserved.
      await setDoc(quizKeysRef(classId), keyPayload, { mergeFields: [...tests.map((q) => q.id), 'updatedAt'] });
      safe.popQuizzes = quizzes.map((q) => {
        if (!(q && q.kind === 'test' && sanitizedById.has(q.id))) return q;
        // Strip the answer fields AND the teacher-private aiGradingInstructions off
        // the class-doc copy (both are hydrated back for the teacher from the key).
        const { aiGradingInstructions: _drop, ...rest } = q as PopQuiz & { aiGradingInstructions?: string };
        return { ...rest, questions: sanitizedById.get(q.id)! };
      }) as ClassData['popQuizzes'];
    }
  }
  /**
   * Submissions: move the private half out before the class doc is written.
   *
   * Placed last so it runs after every other transform, and awaited WITHOUT a
   * catch — if the private write fails we must not publish a stripped array.
   * Covers every teacher-side writer, because they all funnel through here:
   * grading, feedback-only, missing-work placeholders, thread messages, roster
   * comments, group merges, hide/unhide, rename re-writes and residue restore.
   */
  if (Array.isArray(safe.submissions)) {
    safe.submissions = await persistSubmissionPrivate(
      classId, safe.submissions as unknown as Record<string, any>[],
    ) as unknown as ClassData['submissions'];
  }

  /**
   * The embedded write goes FIRST. The mirror follows, best-effort.
   *
   * This block used to run before it, which broke the contract the mirror itself
   * states — "failures here are NEVER allowed to block the embedded write" — in
   * two ways. A transient failure on the class read or the roster read failed the
   * teacher's grade save outright, where before it succeeded. And if the class-doc
   * write then failed, the subdocs for removed keys had already been deleted while
   * the embedded map still listed them: the class doc says the mark exists, the
   * subcollection says it does not, and after the eventual strip the subcollection
   * is the only copy.
   *
   * So: write the marks, then mirror, and swallow mirror failures with a warning.
   * `backfill-grades` reconciles drift, which is exactly what it is for.
   */
  const gradesForMirror = (safe.grades && typeof safe.grades === 'object')
    ? safe.grades as Record<string, Grade>
    : null;
  /**
   * Read the prior map for the diff, but NEVER let that read block the write.
   *
   * This read has to happen before `updateDoc` to be a "before" at all, which put
   * it back on the critical path — the exact contract the reorder was meant to
   * honour ("failures here are NEVER allowed to block the embedded write") was
   * true of the roster read and not of this one. An unavailable prior map just
   * means every row looks changed, so the mirror over-writes rather than under-
   * writes: the safe direction.
   */
  let beforeForMirror: Record<string, Grade> | undefined;
  if (gradesForMirror) {
    try {
      beforeForMirror = (await getDoc(classRef)).data()?.grades as Record<string, Grade> | undefined;
    } catch (err) {
      console.warn(`[updateClass] could not read the prior grades map for class ${classId}; mirroring every row:`, err);
    }
  }

  await updateDoc(classRef, removeUndefined(safe));

  /**
   * Mirror WHOLE-MAP grade writes to the grades subcollection.
   *
   * One guard here rather than five patches at the call sites. Five writers rewrite
   * the whole `grades` map through this function and none mirrored — the quiz
   * auto-apply, the teacher rescue write, both short-answer savers, and the re-grade
   * after an answer-key edit. That drift is currently healed by `backfill-grades`,
   * whose authority is the embedded map, so it stops working the moment the map is
   * stripped and those writes become marks that exist nowhere.
   *
   * Only whole-map writes reach here: `batchUpdateGrades` uses dotted field paths,
   * so its keys never appear as a `grades` object, and it already mirrors atomically
   * inside its own batch.
   *
   * REMOVED KEYS ARE NOT DELETED HERE. Every whole-map writer builds the map from
   * React state, so a co-teacher's concurrent per-key write is absent from it — and
   * deleting on that basis would take out a subdoc that is the only surviving copy
   * of a mark the other teacher had just entered. A stale extra subdoc is
   * reconcilable; a deleted mark is not. Deletions have their own paths
   * (`deleteStudent`, `deleteAssessment`, bundle delete) which purge deliberately
   * from a known key list.
   */
  if (gradesForMirror) {
    try {
      const before = beforeForMirror || {};
      const changed = Object.entries(gradesForMirror).filter(
        ([k, g]) => JSON.stringify(before[k]) !== JSON.stringify(g),
      );
      if (changed.length) {
        const authUidsByStudent = await readAuthUidsByStudent(classId);
        await Promise.allSettled(changed.map(([key, g]) => dualWriteGradeDoc(
          classId, key, g as unknown as Parameters<typeof dualWriteGradeDoc>[2],
          deriveReaders(authUidsByStudent[(g as Grade).studentId]),
        )));
      }
    } catch (err) {
      console.warn(`[updateClass] grade mirror failed for class ${classId}; backfill-grades will reconcile:`, err);
    }
  }
};

// Read a single class doc fresh from the server (bypasses the cache). Used when
// a targeted array flip (e.g. holding a quiz) must map the FRESHEST array so a
// co-teacher's concurrent edit isn't reverted by a stale read-map-write.
export const getClassById = async (classId: string): Promise<ClassData | null> => {
  const snap = await getDocFromServer(doc(db, 'classes', classId));
  if (!snap.exists()) return null;
  return { ...(snap.data() as Omit<ClassData, 'id'>), id: snap.id };
};

// Archive a class with auto-delete date based on plan
export const archiveClass = async (classId: string, retentionDays: number = 30): Promise<void> => {
  const classRef = doc(db, 'classes', classId);
  const now = new Date();
  const autoDeleteDate = new Date(now.getTime() + retentionDays * 24 * 60 * 60 * 1000);

  await updateDoc(classRef, {
    isArchived: true,
    archivedAt: now.toISOString(),
    autoDeleteDate: autoDeleteDate.toISOString()
  });
};

// Unarchive a class
export const unarchiveClass = async (classId: string): Promise<void> => {
  const classRef = doc(db, 'classes', classId);

  // Serialize unarchive with the server deletion lock; a stale archive tile must
  // not promise recovery after an irreversible cascade has started.
  await runTransaction(db, async transaction => {
    const docSnap = await transaction.get(classRef);
    if (!docSnap.exists()) {
      throw new Error(`Cannot restore class: Document ${classId} does not exist in Firestore. The class may have been deleted. Please use "Restore from History" instead.`);
    }
    if (docSnap.data().observationDeletionState === 'deleting') {
      throw new Error('This class is being permanently deleted and cannot be unarchived.');
    }
    transaction.update(classRef, {
      isArchived: false,
      archivedAt: deleteField(),
      autoDeleteDate: deleteField()
    });
  });

  console.log('Class unarchived successfully:', classId);
};

// Call an authenticated Netlify function with the caller's Firebase ID token.
// Returns the raw Response so callers can branch on status.
export const callAuthedFunction = async (name: string, payload: Record<string, unknown>): Promise<Response> => {
  const { getAuth } = await import('firebase/auth');
  const currentUser = getAuth().currentUser;
  if (!currentUser) throw new Error('You must be signed in.');
  const idToken = await currentUser.getIdToken();
  return fetch(`/.netlify/functions/${name}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
    body: JSON.stringify(payload),
  });
};

// Delete a class — routed through the server-side delete-class-data function so
// recursiveDelete cascades every subcollection (grades, releasedGrades,
// gradeHistory, studentAuth, private). A plain client deleteDoc removes only the
// class document and orphans all of those subdocs permanently.
export const deleteClass = async (classId: string): Promise<void> => {
  const res = await callAuthedFunction('delete-class-data', { classId });
  // A missing parent is not proof that its subcollections were erased. Only a
  // confirmed server cascade/finalization is success; orphan retries stay admin-only.
  if (!res.ok) {
    const msg = await res.text().catch(() => '');
    throw new Error(`Failed to delete class ${classId}: ${res.status} ${msg}`);
  }
  console.log('Class + subcollections permanently deleted (server cascade):', classId);

  /**
   * Drop the class's VAULT KEY WRAPS, now that the class and its encrypted roster are gone.
   *
   * An orphan wrap is not clutter, it is a permanent lockout: `resetPassphraseWithRecoveryCodes` is
   * all-or-nothing by necessity and iterates every entry in `classes`, so it demands a recovery code
   * for a class that no longer exists. A teacher who deleted a class and threw its code away could
   * never reset a forgotten passphrase again, blocked by a key for nothing.
   *
   * Best-effort and after the cascade: the delete has already succeeded, and a teacher must not be
   * told it failed because a tidy-up write did not land.
   */
  try {
    const uid = auth.currentUser?.uid;
    if (uid) {
      const { removeClassKeyWraps } = await import('./vault/vaultStore');
      await removeClassKeyWraps(uid, classId);
    }
  } catch (err) {
    console.error(`[deleteClass] could not remove vault key wraps for ${classId}:`, err);
  }
};

// Create a mark set
export const createMarkSet = async (
  teacherId: string, 
  markSetData: Omit<MarkSet, 'id' | 'ownerId'>
): Promise<string> => {
  const newMarkSet = removeUndefined({
    ...markSetData,
    ownerId: teacherId,
    createdAt: new Date().toISOString()
  });
  
  const docRef = await addDoc(collection(db, 'markSets'), newMarkSet);
  return docRef.id;
};

// Update a mark set
export const updateMarkSet = async (
  markSetId: string,
  updates: Partial<MarkSet>
): Promise<void> => {
  const markSetRef = doc(db, 'markSets', markSetId);
  await updateDoc(markSetRef, removeUndefined(updates));
  // Refresh the snapshot carried on every class that uses this mark set. A
  // co-teacher can't read markSets/{id} (owner-only in firestore.rules), so the
  // class-doc copy is the only version they ever see — and a stale copy means the
  // two teachers weight categories differently and disagree about a student's final
  // mark. Best-effort: the mark set itself is already saved, and a missed refresh
  // self-heals the next time this runs or a co-teacher is added.
  try {
    await syncSharedMarkSetToClasses(markSetId);
  } catch (err) {
    console.error('[updateMarkSet] shared snapshot refresh failed (non-fatal):', markSetId, err);
  }
};

/**
 * Write the current mark set onto every class of the CALLER's that references it,
 * as `sharedMarkSet`. This is what a co-teacher resolves against — see
 * ClassData.sharedMarkSet for why the id alone isn't enough.
 */
export const syncSharedMarkSetToClasses = async (markSetId: string): Promise<void> => {
  const uid = auth.currentUser?.uid;
  if (!uid || !markSetId) return;
  const msSnap = await getDoc(doc(db, 'markSets', markSetId));
  if (!msSnap.exists()) return;
  const ms = msSnap.data() as MarkSet;
  if (!Array.isArray(ms.categories) || ms.categories.length === 0) return;
  const snapshot = {
    id: markSetId,
    ...(ms.name ? { name: ms.name } : {}),
    categories: ms.categories,
    updatedAt: new Date().toISOString(),
  };
  // Only the owner's own classes — a co-teacher must never rewrite this.
  const q = query(
    collection(db, 'classes'),
    where('ownerId', '==', uid),
    where('markSetId', '==', markSetId),
  );
  const classesSnap = await getDocs(q);
  await Promise.all(classesSnap.docs.map(d => {
    // Skip classes whose snapshot already matches. `updatedAt` changes on every
    // call, so an unconditional write always mutates the class doc — which
    // re-fires the live subscription, which re-runs the caller's staleness check.
    // If that check ever disagrees persistently (stale local state, key ordering),
    // an unconditional write is an infinite loop. Comparing content here breaks it
    // at the source, independent of whatever the caller believed.
    const existing = d.data()?.sharedMarkSet as ClassData['sharedMarkSet'] | undefined;
    if (existing
      && existing.id === markSetId
      && (existing.name || '') === (ms.name || '')
      && sameMarkSetCategories(existing.categories, ms.categories)) {
      return Promise.resolve();
    }
    return updateDoc(d.ref, { sharedMarkSet: snapshot }).catch(err =>
      console.error('[syncSharedMarkSet] class update failed:', d.id, err));
  }));
};

// Delete a mark set
export const deleteMarkSet = async (markSetId: string): Promise<void> => {
  await deleteDoc(doc(db, 'markSets', markSetId));
};

// Add a student to a class (uses transaction for atomic duplicate checking)
// ─── Student PII isolation ──────────────────────────────────────────────────
// Sensitive fields live in the owner/admin-only `classes/{id}/private/roster`
// doc (keyed by studentId), NOT in the classmate-readable embedded students[].
// See StudentPrivate in types.ts. Reads go through studentPrivateOf().

// NOTE: `email` is deliberately NOT isolated — it's the student's own contact
// (lower severity) and is load-bearing for dedup/matching in addStudent,
// join-class-email, claim-class-membership, and OneRoster. Revisit separately.
// STUDENT_PII_FIELDS / STUDENT_DROP_FIELDS live in the firebase-free utils.ts so
// the server sweep imports the SAME lists (F3 — no drift). Re-exported for callers.
export { STUDENT_PII_FIELDS, STUDENT_DROP_FIELDS };

// Owner-only fields that teacher UI reads directly as `student.X` (not via the
// studentPrivateOf accessor). We merge these back onto the in-memory student
// objects for the owner after hydration so those reads keep working, while the
// persisted doc stays stripped. Only fields with NO updateClass set-path belong
// here (else the release/bulk paths would re-embed or lose them).
// authUids is merged back so the OWNER's in-memory students carry the reader
// allow-list — the grade-write callers thread it into batchUpdateGrades/releaseGrades
// (no per-save roster read, Stage 0 #5). updateClass strips it again before any
// class-doc write, so it never re-embeds and never clobbers the server-owned roster.
// MERGE_BACK_TO_STUDENT + mergeBackStudentPrivate live in the firebase-free
// studentPiiFields.ts, beside the other owner-only field lists, so a test can
// exercise the merge without booting the Firebase SDK.
export { mergeBackStudentPrivate };


const studentPrivateRef = (classId: string) =>
  doc(db, 'classes', classId, 'private', 'roster');

const submissionPrivateRef = (classId: string, submissionId: string) =>
  doc(db, 'classes', classId, 'submissionPrivate', submissionId);

/**
 * The roster's per-student auth allow-lists, for stamping `readerUids`.
 *
 * ONE reader for this, used by every writer that stamps a reader set, because the
 * alternative is each caller deciding for itself and some of them defaulting to
 * `[]` — which, since every stamp is a SET rather than a merge, silently revokes a
 * student's access to their own data. That is exactly the bug this exists to stop
 * recurring.
 *
 * Throws rather than returning empty: a caller that cannot read the roster must not
 * proceed to write an empty allow-list over a good one.
 */
export const readAuthUidsByStudent = async (
  classId: string,
): Promise<Record<string, string[]>> => {
  const snap = await getDoc(studentPrivateRef(classId));
  const roster = (snap.exists() ? snap.data() : {}) as Record<string, { authUids?: string[] }>;
  return Object.fromEntries(
    Object.entries(roster).map(([sid, rec]) => [sid, rec?.authUids || []]),
  );
};

/**
 * Move the never-class-visible half of each submission off the class doc.
 *
 * `submissions[]` is embedded on the class doc and the class doc is readable by
 * every class member, so `thread`, `studentComment` and `selfAssessment` were
 * readable by all thirty students — and the student portal's live listener pushed
 * every write of them into every open student tab. Rules cannot filter fields, so
 * the only fix is for the field not to be there.
 *
 * ORDER MATTERS, and it is the same discipline as the answer keys above: the
 * private docs are written FIRST, awaited, and a failure THROWS so the class-doc
 * write is abandoned. Stripping before the private copy is safe would trade a
 * privacy bug for a data-loss bug, and a lost teacher-student conversation is not
 * recoverable.
 *
 * readerUids is SET from the roster's authUids, never unioned — `deriveReaders`
 * documents why: a union resurrects a uid that a password reset removed, which
 * breaks password-reset-as-device-revocation.
 */
const persistSubmissionPrivate = async (
  classId: string,
  submissions: readonly Record<string, any>[],
): Promise<Record<string, any>[]> => {
  const split = submissions.map(row => ({ row, ...splitSubmissionPrivate({ ...row }) }));
  const needing = split.filter(x => x.privatePart !== null);
  if (needing.length === 0) return split.map(x => x.publicPart);

  // One roster read, only when there is actually something to move.
  let authUidsByStudent: Record<string, string[]> = {};
  try {
    authUidsByStudent = await readAuthUidsByStudent(classId);
  } catch (err) {
    /**
     * THROW. Do not fall through, and do not "leave the rows as they are".
     *
     * This branch used to return the rows untouched so the caller could carry on,
     * reasoning that stripping without readerUids would leave the student unable
     * to read their own conversation. That is the wrong way round, and it was a
     * hole: the teacher's in-memory rows are HYDRATED — the thread is merged back
     * onto them for the UI — so returning them untouched put every conversation
     * back onto the classmate-readable class doc. A flaky roster read was enough
     * to re-broadcast the lot to thirty children.
     *
     * The two outcomes are not symmetric. `readerUids: []` fails closed and heals
     * on the student's next sign-in; re-embedding cannot be un-sent. So this
     * aborts the class-doc write, exactly as the answer-key path above does.
     */
    console.error(`[persistSubmissionPrivate] cannot read the private roster for class ${classId}; refusing to write the class doc rather than re-embed private submission data:`, err);
    throw err;
  }

  /**
   * A thread write UNIONS by message id; it never replaces the stored array.
   *
   * This is the guard for the sharpest failure mode in the whole split. Several
   * paths set the teacher's class state from a RAW class doc with no hydration
   * (the Classroom push and sync responses), so the in-memory row can carry no
   * thread at all. The teacher then replies, `[...(s.thread || []), msg]` yields
   * `[msg]`, and a wholesale write would replace the entire conversation with that
   * one message — silently, unrecoverably, and only for the classes that happened
   * to pass through those paths.
   *
   * Union by id makes a stale row incapable of destroying history: the worst it can
   * do is add. Deletion is therefore NOT possible through here, which is correct —
   * it is an explicit act and goes through deleteSubmissionThreadMessage.
   */
  /**
   * NOT caught. A swallowed read here reopens the very hole this union closes: a
   * failed read makes `prior` empty, the union degenerates to "just the incoming
   * array", and `merge` replaces an array wholesale — so a transient error would
   * truncate a conversation to whatever a possibly-stale row happened to carry.
   * Same reasoning as the roster read above: abort the save instead.
   */
  const stored = await Promise.all(needing.map(({ row }) =>
    getDoc(submissionPrivateRef(classId, String(row.id)))));

  const writes = needing.map(({ row, privatePart }, i) => {
    const prior = (stored[i].exists() ? stored[i].data() : {}) as Record<string, any>;
    const payload: Record<string, unknown> = { ...privatePart };

    if (Array.isArray(payload.thread)) {
      payload.thread = unionThread(prior.thread, payload.thread as any[]);
    }

    // Skip a write that would change nothing. Every teacher save carrying
    // `submissions` reaches every row, so without this a single grade save rewrote
    // one document per submission in the class and widened the clobber window.
    const unchanged = Object.keys(payload).every(
      k => JSON.stringify(payload[k]) === JSON.stringify(prior[k]),
    );
    if (unchanged) return null;

    return setDoc(
      submissionPrivateRef(classId, String(row.id)),
      {
        ...payload,
        studentId: row.studentId ?? null,
        readerUids: deriveReaders(authUidsByStudent[String(row.studentId)]),
        updatedAt: new Date().toISOString(),
      },
      // merge so a write carrying only a thread does not erase a stored
      // studentComment written by a different code path.
      { merge: true },
    );
  }).filter((w): w is Promise<void> => w !== null);

  await Promise.all(writes);

  return split.map(x => x.publicPart);
};

/**
 * Remove one message from a submission's private thread.
 *
 * The only write permitted to SHRINK a thread, and therefore its own function:
 * persistSubmissionPrivate unions by id so that a stale, un-hydrated row can never
 * destroy a conversation, which also means a teacher moderating an inappropriate
 * message cannot get their deletion to stick through that path — it reappeared on
 * the next hydrate. Deleting is an explicit act, so it gets an explicit call that
 * writes the pruned array directly.
 */
export const deleteSubmissionThreadMessage = async (
  classId: string,
  submissionId: string,
  messageId: string,
): Promise<void> => {
  const ref = submissionPrivateRef(classId, submissionId);
  const snap = await getDoc(ref);
  const prior = (snap.exists() ? snap.data() : {}) as Record<string, any>;
  const thread = pruneThread(prior.thread as any[], messageId);
  // setDoc with merge, not updateDoc: the doc may not exist yet for a class whose
  // rows are still embedded, and an update would throw rather than no-op.
  await setDoc(ref, { thread, updatedAt: new Date().toISOString() }, { merge: true });
};

/**
 * Attach the private half of each submission back onto a teacher's in-memory
 * class, and migrate any still-embedded rows on the way past.
 *
 * Mirrors hydrateStudentPrivate: teacher UI keeps reading `submission.thread` and
 * `submission.studentComment`, while the persisted document stays stripped. The
 * migration is lazy — it runs when a teacher opens the class — so it needs a
 * dormant-class sweep to finish the job, exactly as the embedded-PII sweep does.
 */
export const hydrateSubmissionPrivate = async (classData: ClassData): Promise<ClassData> => {
  const rows = (classData.submissions || []) as Record<string, any>[];

  // Migrate first, so the merge below hands back what we just stored rather than
  // silently dropping it.
  if (rows.some(r => hasSubmissionPrivate(r))) {
    try {
      // persistSubmissionPrivate throws rather than returning unstripped rows, so
      // reaching here means every private half is safely stored and the class doc
      // can be updated. The catch below keeps a failed migration from breaking the
      // teacher's class load — it just stays un-migrated until the next attempt.
      const stripped = await persistSubmissionPrivate(classData.id, rows);
      await updateDoc(doc(db, 'classes', classData.id), { submissions: stripped });
    } catch (err) {
      console.error(`[hydrateSubmissionPrivate] migration failed for class ${classData.id}:`, err);
    }
  }

  try {
    const snap = await getDocs(collection(db, 'classes', classData.id, 'submissionPrivate'));
    const byId = new Map<string, Record<string, unknown>>();
    snap.forEach(d => byId.set(d.id, d.data() as Record<string, unknown>));
    if (byId.size) {
      classData.submissions = (classData.submissions || []).map(sub =>
        mergeSubmissionPrivate({ ...(sub as unknown as Record<string, unknown>) }, byId.get(String((sub as any).id))),
      ) as unknown as ClassData['submissions'];
    }
  } catch {
    // Not permitted (non-owner) or missing — leave the rows as they are. A teacher
    // seeing no thread is a visible gap; a student seeing someone else's is not.
  }
  return classData;
};

/** Split a student object into its public (embeddable) part and its PII part. */
export const splitStudentPii = (
  student: Record<string, any>,
): { publicPart: Record<string, any>; pii: StudentPrivate } => {
  const publicPart: Record<string, any> = { ...student };
  const pii: Record<string, any> = {};
  // Purge deprecated credential residue — dropped entirely, never persisted.
  for (const f of STUDENT_DROP_FIELDS) if (publicPart[f] !== undefined) delete publicPart[f];
  for (const f of STUDENT_PII_FIELDS) {
    if (publicPart[f] !== undefined) {
      pii[f] = publicPart[f];
      delete publicPart[f];
    }
  }
  return { publicPart, pii: pii as StudentPrivate };
};

/** Merge a student's PII into the class's private roster doc. */
export const writeStudentPrivate = async (
  classId: string,
  studentId: string,
  pii: StudentPrivate,
): Promise<void> => {
  if (!pii || Object.keys(pii).length === 0) return;
  await setDoc(studentPrivateRef(classId), { [studentId]: removeUndefined(pii as Record<string, unknown>) }, { merge: true });
};

/** Drop a student's PII from the private roster doc (on delete). */
export const removeStudentPrivate = async (classId: string, studentId: string): Promise<void> => {
  try {
    await updateDoc(studentPrivateRef(classId), { [studentId]: deleteField() });
  } catch {
    // roster doc may not exist yet — nothing to remove
  }
};

/** Clear specific PII fields for one student in the roster (delete sentinel). Used
 *  when a field is set to undefined (writeStudentPrivate's merge can't remove a
 *  key), e.g. clearing pendingHubAverage on release or an offset in the gradebook. */
/**
 * Strict variant: rethrows anything but a missing roster document.
 *
 * `clearStudentPrivateFields` swallows every error and only logs. That is right for the places it was written
 * for — clearing a stale hub offset must not fail a grade save — and catastrophic for the vault migration,
 * which deletes plaintext on the strength of these calls succeeding. With the swallowing version the
 * migration reported "removed from our servers" while realFirstName, parentEmail and the OEN were still
 * sitting in the roster, and nobody was told.
 *
 * A missing roster document is still tolerated: there is nothing to clear, which is success.
 */
/**
 * Blank fields on MANY students in ONE atomic write.
 *
 * The vault migration used `updateStudent` per student, which is a `getDoc` plus a whole-array `updateDoc`
 * each time: 150 round trips for a class of 75, and 75 whole-array writes fanned out to every classmate's
 * `onSnapshot`. Slow, but the real problem is the race. The teacher's own `subscribeToClass` runs
 * `hydrateStudentPrivate`, which writes a stripped `students` array derived from an earlier snapshot — and if
 * one of those lands between two migration writes, it restores the field that was just blanked. The
 * migration then deletes that student's roster plaintext believing the class-doc copy is gone, and the next
 * "run this again" has nothing left to merge from.
 *
 * One transaction: one read, one write, no window. Also an order-of-magnitude fewer writes, which matters
 * because each one wakes thirty student clients.
 *
 * Values are blanked to '' rather than deleted, because an absent field on a student record reads as "never
 * had one"; a teacher looking at a migrated roster should see it is empty because it was emptied.
 */
export const blankStudentFieldsOnClassDoc = async (
  classId: string,
  fieldsByStudentId: Record<string, readonly string[]>,
): Promise<void> => {
  const ids = Object.keys(fieldsByStudentId);
  if (!ids.length) return;
  const classRef = doc(db, 'classes', classId);

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(classRef);
    if (!snap.exists()) throw new Error('Class not found');
    const current = ((snap.data() as { students?: Record<string, any>[] }).students ?? []);

    const students = current.map((st) => {
      const fields = fieldsByStudentId[st.id as string];
      if (!fields?.length) return st;
      const next: Record<string, any> = { ...st };
      for (const f of fields) next[f] = '';
      /**
       * STAMPED as a placeholder, in the same write that clears the plaintext.
       *
       * This function is the legacy migration's clear step, and the row it touches has a placeholder
       * in `firstName` with the real name moving into the vault. Nothing marked it as such, so after
       * the migration cleared `realFirstName` the row carried NO marker at all — and
       * `namesAlreadySafe` then read the placeholder as the child's real name. The conversion banner
       * told the teacher thirty real names were readable, and restoring a student deleted from that
       * class sealed their placeholder as their identity over the stored one.
       *
       * Set here rather than in a separate write, because the moment the marker is needed is the
       * moment `realFirstName` stops being there.
       */
      next.placeholderName = true;
      // Strip on the way out, as every other class-doc writer does, so this can never re-embed owner-only
      // fields that happen to be present on the in-transaction copy.
      return splitStudentPii(next).publicPart;
    });

    /**
     * Guarded like any other class-doc write. It no-ops unless a vault is unlocked for this class, and it
     * compares VALUES — so an untouched student carrying a decrypted name (because someone merged one on
     * elsewhere) is caught here rather than written out under cover of a migration.
     */
    assertNoVaultLeak({ students }, auth.currentUser?.uid ?? '', classId, 'this roster blanking');

    tx.update(classRef, { students });
  });
};

/**
 * Replace named fields on specific students' class-document rows.
 *
 * The sibling of `blankStudentFieldsOnClassDoc`, and the difference is the whole point. Blanking is
 * right when a field is being emptied — a student number that now lives in the vault. It is wrong for
 * the NAME, and converting a live class is the case where that matters: an ordinary class has no
 * pseudonym to fall back on, so blanking `firstName`/`lastName` leaves a grid of nameless rows with
 * marks beside them. The row has to be rewritten with the placeholder in place of the real name, in
 * the same write that empties everything else.
 *
 * Values are supplied per student rather than computed here, because the placeholder comes from
 * `planLiveConversion` — which assigns them across the whole class at once so the alphabetical order
 * is preserved and no word is issued twice.
 *
 * Transactional and leak-guarded like every other class-document writer: it compares VALUES against
 * what the unlocked vault holds, so a caller that passed a real name through by mistake is refused
 * here rather than writing it out under cover of a conversion.
 */
export const rewriteStudentFieldsOnClassDoc = async (
  classId: string,
  valuesByStudentId: Record<string, Record<string, unknown>>,
  /**
   * Placeholder first names to record as issued, in the SAME transaction as the rewrite.
   *
   * `addStudent` refuses a first name already in `usedNicknames` or already on the roster, which is
   * what stops two children answering to Abacus. A conversion that rewrote thirty rows without
   * recording the words it spent would let the next hand-add reissue one — and a separate write
   * afterwards leaves a window where it can.
   */
  addUsedNicknames: readonly string[] = [],
  /**
   * uid → studentId to merge into `memberStudentIds`, in the SAME transaction as the rewrite.
   *
   * The rewrite is what blanks `email`, and the email was the student portal's only way to find its
   * own row. Writing the map afterwards leaves a window — however short — in which every
   * account-joined student in the class is unresolvable and sees no grades at all.
   */
  addMemberStudentIds: Record<string, string> = {},
  /**
   * studentId → the name to put on every DENORMALISED copy: submissions, stream posts and quiz
   * attempts, all of which live on this same classmate-readable document.
   *
   * Renaming a roster row does not touch any of them. So a converted class kept every student's real
   * name in `submissions[].studentName`, `groupMemberNames`, `stream[].authorName` and
   * `popQuizAttempts[].studentName` — readable by all thirty classmates — while the banner called it
   * protected and the teacher UI showed placeholders everywhere, because it prefers the current
   * roster name when rendering. The `sanitiseRoster` path learned this the same way.
   *
   * It also breaks the teacher's own class afterwards: for a student with a one-word name,
   * `studentName` equals the decrypted first name, so the leak guard refuses the next
   * `updateClass({submissions})` and they can no longer post, mark a submission, or run a quiz.
   *
   * Applied inside THIS transaction, against the document as it actually is — not against the copy
   * the panel captured when it opened.
   */
  renameDenormalised: Record<string, { firstName: string; lastName: string }> = {},
): Promise<void> => {
  const ids = Object.keys(valuesByStudentId);
  /**
   * RETURN ONLY WHEN THERE IS NOTHING AT ALL TO WRITE.
   *
   * It returned on an empty `valuesByStudentId` alone — and the conversion's re-run path calls this
   * with exactly that: no rows left to convert, but a uid → studentId map to record. So the headline
   * retry fix ("a re-run links a student who has since signed in") wrote nothing, and the outcome was
   * computed from the in-memory map, so it reported `fullyProtected: true` and "nothing needed
   * converting" while the student still could not find their own row.
   *
   * The rest of the transaction already handles an empty `ids`: the missing-roster check iterates
   * nothing, `students` maps to itself, and the leak guard still scans the roster as it does on the
   * normal path.
   */
  if (!ids.length && !Object.keys(addMemberStudentIds).length && !addUsedNicknames.length
    && !Object.keys(renameDenormalised).length) return;
  const classRef = doc(db, 'classes', classId);

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(classRef);
    if (!snap.exists()) throw new Error('Class not found');
    const current = ((snap.data() as { students?: Record<string, any>[] }).students ?? []);

    /**
     * Every student named must actually be on the roster. A silent miss would leave that child's real
     * name on the document while the conversion reported success and deleted nothing else — the worst
     * available outcome, because the banner would then call the class protected.
     */
    const present = new Set(current.map(st => st.id as string));
    const missing = ids.filter(id => !present.has(id));
    if (missing.length) {
      throw new Error(
        `Cannot rewrite ${missing.length} student row${missing.length === 1 ? '' : 's'} on class `
        + `${classId}: no longer on the roster. Reload the class and try again.`,
      );
    }

    const students = current.map((st) => {
      const values = valuesByStudentId[st.id as string];
      if (!values) return st;
      // Strip on the way out, as every other class-doc writer does, so a caller cannot re-embed
      // owner-only fields that happen to be present on the in-transaction copy.
      return splitStudentPii({ ...st, ...values }).publicPart;
    });

    assertNoVaultLeak({ students }, auth.currentUser?.uid ?? '', classId, 'this roster rewrite');

    const update: Record<string, any> = { students };

    if (Object.keys(renameDenormalised).length) {
      const asClass = { ...(snap.data() as Record<string, unknown>), students } as unknown as ClassData;
      const map = new Map<string, Partial<Student>>(
        Object.entries(renameDenormalised).map(([id, n]) => [id, n as Partial<Student>]),
      );
      const patch = planDenormalisedNameRewrite(asClass, map);
      /**
       * STRIPPED, because Firestore rejects an explicit `undefined` and this project does not set
       * `ignoreUndefinedProperties`. `planDenormalisedNameRewrite` writes
       * `studentName: rename(..) ?? sub.studentName`, which is `undefined` when the field was absent
       * — so one submission with no `studentName` would make `tx.update` throw and every retry fail
       * at the same point. Dormant today because all current writers set the field; a dormant
       * unrecoverable failure in the most destructive path in the product is not worth keeping.
       */
      if (patch) Object.assign(update, stripUndefinedDeep(patch));
    }

    if (Object.keys(addMemberStudentIds).length) {
      const existing = ((snap.data() as { memberStudentIds?: Record<string, string> })
        .memberStudentIds ?? {});
      update.memberStudentIds = { ...existing, ...addMemberStudentIds };
    }
    if (addUsedNicknames.length) {
      const existing = ((snap.data() as { usedNicknames?: string[] }).usedNicknames ?? []);
      const merged = new Set([...existing, ...addUsedNicknames]);
      update.usedNicknames = [...merged];
    }
    tx.update(classRef, update);
  });
};

export const clearStudentPrivateFieldsStrict = async (
  classId: string, studentId: string, fields: string[],
): Promise<void> => {
  if (!fields.length) return;
  const patch: Record<string, ReturnType<typeof deleteField>> = {};
  for (const f of fields) patch[`${studentId}.${f}`] = deleteField();
  try {
    await updateDoc(studentPrivateRef(classId), patch);
  } catch (err) {
    if ((err as { code?: string })?.code === 'not-found') return;
    throw err;
  }
};

export const clearStudentPrivateFields = async (
  classId: string, studentId: string, fields: string[],
): Promise<void> => {
  if (!fields.length) return;
  const patch: Record<string, ReturnType<typeof deleteField>> = {};
  for (const f of fields) patch[`${studentId}.${f}`] = deleteField();
  try {
    await updateDoc(studentPrivateRef(classId), patch);
  } catch (err) {
    // A missing roster doc is expected (nothing to clear); anything else is a real
    // failure — a field the teacher meant to clear may still be in the roster.
    if ((err as { code?: string })?.code !== 'not-found') {
      console.error(`[clearStudentPrivateFields] failed to clear ${fields.join(',')} for student ${studentId} in class ${classId}:`, err);
    }
  }
};

/**
 * Read JUST the owner-only Hub-average overrides from the private roster.
 *
 * `releaseGrades` re-reads the class doc rather than trusting the caller's copy,
 * and the class doc has had `pendingHubAverage` / `studentHubAverageOffset`
 * stripped out of students[] — they are owner-only. Without this read, a release
 * computes every student's average as though the teacher had never adjusted it,
 * and since `releasedAverages` is the only mark students and parents ever see,
 * the adjustment reaches every teacher-facing surface and none of theirs.
 *
 * Narrow on purpose — hydrateStudentPrivate would do this and also run the PII
 * migration (a strip write against the class doc). A grade release is the wrong
 * moment to trigger a migration write on the very document it is updating.
 */
export const readHubOverrides = async (
  classId: string,
): Promise<Record<string, { pendingHubAverage?: number | null; studentHubAverageOffset?: number }>> => {
  try {
    const snap = await getDoc(studentPrivateRef(classId));
    if (!snap.exists()) return {};
    const roster = snap.data() as Record<string, StudentPrivate>;
    const out: Record<string, { pendingHubAverage?: number | null; studentHubAverageOffset?: number }> = {};
    for (const [studentId, priv] of Object.entries(roster || {})) {
      if (!priv || typeof priv !== 'object') continue;
      const entry: { pendingHubAverage?: number | null; studentHubAverageOffset?: number } = {};
      if (priv.pendingHubAverage !== undefined) entry.pendingHubAverage = priv.pendingHubAverage;
      if (priv.studentHubAverageOffset !== undefined) entry.studentHubAverageOffset = priv.studentHubAverageOffset;
      if (Object.keys(entry).length) out[studentId] = entry;
    }
    return out;
  } catch (err) {
    // FAIL LOUD. Returning {} silently is a wrong mark, not a missing one — the
    // student would be shown their unadjusted average and nothing would look
    // broken. The caller aborts the release instead.
    console.error(`[readHubOverrides] could not read the private roster for class ${classId}:`, err);
    throw err;
  }
};

/** Load the private roster and attach it to classData.studentPrivate (owner/admin
 *  only). Also (a) migrates any legacy PII still embedded on the class doc into
 *  the private roster and strips it off the doc, and (b) merges the
 *  MERGE_BACK_TO_STUDENT fields back onto the in-memory student objects so
 *  teacher UI that reads `student.X` keeps working (the persisted doc stays
 *  stripped, so classmates can't read them). */
export const hydrateStudentPrivate = async (classData: ClassData): Promise<ClassData> => {
  let roster: Record<string, StudentPrivate> = {};
  try {
    const snap = await getDoc(studentPrivateRef(classData.id));
    roster = snap.exists() ? (snap.data() as Record<string, StudentPrivate>) : {};

    // One-time migration. Two INDEPENDENT conditions (S1: don't couple them):
    //  - needsStripWrite: ANY PII/deprecated field is embedded → strip the doc,
    //    even when the roster ALREADY holds that value (a stale pre-migration
    //    client could otherwise re-embed a value that never gets stripped again).
    //  - rosterAdditions: embedded values not yet in the roster → persist them so
    //    stripping can't lose data.
    const rosterAdditions: Record<string, Record<string, any>> = {};
    let needsStripWrite = false;
    for (const s of classData.students || []) {
      const rec = s as Record<string, any>;
      const add: Record<string, any> = {};
      for (const f of STUDENT_PII_FIELDS) {
        if (rec[f] === undefined) continue;
        needsStripWrite = true; // present on the doc at all → must be stripped
        if (roster[s.id]?.[f as keyof StudentPrivate] === undefined) add[f] = rec[f];
      }
      if (Object.keys(add).length) rosterAdditions[s.id] = add;
      // Deprecated credential residue → strip (dropped, not moved). Warn: a live
      // hash was embedded on the classmate-readable doc.
      if (STUDENT_DROP_FIELDS.some(f => rec[f] !== undefined)) {
        needsStripWrite = true;
        console.warn(`[hydrateStudentPrivate] purging deprecated credential residue from student ${s.id} in class ${classData.id}`);
      }
    }
    if (Object.keys(rosterAdditions).length) {
      await setDoc(studentPrivateRef(classData.id), rosterAdditions, { merge: true });
      for (const [sid, pii] of Object.entries(rosterAdditions)) roster[sid] = { ...(roster[sid] || {}), ...pii } as StudentPrivate;
    }
    if (needsStripWrite) {
      const stripped = (classData.students || []).map(s => splitStudentPii({ ...s }).publicPart);
      await updateDoc(doc(db, 'classes', classData.id), { students: stripped }).catch(err => console.error('[hydrateStudentPrivate] strip write failed:', err));
    }
  } catch {
    // Not permitted (non-owner) or missing — leave undefined; accessor falls back.
    classData.studentPrivate = classData.studentPrivate || {};
    return classData;
  }

  classData.studentPrivate = roster;
  // Merge the display-required fields back onto the in-memory students (owner UI).
  classData.students = mergeBackStudentPrivate(classData.students, roster);
  return classData;
};

// ── Per-assignment AI grading instructions (owner-only) ──────────────────────
// These are teacher-only guidance for the AI grader ("mark grammar leniently",
// etc.). They must NEVER live on the class doc, which every classmate can read —
// same reasoning as student PII above. Stored in the owner-only private doc,
// hydrated onto assessments in memory for the teacher's UI, stripped on write.
const aiInstructionsRef = (classId: string) =>
  doc(db, 'classes', classId, 'private', 'aiGrading');

/** Remove aiGradingInstructions from every assessment before it hits the class doc. */
const stripAiInstructions = (assessments: any[]): any[] =>
  (assessments || []).map(a => {
    if (a && a.aiGradingInstructions !== undefined) {
      const { aiGradingInstructions: _drop, ...rest } = a;
      return rest;
    }
    return a;
  });

/** Read one assignment's AI grading instructions from the owner-only private doc — the
 *  SOURCE OF TRUTH. The grading panel reads this on open so a just-saved value can't be
 *  masked by a stale in-memory assessment (local state can be clobbered by a concurrent
 *  class reload / hydrate race). Returns '' when none. Owner/admin only. */
export const getAssignmentAiInstructions = async (
  classId: string, assessmentId: string,
): Promise<string> => {
  try {
    const snap = await getDoc(aiInstructionsRef(classId));
    const map = (snap.exists() ? snap.data() : {}) as Record<string, string>;
    return typeof map[assessmentId] === 'string' ? map[assessmentId] : '';
  } catch {
    return '';
  }
};

// ── Per-TEST answer keys (owner-only) ───────────────────────────────────────
// Keeps a test's answer-revealing fields OFF the student-readable class doc (see
// services/quizAnswerKey.ts). Stored in the owner-only private doc, keyed by quizId.
// Phase 2b DUAL-WRITE: the key is written here IN ADDITION to the (still-present)
// answers on the class doc, so nothing reads from it yet and behaviour is unchanged;
// later phases switch the teacher UI + server grader to read here, then strip the
// class-doc answers.
const quizKeysRef = (classId: string) => doc(db, 'classes', classId, 'private', 'quizKeys');

/** Owner: store a test's answer key in the private doc (keyed by quizId). Best-effort. */
export const setQuizAnswerKey = async (classId: string, quizId: string, key: QuizAnswerKey): Promise<void> => {
  try {
    await setDoc(quizKeysRef(classId), { [quizId]: key, updatedAt: new Date().toISOString() }, { merge: true });
  } catch (e) {
    console.error('setQuizAnswerKey failed', e);
  }
};

/** A student's in-progress (autosaved, not-yet-submitted) test answers, stored in the
 *  owner-only private doc. Read by the teacher's rescue view. */
export interface QuizDraft {
  quizId: string;
  studentId: string;
  studentName: string;
  answers: Record<string, unknown>;
  work: Record<string, unknown>;
  flagged: string[];
  notes: string;
  currentIndex: number;
  answeredCount: number;
  questionCount: number;
  startedAt: string;
  updatedAt: string;
}

const quizDraftRef = (classId: string, quizId: string, studentId: string) =>
  doc(db, 'classes', classId, 'private', `qd_${quizId}_${studentId}`);

/** Owner: read the in-progress autosave drafts for a test, for the given students
 *  (map studentId → draft; students with no draft are omitted). Best-effort. */
export const getQuizDrafts = async (
  classId: string,
  quizId: string,
  studentIds: string[],
): Promise<Record<string, QuizDraft>> => {
  const out: Record<string, QuizDraft> = {};
  await Promise.all(studentIds.map(async (sid) => {
    try {
      const snap = await getDoc(quizDraftRef(classId, quizId, sid));
      if (snap.exists()) out[sid] = snap.data() as QuizDraft;
    } catch { /* skip an unreadable draft */ }
  }));
  return out;
};

/** Owner: discard a student's in-progress draft (after a rescue submit, or granting a
 *  clean re-write). The attempt itself is persisted by the caller via the normal
 *  class-update path (which also syncs the linked assessment grade). */
export const clearQuizDraft = async (classId: string, quizId: string, studentId: string): Promise<void> => {
  try { await deleteDoc(quizDraftRef(classId, quizId, studentId)); }
  catch { /* best-effort */ }
};

/** Owner: read every test's answer key for a class (map quizId → key). Returns {} on error. */
export const getQuizAnswerKeys = async (classId: string): Promise<Record<string, QuizAnswerKey>> => {
  try {
    const snap = await getDoc(quizKeysRef(classId));
    if (!snap.exists()) return {};
    const data = snap.data() as Record<string, unknown>;
    const out: Record<string, QuizAnswerKey> = {};
    for (const k of Object.keys(data)) {
      if (k !== 'updatedAt' && data[k] && typeof data[k] === 'object') out[k] = data[k] as QuizAnswerKey;
    }
    return out;
  } catch {
    return {};
  }
};

/** Hydrate each TEST's answer key from the private quizKeys doc onto in-memory
 *  popQuizzes, so the teacher's UI (results, builder) still has the answers once
 *  they're no longer inline on the class doc. Owner-only: the private read fails for
 *  students, so they never get answers hydrated. Mutates classData in place (matches
 *  hydrateAiInstructions). Tests with no stored key are left unchanged. */
export const hydrateQuizAnswerKeys = async (classData: ClassData): Promise<ClassData> => {
  try {
    const keys = await getQuizAnswerKeys(classData.id);
    if (!Object.keys(keys).length) return classData;
    classData.popQuizzes = (classData.popQuizzes || []).map((qz) => {
      const key = keys[qz.id];
      if (qz.kind !== 'test' || !key) return qz;
      return {
        ...qz,
        questions: hydrateQuestions(qz.questions, key),
        ...(key.aiGradingInstructions ? { aiGradingInstructions: key.aiGradingInstructions } : {}),
      };
    });
  } catch {
    // non-owner / missing — leave as-is
  }
  return classData;
};

/** Set (or clear, when empty) one assignment's AI grading instructions. */
export const setAssignmentAiInstructions = async (
  classId: string, assessmentId: string, instructions: string | undefined,
): Promise<void> => {
  const ref = aiInstructionsRef(classId);
  const val = (instructions || '').trim();
  if (val) {
    await setDoc(ref, { [assessmentId]: val }, { merge: true });
  } else {
    await updateDoc(ref, { [assessmentId]: deleteField() }).catch(() => { /* doc may not exist */ });
  }
};

/** Load private AI instructions, merge onto in-memory assessments (owner UI), and
 *  migrate any legacy instructions still inline on the class doc. Owner/admin only. */
export const hydrateAiInstructions = async (classData: ClassData): Promise<ClassData> => {
  try {
    const snap = await getDoc(aiInstructionsRef(classData.id));
    const map: Record<string, string> = snap.exists() ? (snap.data() as Record<string, string>) : {};
    // One-time migration: pull any instructions still inline on the class doc into
    // the private doc, then strip them off the class doc so classmates can't read them.
    const legacy: Record<string, string> = {};
    for (const a of classData.assessments || []) {
      const inline = (a as { aiGradingInstructions?: string }).aiGradingInstructions;
      if (typeof inline === 'string' && inline.trim() && map[a.id] === undefined) legacy[a.id] = inline.trim();
    }
    if (Object.keys(legacy).length > 0) {
      await setDoc(aiInstructionsRef(classData.id), legacy, { merge: true });
      Object.assign(map, legacy);
      await updateDoc(doc(db, 'classes', classData.id), { assessments: stripAiInstructions(classData.assessments || []) }).catch(() => {});
    }
    // Hydrate for the teacher's UI (in-memory only; never written back inline).
    classData.assessments = (classData.assessments || []).map(a => ({
      ...a,
      aiGradingInstructions: map[a.id] !== undefined ? map[a.id] : undefined,
    })) as ClassData['assessments'];
  } catch {
    // non-owner / missing — leave as-is
  }
  return classData;
};

/**
 * Read a student's sensitive fields. Prefers the private roster doc; falls back
 * to any legacy still-embedded values (pre-backfill window) so the UI keeps
 * working during rollout. Returns {} if nothing is available.
 */
export const studentPrivateOf = (
  classData: Pick<ClassData, 'studentPrivate' | 'students'>,
  studentId: string,
): StudentPrivate => {
  const fromDoc = classData.studentPrivate?.[studentId];
  if (fromDoc) return fromDoc;
  const embedded = classData.students?.find(s => s.id === studentId) as Record<string, any> | undefined;
  if (!embedded) return {};
  const pii: Record<string, any> = {};
  for (const f of STUDENT_PII_FIELDS) if (embedded[f] !== undefined) pii[f] = embedded[f];
  return pii as StudentPrivate;
};

export const addStudent = async (classId: string, student: Omit<Student, 'id'>, preserveId?: string): Promise<string> => {
  /**
   * VAULT LEAK GUARD. No-ops unless a vault is unlocked for this class in this session, which is the only
   * moment a decrypted name can be in flight. Compares VALUES, not keys: a resolved real name lands in
   * `firstName`, the same field a pseudonym occupies, so a key allowlist sees nothing wrong.
   */
  assertNoVaultLeak(student, auth.currentUser?.uid ?? '', classId, 'this new student');

  const classRef = doc(db, 'classes', classId);
  // Normally a fresh id; restore-from-history passes the ORIGINAL id so the
  // snapshotted grade keys (`${studentId}-${assessmentId}`) match when they're
  // re-attached after the restore.
  const studentId = preserveId || `student_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  // Sensitive fields go to the private roster doc; only the public part is embedded.
  /**
   * A masked student must never be written, on ANY path.
   *
   * `updateClass` already refuses a masked roster, but addStudent writes through
   * its own transaction and skipped that check — which is how restoring a student
   * who had been deleted while names were masked put the PSEUDONYM back on the
   * roster permanently, and then made every later roster save throw forever.
   */
  if (isMaskedStudent(student)) {
    throw new Error('That student record has masked names. Turn off "Hide names" and try again.');
  }
  const { publicPart, pii } = splitStudentPii({ ...student, id: studentId });
  const newStudent = removeUndefined(publicPart);

  await runTransaction(db, async (transaction) => {
    const classSnap = await transaction.get(classRef);

    if (!classSnap.exists()) {
      throw new Error('Class not found');
    }

    const classData = classSnap.data() as ClassData;

    // Restore idempotency: if this exact id is already present (a prior restore
    // attempt that failed downstream is being retried), no-op instead of tripping
    // the duplicate-number/email guards — so the retry can proceed to re-attach
    // grades rather than wedging on "already exists".
    if (preserveId && classData.students.some(s => s.id === preserveId)) {
      return;
    }

    // Check for duplicate student number (only in non-nickname mode)
    if (!classData.useNicknames && student.studentNumber) {
      const duplicateByNumber = classData.students.find(
        s => s.studentNumber && s.studentNumber === student.studentNumber
      );
      if (duplicateByNumber) {
        throw new Error(`A student with student number ${student.studentNumber} already exists in this class. (${duplicateByNumber.firstName} ${duplicateByNumber.lastName})`);
      }
    }

    // Check for duplicate email (only if email is provided and not in nickname mode)
    if (!classData.useNicknames && student.email) {
      const duplicateByEmail = classData.students.find(
        s => s.email && s.email.toLowerCase() === student.email?.toLowerCase()
      );
      if (duplicateByEmail) {
        throw new Error(`A student with email ${student.email} already exists in this class. (${duplicateByEmail.firstName} ${duplicateByEmail.lastName})`);
      }
    }

    // Check for duplicate nickname in nickname mode
    if (classData.useNicknames) {
      if (classData.usedNicknames?.includes(student.firstName) ||
          classData.students?.some(s => s.firstName === student.firstName)) {
        throw new Error(`The nickname "${student.firstName}" is already in use.`);
      }
    }

    // Build update — use spread to add to arrays within the transaction
    const updatedStudents = [...(classData.students || []), newStudent];
    const updateData: Record<string, any> = {
      students: updatedStudents
    };

    if (classData.useNicknames) {
      updateData.usedNicknames = [...(classData.usedNicknames || []), student.firstName];
    }

    transaction.update(classRef, updateData);
  });

  // Persist sensitive fields to the owner/admin-only private roster doc.
  await writeStudentPrivate(classId, studentId, pii);

  // Fire-and-forget: sync to school directory if this is a school class
  (async () => {
    try {
      const classSnap = await getDoc(classRef);
      const classData = classSnap.data() as ClassData | undefined;
      if (!classData?.schoolId) return; // Not a school class — nothing to sync
      // Groups (teams, clubs, homerooms, staff spaces) are not part of the academic
      // roster — never mirror their members into the student directory. This also
      // guards against a staff-member group turning teachers into directory students.
      if (classData.classType === 'group') return;
      // A teacher or admin taking this class as a student stays in the class
      // roster and gradebook, but never rolls up into the school directory —
      // that feed drives the Students tab, graduation, the credit ledger and the
      // ONSIS export, where a teacher counted as a child is simply wrong.
      // studentDirectoryEligibility.ts names this as one of its enforcement
      // points; it was the one left unguarded, so a teacher added by hand to a
      // school class still became a directory student.
      if ((student as { isStaffParticipant?: boolean }).isStaffParticipant) return;

      const schoolId = classData.schoolId;
      const email = student.email?.toLowerCase().trim();
      const studentNumber = student.studentNumber?.trim();

      // Try to find matching school directory student
      let matchedSchoolStudentId: string | null = null;

      if (email) {
        const emailQuery = query(collection(db, `schools/${schoolId}/students`), where('email', '==', email));
        const emailSnap = await getDocs(emailQuery);
        if (!emailSnap.empty) matchedSchoolStudentId = emailSnap.docs[0].id;
      }
      if (!matchedSchoolStudentId && studentNumber) {
        const numQuery = query(collection(db, `schools/${schoolId}/students`), where('studentNumber', '==', studentNumber));
        const numSnap = await getDocs(numQuery);
        if (!numSnap.empty) matchedSchoolStudentId = numSnap.docs[0].id;
      }

      if (matchedSchoolStudentId) {
        // Link existing school student — patch the class roster entry
        const updatedClassSnap = await getDoc(classRef);
        const updatedClassData = updatedClassSnap.data() as ClassData;
        const updatedStudents = updatedClassData.students.map(s =>
          s.id === studentId ? { ...s, schoolStudentId: matchedSchoolStudentId, linkedSchoolId: schoolId } : s
        );
        await updateDoc(classRef, { students: updatedStudents });
        await ensureEnrollment(schoolId, matchedSchoolStudentId, classId, 'manual');
      } else {
        // Create new school directory entry
        const newSchoolStudent = removeUndefined({
          schoolId,
          firstName: student.firstName,
          lastName: student.lastName,
          studentNumber: student.studentNumber || '',
          email: student.email || '',
          parentName: student.parentName || '',
          parentEmail: student.parentEmail || '',
          parentPhone: student.parentPhone || '',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          sourceSystem: 'manual',
        } as unknown as Record<string, unknown>);
        const schoolStudentRef = await addDoc(collection(db, `schools/${schoolId}/students`), newSchoolStudent);

        // Patch class roster with the link
        const updatedClassSnap = await getDoc(classRef);
        const updatedClassData = updatedClassSnap.data() as ClassData;
        const updatedStudents = updatedClassData.students.map(s =>
          s.id === studentId ? { ...s, schoolStudentId: schoolStudentRef.id, linkedSchoolId: schoolId } : s
        );
        await updateDoc(classRef, { students: updatedStudents });
        await ensureEnrollment(schoolId, schoolStudentRef.id, classId, 'manual');
      }
    } catch (err) {
      console.error('[addStudent] Background school sync failed (non-blocking):', err);
    }
  })();

  return studentId;
};

// Update a student
export const updateStudent = async (
  classId: string, 
  studentId: string, 
  updates: Partial<Student>
): Promise<void> => {
  /**
   * VAULT LEAK GUARD. No-ops unless a vault is unlocked for this class. Compares VALUES, not keys,
   * because a resolved real name arrives in `firstName` — the same field a pseudonym occupies.
   */
  assertNoVaultLeak(updates, auth.currentUser?.uid ?? '', classId, 'this student update');
  const classRef = doc(db, 'classes', classId);
  const classDoc = await getDoc(classRef);
  
  if (!classDoc.exists()) {
    throw new Error('Class not found');
  }
  
  const classData = classDoc.data() as ClassData;

  // Route sensitive updates to the private roster; keep only public fields embedded.
  const { publicPart: publicUpdates, pii } = splitStudentPii(updates as Record<string, any>);
  // A PII field the caller explicitly set to undefined is a CLEAR — writeStudentPrivate
  // merges and can't remove a key, so collect these for a delete-sentinel pass.
  const piiClears = STUDENT_PII_FIELDS.filter(
    f => Object.prototype.hasOwnProperty.call(updates, f) && (updates as Record<string, any>)[f] === undefined,
  );
  const students = classData.students.map(s => {
    if (s.id !== studentId) return s;
    // Merge public updates AND strip any legacy embedded PII from this record.
    const { publicPart } = splitStudentPii({ ...s, ...publicUpdates });
    return removeUndefined(clearNameStampsIfRenamed(s, publicPart));
  });

  await updateDoc(classRef, { students });
  await writeStudentPrivate(classId, studentId, pii);
  if (piiClears.length) await clearStudentPrivateFields(classId, studentId, piiClears as string[]);
};

// Remove existing student data. TeacherApp's confirmed deletion workflow also
// permanently purges server-only observations before marking its History record complete.
export const deleteStudent = async (classId: string, studentId: string): Promise<void> => {
  const classRef = doc(db, 'classes', classId);
  const classDoc = await getDoc(classRef);
  
  if (!classDoc.exists()) {
    throw new Error('Class not found');
  }
  
  const classData = classDoc.data() as ClassData;
  const removedStudent = classData.students.find(s => s.id === studentId);
  const students = classData.students.filter(s => s.id !== studentId);

  const matches = (key: string) => key.startsWith(`${studentId}-`);
  // Keys to purge = union across BOTH embedded maps (see removedGradeKeys).
  const removedKeys = removedGradeKeys(classData, matches);

  // Remove this student's grades from the embedded map...
  const grades = { ...classData.grades };
  for (const key of Object.keys(grades)) if (matches(key)) delete grades[key];

  // ...and from the embedded releasedGrades map (previously left orphaned — a
  // deleted student's released grades lingered on the doc).
  const updatePayload: Record<string, any> = { students, grades };
  if (classData.releasedGrades) {
    const releasedGrades = { ...classData.releasedGrades };
    for (const key of Object.keys(releasedGrades)) if (matches(key)) delete releasedGrades[key];
    updatePayload.releasedGrades = releasedGrades;
  }

  // Everything else on the class doc that still names this student — their
  // submissions (work + feedback + the private thread), attendance marks, XP events
  // and seat. These used to survive a "delete" on a doc classmates can read.
  Object.assign(updatePayload, purgeStudentResidue(classData, studentId));

  /**
   * RELEASE THE NICKNAME, or the student can never be restored.
   *
   * `usedNicknames` is append-only: `addStudent` pushes to it and nothing ever removed an entry. In a
   * nickname or Privacy First class `addStudent` refuses a first name already in that list, and
   * restore-from-History goes through `addStudent` — so deleting a student permanently burned their
   * placeholder and the restore failed with "The nickname 'Abacus' is already in use." The grades,
   * residue and (in a vaulted class) the encrypted record were all still there; the restore was blocked
   * purely by bookkeeping.
   *
   * Only the leaving student's own entry is removed, so nobody else's claim is disturbed.
   */
  const freedNickname = (removedStudent?.firstName || '').trim();
  if (freedNickname && Array.isArray(classData.usedNicknames)) {
    const remaining = classData.usedNicknames.filter(n => n !== freedNickname);
    if (remaining.length !== classData.usedNicknames.length) {
      updatePayload.usedNicknames = remaining;
    }
  }

  // CRITICAL: remove the student's account uid(s) from classMembers, or their
  // portal keeps showing the class — the student side loads classes via a
  // `classMembers array-contains <uid>` query, so a lingering uid means a deleted
  // student still sees (and can open) the class indefinitely.
  const removedUids = (removedStudent?.authUids || []).filter(Boolean);
  if (removedUids.length) {
    const cm = Array.isArray(classData.classMembers) ? classData.classMembers : [];
    if (cm.some(m => removedUids.includes(m))) {
      updatePayload.classMembers = cm.filter(m => !removedUids.includes(m));
    }
  }

  await updateDoc(classRef, updatePayload);
  await removeStudentPrivate(classId, studentId);
  // Phase 0b: purge the mirrored grades/releasedGrades subcollection docs.
  await purgeGradeSubdocs(classId, removedKeys);
  // Their grade-history entries record marks + feedback — they can't survive an
  // erasure either. Best-effort: the student is already off the class.
  try {
    await purgeStudentGradeHistory(classId, studentId);
  } catch (err) {
    console.error(`[deleteStudent] could not purge gradeHistory for ${studentId} in ${classId}:`, err);
  }
  // Same for the attendance audit trail — it names the student.
  try {
    await purgeStudentAttendanceHistory(classId, studentId);
  } catch (err) {
    console.error(`[deleteStudent] could not purge attendanceHistory for ${studentId} in ${classId}:`, err);
  }
  // The school-wide attendance roll-up keeps its own copy of each day's marks, and
  // that path is readable by admins — so a deleted student's attendance would remain
  // visible there. Bounded by the dates this class actually marked.
  if (classData.schoolId) {
    try {
      const dates = (classData.attendance || []).map(d => d.date).filter(Boolean);
      if (dates.length) await purgeStudentFromSchoolAttendance(classData.schoolId, classId, studentId, dates);
    } catch (err) {
      console.error(`[deleteStudent] could not purge school attendance roll-ups for ${studentId} in ${classId}:`, err);
    }
    // Close the enrolment episode instead of leaving it orphaned as stale-active.
    // The record itself survives (OnSIS needs the enter/exit history) — we only
    // flip its status to 'dropped' + stamp droppedAt.
    if (removedStudent?.schoolStudentId) {
      try {
        await exitEnrollment(classData.schoolId, removedStudent.schoolStudentId, classId, 'dropped');
      } catch (err) {
        console.error(`[deleteStudent] could not close enrolment for ${studentId} in ${classId}:`, err);
      }
    }
  }
  // The nickname credential lives in the server-only studentAuth subcollection
  // (rules deny all client access), so it can't be removed from here — do it via
  // the Admin SDK. Best-effort: the student is already gone from the class either
  // way, and a stale hash is unreadable by anyone, but leaving it is an erasure gap.
  try {
    await callAuthedFunction('delete-student-auth', { classId, studentId });
  } catch (err) {
    console.error(`[deleteStudent] could not purge the nickname credential for ${studentId} in ${classId} (student is deleted; credential remains):`, err);
  }
};

/**
 * Withdraw (park) a student in a class WITHOUT deleting anything. Sets their
 * per-class status to 'withdrawn' + stamps withdrawnAt/reason — grades, feedback
 * and history are all retained; the gradebook shows them in a separate Withdrawn
 * section, out of the active roster and averages. For a school class this also
 * records the enrolment EXIT episode ('dropped') so the OnSIS record is complete.
 * Reversible via reinstateStudentInClass. This is the soft path; deleteStudent is
 * the irreversible one.
 */
export const withdrawStudentFromClass = async (
  classId: string,
  studentId: string,
  opts?: { reason?: string; by?: string; mode?: 'dropped' | 'transferred' },
): Promise<void> => {
  const classRef = doc(db, 'classes', classId);
  const classSnap = await getDoc(classRef);
  if (!classSnap.exists()) throw new Error('Class not found');
  const classData = classSnap.data() as ClassData;
  const target = classData.students.find(s => s.id === studentId);
  if (!target) throw new Error('Student not in class');

  const now = new Date().toISOString();
  const students = classData.students.map(s =>
    s.id === studentId
      ? removeUndefined({ ...s, status: 'withdrawn', withdrawnAt: now, withdrawnReason: opts?.reason }) as Student
      : s,
  );
  await updateDoc(classRef, { students });

  if (classData.schoolId && target.schoolStudentId) {
    await exitEnrollment(classData.schoolId, target.schoolStudentId, classId, opts?.mode ?? 'dropped', opts?.by, opts?.reason);
  }
};

/**
 * Reinstate a previously-withdrawn student — clears the withdrawn flags and, for
 * a school class, opens a fresh enrolment episode (re-entry is a new episode in
 * OnSIS terms, so we don't un-drop the old record). Grades were never touched.
 */
export const reinstateStudentInClass = async (
  classId: string,
  studentId: string,
  by?: string,
): Promise<void> => {
  const classRef = doc(db, 'classes', classId);
  const classSnap = await getDoc(classRef);
  if (!classSnap.exists()) throw new Error('Class not found');
  const classData = classSnap.data() as ClassData;
  const target = classData.students.find(s => s.id === studentId);
  if (!target) throw new Error('Student not in class');

  const students = classData.students.map(s => {
    if (s.id !== studentId) return s;
    const { withdrawnAt, withdrawnReason, ...rest } = s as Student & { withdrawnAt?: string; withdrawnReason?: string };
    void withdrawnAt; void withdrawnReason;
    return { ...rest, status: 'active' as const };
  });
  await updateDoc(classRef, { students });

  if (classData.schoolId && target.schoolStudentId) {
    await ensureEnrollment(classData.schoolId, target.schoolStudentId, classId, 'manual', by);
  }
};

// Add an assessment to a class
export const addAssessment = async (
  classId: string,
  assessment: Omit<Assessment, 'id'>,
  preserveId?: string
): Promise<string> => {
  const classRef = doc(db, 'classes', classId);
  const classDoc = await getDoc(classRef);

  if (!classDoc.exists()) {
    throw new Error('Class not found');
  }

  // Normally a fresh id; restore-from-history passes the ORIGINAL id so the existing
  // grades for this assessment (keyed studentId-assessmentId) reconnect.
  const assessmentId = preserveId || `assess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  // Keep AI grading instructions off the class doc (owner-only private storage).
  const { aiGradingInstructions, ...clean } = assessment as Omit<Assessment, 'id'> & { aiGradingInstructions?: string };
  const newAssessment = removeUndefined({ ...clean, id: assessmentId });

  await updateDoc(classRef, {
    assessments: arrayUnion(newAssessment)
  });
  if (typeof aiGradingInstructions === 'string' && aiGradingInstructions.trim()) {
    await setDoc(aiInstructionsRef(classId), { [assessmentId]: aiGradingInstructions.trim() }, { merge: true });
  }

  return assessmentId;
};

// Update an assessment
export const updateAssessment = async (
  classId: string, 
  assessmentId: string, 
  updates: Partial<Assessment>
): Promise<void> => {
  const classRef = doc(db, 'classes', classId);
  const classDoc = await getDoc(classRef);
  
  if (!classDoc.exists()) {
    throw new Error('Class not found');
  }
  
  const classData = classDoc.data() as ClassData;
  const assessments = classData.assessments.map(a =>
    a.id === assessmentId ? removeUndefined({ ...a, ...updates }) : a
  );

  // Never persist AI grading instructions on the class doc; relocate the target's
  // to the owner-only private doc (handles set + clear).
  await updateDoc(classRef, { assessments: stripAiInstructions(assessments) });
  if ('aiGradingInstructions' in updates) {
    await setAssignmentAiInstructions(classId, assessmentId, (updates as { aiGradingInstructions?: string }).aiGradingInstructions);
  }
};

// Delete an assessment
export const deleteAssessment = async (classId: string, assessmentId: string): Promise<void> => {
  const classRef = doc(db, 'classes', classId);
  const classDoc = await getDoc(classRef);
  
  if (!classDoc.exists()) {
    throw new Error('Class not found');
  }
  
  const classData = classDoc.data() as ClassData;
  const assessments = classData.assessments.filter(a => a.id !== assessmentId);

  const matches = (key: string) => key.endsWith(`-${assessmentId}`);
  // Keys to purge = union across BOTH embedded maps (see removedGradeKeys).
  const removedKeys = removedGradeKeys(classData, matches);

  // Remove this assessment's grades from the embedded map...
  const grades = { ...classData.grades };
  for (const key of Object.keys(grades)) if (matches(key)) delete grades[key];

  // ...and from the embedded releasedGrades map (previously left orphaned).
  const updatePayload: Record<string, any> = { assessments, grades };
  if (classData.releasedGrades) {
    const releasedGrades = { ...classData.releasedGrades };
    for (const key of Object.keys(releasedGrades)) if (matches(key)) delete releasedGrades[key];
    updatePayload.releasedGrades = releasedGrades;
  }

  await updateDoc(classRef, updatePayload);
  // Phase 0b: purge the mirrored grades/releasedGrades subcollection docs.
  await purgeGradeSubdocs(classId, removedKeys);
};

// Update a grade with rubric scores and optional feedback
export const updateGradeWithRubric = async (
  classId: string,
  studentId: string,
  assessmentId: string,
  score: number,
  rubricScores: Record<string, number>,
  feedback?: string
): Promise<void> => {
  const classRef = doc(db, 'classes', classId);
  const gradeKey = `${studentId}-${assessmentId}`;

  const gradeData: Record<string, any> = {
    studentId,
    assessmentId,
    rawScore: score,
    rubricScores,
    ...(feedback ? { feedback } : {}),
  };

  const classDoc = await getDoc(classRef);
  const classData = classDoc.data();

  const updateData: Record<string, any> = {
    [`grades.${gradeKey}`]: removeUndefined(gradeData)
  };

  if (classData && classData.releasedGrades === undefined) {
    updateData.releasedGrades = {};
  }

  await updateDoc(classRef, updateData);

  /**
   * Mirror with the student's REAL reader allow-list.
   *
   * This passed no readers, so it took the old `[]` default — and because the
   * mirror is a set, saving a rubric mark wiped the allow-list on that student's
   * grade doc. Invisible today only because students cannot read live grades at
   * all; the moment they do, a rubric save would have blanked their access.
   */
  const authUidsByStudent = await readAuthUidsByStudent(classId);
  await dualWriteGradeDoc(classId, gradeKey, {
    studentId,
    assessmentId,
    rawScore: score,
    rubricScores,
    ...(feedback ? { feedback } : {}),
  }, deriveReaders(authUidsByStudent[studentId]));
};

// ─── Phase 0b: grades subcollection dual-write ─────────────────────────────
//
// Embedded `classes/{classId}.grades` map remains the source of truth and the
// only thing we read from. Each write path also fans out to the new
// `classes/{classId}/grades/{gradeKey}` subcollection so we can validate +
// flip reads later without losing data.
//
// Failures here are NEVER allowed to block the embedded write — the embedded
// write has already succeeded by the time we get here, and the subcollection
// is best-effort. Drift gets caught and reconciled by `backfill-grades`.
const dualWriteGradeDoc = async (
  classId: string,
  gradeKey: string,
  grade: { studentId: string; assessmentId: string; rawScore: string | number; feedback?: string; rubricScores?: Record<string, number> } | null,
  /**
   * REQUIRED — no default, deliberately.
   *
   * This used to default to `[]`, and because the mirror is a SET rather than a
   * merge, any caller that forgot it OVERWROTE a student's reader allow-list with
   * nothing. That is a silent revocation: the grade is still there, the student
   * simply stops being able to read it, and nothing errors. Two callers were doing
   * exactly that. Making it required turns "forgot to think about readers" from a
   * runtime data bug into a compile error.
   */
  readerUids: string[],
): Promise<void> => {
  try {
    const ref = doc(db, 'classes', classId, 'grades', gradeKey);
    if (grade === null) {
      await deleteDoc(ref);
    } else {
      await setDoc(ref, removeUndefined({
        studentId: grade.studentId,
        assessmentId: grade.assessmentId,
        rawScore: grade.rawScore,
        ...(grade.feedback !== undefined ? { feedback: grade.feedback } : {}),
        ...(grade.rubricScores !== undefined ? { rubricScores: grade.rubricScores } : {}),
        // Stage 0: OVERWRITE readers from roster truth (set, never arrayUnion).
        readerUids,
      }));
    }
  } catch (err) {
    // Embedded is the source of truth — log and move on. backfill-grades
    // will catch any drift.
    console.warn('[dual-write grades]', { classId, gradeKey, err });
  }
};

// Phase 0b: exported so grade writes that happen OUTSIDE batchUpdateGrades — e.g.
// submission grading in App.tsx, which persists the grades map via updateClass —
// can keep the `grades` subcollection mirror in sync. Pass only the changed keys.
export const syncGradeSubdocs = async (
  classId: string,
  grades: Grade[],
  /**
   * REQUIRED, for the same reason the two mirrors below it are: an omitted roster
   * becomes `deriveReaders(undefined)` → `[]`, and since the stamp is a SET that
   * silently revokes a student's access to their own mark. Making the two mirrors
   * strict while leaving the default here would have closed the door and left the
   * window open one function over.
   */
  authUidsByStudent: Record<string, string[]>,
): Promise<void> => {
  if (grades.length === 0) return;
  await Promise.allSettled(
    grades.map(g => dualWriteGradeDoc(
      classId,
      `${g.studentId}-${g.assessmentId}`,
      g,
      deriveReaders(authUidsByStudent[g.studentId]),
    )),
  );
};

/**
 * Re-attach grades that were purged when a student/assessment was deleted, on a
 * restore-from-history. Merges the snapshot back into the class doc's EMBEDDED
 * grades/releasedGrades maps (the documented source of truth) and best-effort
 * re-mirrors the grade subdocs so students can see them again (the grade backfill
 * reconciles any mirror drift). Existing keys are preserved — only the restored
 * keys are added back. No-op when there's nothing to restore.
 */
export const reattachGradesForRestore = async (
  classId: string,
  gradesSnapshot?: Record<string, Grade>,
  releasedGradesSnapshot?: Record<string, Grade>,
): Promise<void> => {
  const g = gradesSnapshot || {};
  const r = releasedGradesSnapshot || {};
  if (Object.keys(g).length === 0 && Object.keys(r).length === 0) return;
  const classRef = doc(db, 'classes', classId);
  const snap = await getDoc(classRef);
  // There ARE grades to restore but the class is gone — throw so the caller keeps the
  // history record (never report success + delete it with the grades unattached).
  if (!snap.exists()) throw new Error('Class not found while re-attaching grades');
  const data = snap.data() as { grades?: Record<string, Grade>; releasedGrades?: Record<string, Grade> };
  const updates: Partial<ClassData> = {};
  if (Object.keys(g).length) updates.grades = { ...(data.grades || {}), ...g };
  if (Object.keys(r).length) updates.releasedGrades = { ...(data.releasedGrades || {}), ...r };
  await updateDoc(classRef, updates);
  /**
   * Re-mirror BOTH halves, with the real reader allow-lists.
   *
   * Two bugs here. The grade mirror passed no roster, so every restored grade doc
   * was written with an empty allow-list — a restore quietly revoked the student's
   * access to the marks it had just given back. And the RELEASED snapshot was
   * merged into the embedded map but never mirrored at all, so once students read
   * the subcollection a restored student's released marks would simply not exist
   * for them until the next full release.
   *
   * Best-effort on failure is still right — the embedded write has already
   * succeeded and the reconciler heals drift — but not caring about readers is not.
   */
  try {
    const authUidsByStudent = await readAuthUidsByStudent(classId);
    await syncGradeSubdocs(classId, Object.values(g), authUidsByStudent);
    /**
     * Restoring a student re-mirrors their released marks — through the SAME eligibility filter as
     * the release pipeline. Without it, an undo would re-publish a readable copy of a draft or
     * hidden assessment's mark that the release path had correctly withheld, which is the bug
     * reintroduced by a different door.
     */
    // `data` above is narrowly typed to the two grade maps; the eligibility predicate needs the
    // assessments and popQuizzes too, which the document carries. Merge the restored live marks in
    // so a release condition that reads them evaluates against the post-restore state.
    const fullClass = snap.data() as ClassData;
    const restored = { ...fullClass, grades: { ...(fullClass.grades || {}), ...g } } as ClassData;
    const { readable: restorableKeys } = splitReleasedGrades(restored, r as Record<string, Grade>);
    await Promise.allSettled(restorableKeys.map(key =>
      dualWriteReleasedGradeDoc(classId, key, r[key], deriveReaders(authUidsByStudent[r[key].studentId]))));
  } catch { /* backfill reconciles */ }
};

/**
 * FULL undo of a student deletion: re-attach the residue that delete purged
 * (submissions, attendance marks, XP events, seat) back onto the class doc, and the
 * parent-contact/OEN PII back onto the PRIVATE roster. Merges by id/date so existing
 * data (other students) is never clobbered. Portal access (classMembers/authUids)
 * is deliberately NOT restored — the student rejoins to regain it. No-op for legacy
 * records with no residue. Submission Storage files are never deleted on student
 * delete, so restored submissions reconnect to live files.
 */
export const restoreStudentResidue = async (
  classId: string,
  studentId: string,
  residue?: DeletionRecord['studentResidue'],
): Promise<void> => {
  if (!residue) return;
  const classRef = doc(db, 'classes', classId);
  const snap = await getDoc(classRef);
  if (!snap.exists()) return; // class gone — grades already succeeded; residue is best-effort
  const data = snap.data() as ClassData;
  const updates = computeResidueUpdates(data, studentId, residue);

  /**
   * This writes the class doc DIRECTLY, not through updateClass, so it needs its
   * own strip — the deletion-history snapshot still carries the private half of
   * every submission (threads, confidential notes) from before the migration, and
   * restoring a student would put it straight back on the classmate-readable doc.
   */
  if (Array.isArray(updates.submissions)) {
    updates.submissions = await persistSubmissionPrivate(
      classId, updates.submissions as unknown as Record<string, any>[],
    ) as unknown as ClassData['submissions'];
  }

  /**
   * REPLACE THE DENORMALISED NAME on restored submissions, in a class that holds no identity.
   *
   * `submissions[].studentName` is a snapshot of the name at submission time, and it is NOT in
   * `SUBMISSION_PRIVATE_FIELDS` — so a student who submitted work before the class was sealed has
   * "Maya Chen" sitting in the residue. The guard below then refuses the whole write, which sounds
   * right and makes the restore IMPOSSIBLE: `addStudent` has already run, the grades have already
   * re-attached, and every retry fails at the same point. The student is permanently half-restored —
   * no attendance, no XP, no seat, no submissions — and the history record can never be cleared.
   *
   * The name on a submission is a convenience for display, and every display now resolves it through
   * the vault anyway. So it is replaced with the placeholder the class document carries, which is both
   * safe to store and the right thing to show.
   */
  const promisesNoIdentity = data.useNicknames === true || data.privacyFirst === true;
  if (promisesNoIdentity && Array.isArray(updates.submissions)) {
    const placeholder = (data.students || []).find(st => st.id === studentId);
    const shownName = placeholder
      ? `${placeholder.firstName ?? ''} ${placeholder.lastName ?? ''}`.trim()
      : '';
    updates.submissions = (updates.submissions as unknown as Record<string, unknown>[]).map((sub) => {
      const next: Record<string, unknown> = { ...sub };
      if (sub.studentId === studentId && sub.studentName) next.studentName = shownName;

      /**
       * GROUP SUBMISSIONS CARRY EVERY MEMBER'S NAME, and missing them reproduced exactly the
       * unrecoverable half-restore this rewrite was written to prevent.
       *
       * `groupMemberNames` is a parallel array to `groupMemberIds`, is not in
       * SUBMISSION_PRIVATE_FIELDS, and is not touched by `persistSubmissionPrivate` — so a student who
       * handed in group work before the class was sealed has their own real name AND their classmates'
       * sitting in the residue. The guard matches leaf strings, including the assembled "First Last"
       * form, so it refused the whole write; `addStudent` and the grade re-attach had already run, so
       * every retry failed at the same point and the student stayed half-restored forever.
       *
       * Each member is resolved against the CLASS DOCUMENT, so a migrated classmate's real name is
       * replaced by their own placeholder rather than by this student's.
       */
      /**
       * ONLY the submissions this restore is actually putting back.
       *
       * `computeResidueUpdates` sets `updates.submissions = [...existing, ...restored]`, so this `.map`
       * walks every submission in the class — and the first version rewrote `groupMemberNames` on all
       * of them. Any pre-existing group submission whose two arrays had drifted out of alignment was
       * blanked for everybody, and a member who had since been deleted became `''`, in any nickname
       * class, with no vault involved. Silent degradation of data this restore has no business
       * touching.
       *
       * Scoped to rows that name the restored student: their own submissions, plus group work they
       * were part of. That is exactly the set whose stale names could refuse the write.
       */
      const memberIds = Array.isArray(sub.groupMemberIds) ? sub.groupMemberIds as string[] : [];
      const isThisRestore = sub.studentId === studentId || memberIds.includes(studentId);
      if (isThisRestore && Array.isArray(sub.groupMemberNames)) {
        const names = sub.groupMemberNames as unknown[];
        const ids = memberIds;
        /**
         * Only map by index when the two arrays ALIGN. `studentPurge` records that they can drift —
         * and a misaligned array means index i is somebody else, so resolving by position would put
         * one child's placeholder where another's name was. When we cannot tell who is who, every name
         * is dropped: a blank is residue, a wrong name is a mistake about a child.
         */
        const aligned = names.length === ids.length && ids.length > 0;
        next.groupMemberNames = names.map((_name, i) => {
          if (!aligned) return '';
          const member = (data.students || []).find(st => st.id === ids[i]);
          return member ? `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim() : '';
        });
      }
      return next;
    }) as unknown as ClassData['submissions'];
  }
  /**
   * VAULT LEAK GUARD, on a path that restores HISTORIC data.
   *
   * This is the one writer where the dangerous input is not something the app just computed but
   * something it stored months ago. A deletion record written before the vault existed — or before
   * `deletionSnapshot` stopped copying PII onto them — carries the child's real name, and restoring the
   * student would put it straight back onto the class document that the vault was created to keep it off.
   *
   * Both halves are checked, because they go to different places and only the second one is the PII
   * carrier the comment below is about.
   */
  assertNoVaultLeak(updates, auth.currentUser?.uid ?? '', classId, 'this restored student data');
  if (Object.keys(updates).length) await updateDoc(classRef, removeUndefined(updates));

  // Parent contact / OEN / accommodations / notes back to the OWNER-ONLY private
  // roster. NOT swallowed: this is the ONLY carrier of that PII (data.student doesn't
  // hold it), so if the write fails it must throw — the caller then keeps the history
  // record for a retry instead of deleting it and losing the PII forever.
  if (residue.studentPrivate && Object.keys(residue.studentPrivate).length) {
    assertNoVaultLeak(
      residue.studentPrivate, auth.currentUser?.uid ?? '', classId, 'this restored student PII',
    );
    /**
     * In a class that promises to hold no identity, the IDENTIFYING half of this belongs in the vault,
     * not back on the readable roster. The caller has already put it there — it splits the record
     * through `planVaultImport` and seals it — so writing it here too would recreate exactly the
     * readable copy the vault exists to replace.
     *
     * The rest (accommodations, notes, the parent magic-link token) is teacher working state that lives
     * on the roster in every class, so it still goes back.
     */
    const toRoster = promisesNoIdentity
      ? Object.fromEntries(
        Object.entries(residue.studentPrivate)
          .filter(([k]) => !(VAULTED_IMPORT_FIELDS as readonly string[]).includes(k)),
      )
      : residue.studentPrivate;
    if (Object.keys(toRoster).length) {
      await setDoc(studentPrivateRef(classId), { [studentId]: removeUndefined(toRoster) }, { merge: true });
    }
  }
};

const dualWriteReleasedGradeDoc = async (
  classId: string,
  gradeKey: string,
  grade: Grade,
  /**
   * REQUIRED, for the same reason as dualWriteGradeDoc: the stamp is a SET, so a
   * forgotten argument silently revokes a student's access to their own released
   * mark. This one matters more — students DO read this collection.
   */
  readerUids: string[],
): Promise<void> => {
  try {
    const ref = doc(db, 'classes', classId, 'releasedGrades', gradeKey);
    await setDoc(ref, removeUndefined({
      studentId: grade.studentId,
      assessmentId: grade.assessmentId,
      rawScore: grade.rawScore,
      ...(grade.feedback !== undefined ? { feedback: grade.feedback } : {}),
      ...(grade.rubricScores !== undefined ? { rubricScores: grade.rubricScores } : {}),
      // Stage 0: OVERWRITE readers from roster truth (set, never arrayUnion).
      readerUids,
    }));
  } catch (err) {
    console.warn('[dual-write releasedGrades]', { classId, gradeKey, err });
  }
};

// Phase 0b: when grade keys are removed from the embedded map (student or
// assessment deletion), delete the mirrored docs from BOTH the grades and
// releasedGrades subcollections so they don't orphan. Best-effort; deleteDoc on a
// non-existent doc is a no-op, so passing keys that were never released is safe.
export const purgeGradeSubdocs = async (classId: string, gradeKeys: string[]): Promise<void> => {
  if (gradeKeys.length === 0) return;
  await Promise.allSettled(
    gradeKeys.flatMap(k => [
      deleteDoc(doc(db, 'classes', classId, 'grades', k)),
      deleteDoc(doc(db, 'classes', classId, 'releasedGrades', k)),
    ]),
  );
};

// Phase 0b read path: fetch one student's grade-history entries from the
// subcollection. Single equality filter (studentId) — auto-indexed, no composite
// index needed; caller sorts client-side. Owner/school-admin only (firestore
// rules). Returns `null` on error (distinct from an empty [] result) so the UI
// can show "couldn't load — retry" instead of silently rendering no history —
// important once the embedded fallback is dropped and the subcollection is the
// only source.
/** Delete every gradeHistory entry for one student (on student deletion). Their
 *  history records marks + feedback, so it can't survive an erasure. */
export const purgeStudentGradeHistory = async (classId: string, studentId: string): Promise<void> => {
  const snap = await getDocs(query(
    collection(db, 'classes', classId, 'gradeHistory'),
    where('studentId', '==', studentId),
  ));
  await Promise.allSettled(snap.docs.map(d => deleteDoc(d.ref)));
};

/** Delete every attendanceHistory entry for one student (on student deletion) — the
 *  attendance audit trail names the student, so it can't survive an erasure either. */
export const purgeStudentAttendanceHistory = async (classId: string, studentId: string): Promise<void> => {
  const snap = await getDocs(query(
    collection(db, 'classes', classId, 'attendanceHistory'),
    where('studentId', '==', studentId),
  ));
  await Promise.allSettled(snap.docs.map(d => deleteDoc(d.ref)));
};

/**
 * Remove one student from a school's attendance roll-up docs
 * (schools/{schoolId}/attendance/{date}/records/{classId}).
 *
 * Bounded by the class's own attendance dates rather than scanning the school — we
 * already know exactly which days this class marked. Best-effort per day.
 */
export const purgeStudentFromSchoolAttendance = async (
  schoolId: string,
  classId: string,
  studentId: string,
  dates: string[],
): Promise<void> => {
  await Promise.allSettled(dates.map(async (date) => {
    const ref = doc(db, 'schools', schoolId, 'attendance', date, 'records', classId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return;
    const data = snap.data() as { records?: Record<string, string>; lateTimes?: Record<string, string> };
    if (data.records?.[studentId] === undefined && data.lateTimes?.[studentId] === undefined) return;
    const records = { ...(data.records || {}) }; delete records[studentId];
    const lateTimes = { ...(data.lateTimes || {}) }; delete lateTimes[studentId];
    await updateDoc(ref, { records, lateTimes, updatedAt: new Date().toISOString() });
  }));
};

export const getStudentGradeHistory = async (
  classId: string,
  studentId: string,
): Promise<GradeHistoryEntry[] | null> => {
  try {
    const q = query(
      collection(db, 'classes', classId, 'gradeHistory'),
      where('studentId', '==', studentId),
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => d.data() as GradeHistoryEntry);
  } catch (err) {
    console.warn('[getStudentGradeHistory]', { classId, studentId, err });
    return null;
  }
};

// Batch-write all pending grades + history entries in a single Firestore op.
// Replaces the per-grade auto-save model — collapses N grade edits into 1 write.
//
// `ensureReleasedGradesField` opts the class into release control (writes
// releasedGrades: {} if missing). Only relevant for legacy classes where the
// field doesn't exist yet; the caller passes this based on local class state
// so we avoid an extra getDoc.
export const batchUpdateGrades = async (
  classId: string,
  grades: Array<Grade>,
  historyEntries: GradeHistoryEntry[],
  ensureReleasedGradesField: boolean,
  // Stage 0: the reader allow-list per studentId, threaded from the caller's
  // hydrated roster (no per-save roster read). Missing entry → [] (empty readers),
  // never a throw — the reconcile heals it later.
  authUidsByStudent: Record<string, string[]> = {},
): Promise<void> => {
  if (grades.length === 0 && historyEntries.length === 0) return;
  const classRef = doc(db, 'classes', classId);
  const updateData: Record<string, any> = {};

  // Read existing grades so a rawScore-only save doesn't wipe feedback,
  // rubricScores, submittedAt, etc. Writing the whole object at `grades.KEY`
  // replaces it wholesale, so we merge the prior fields forward.
  const existingGrades = grades.length > 0
    ? (((await getDoc(classRef)).data()?.grades) as Record<string, Grade> | undefined) || {}
    : {};

  const mergedList: Grade[] = [];
  for (const g of grades) {
    const gradeKey = `${g.studentId}-${g.assessmentId}`;
    const mergedGrade: Grade = {
      ...(existingGrades[gradeKey] || {}),
      studentId: g.studentId,
      assessmentId: g.assessmentId,
      rawScore: g.rawScore,
      ...(g.feedback !== undefined ? { feedback: g.feedback } : {}),
      ...(g.rubricScores !== undefined ? { rubricScores: g.rubricScores } : {}),
    };
    updateData[`grades.${gradeKey}`] = removeUndefined(mergedGrade as unknown as Record<string, any>);
    mergedList.push(mergedGrade);
  }
  if (ensureReleasedGradesField) {
    updateData.releasedGrades = {};
  }

  // Phase 0b: history entries no longer append to the embedded `gradeHistory[]`
  // array (unbounded growth was the #1 contributor to class-doc bloat toward the
  // 1 MiB limit). New entries are written ONLY to the `gradeHistory` subcollection.
  // Because there is no embedded copy anymore, history is PRIMARY data, not a
  // best-effort mirror — a lost write is an unrecoverable loss of the audit record
  // (backfill only copies embedded→sub, so it can't recover what was never
  // embedded).
  // Stage 0 #5: the grade mirror subdocs are no longer a best-effort post-batch
  // write — they're folded INTO the batch(es), because Stage 2 makes students read
  // the subcollection, so a missed mirror = a grade the student can't see. Each
  // carries readerUids OVERWRITTEN from roster truth (deriveReaders; set, never
  // arrayUnion). A student with no roster entry gets [] readers, not a throw.
  const mirrors = mergedList.map(m => ({
    key: `${m.studentId}-${m.assessmentId}`,
    data: removeUndefined({
      studentId: m.studentId,
      assessmentId: m.assessmentId,
      rawScore: m.rawScore,
      ...(m.feedback !== undefined ? { feedback: m.feedback } : {}),
      ...(m.rubricScores !== undefined ? { rubricScores: m.rubricScores } : {}),
      readerUids: deriveReaders(authUidsByStudent[m.studentId]),
    }),
  }));

  // D1 ordering — see planGradeWrite. Fits one batch → single atomic writeBatch
  // (embedded update + history + mirrors, all-or-nothing). Otherwise history +
  // mirror sets commit first in sequential chunks and the embedded class-doc update
  // lands ALONE in the final batch, so a mid-run crash can only leave orphan
  // mirror/history subdocs (reconcile deletes them) — never an embedded grade whose
  // readable mirror never landed, nor a grade change with no audit record.
  const plan = planGradeWrite({
    classUpdate: updateData,
    mirrors,
    history: historyEntries.map(e => ({ id: e.id, data: removeUndefined(e as unknown as Record<string, any>) })),
  });

  for (const batchOps of plan) {
    const b = writeBatch(db);
    for (const op of batchOps) {
      if (op.kind === 'classUpdate') b.update(classRef, op.data as Record<string, any>);
      else if (op.kind === 'mirrorSet') b.set(doc(db, 'classes', classId, 'grades', op.key), op.data as Record<string, any>);
      else b.set(doc(db, 'classes', classId, 'gradeHistory', op.id), op.data as Record<string, any>);
    }
    await b.commit(); // sequential — a failed chunk stops the rest
  }
};

// Release grades to students (copy current grades to releasedGrades)
export const releaseGrades = async (
  classId: string,
  markSet?: MarkSet,
  // Stage 0: reader allow-list per studentId, threaded from the caller's hydrated
  // roster (the class doc read here has no authUids — they're stripped to the roster).
  authUidsByStudent: Record<string, string[]> = {},
): Promise<void> => {
  const classRef = doc(db, 'classes', classId);
  const classDoc = await getDoc(classRef);

  if (!classDoc.exists()) {
    throw new Error('Class not found');
  }

  const classData = { ...classDoc.data(), id: classId } as ClassData;

  // A TEST whose results have NOT been released must stay fully hidden from
  // students — no grade row, no mark, and NOT counted in the released average —
  // until the teacher releases its results. So we strip those assessments from
  // both the released grade map and the average snapshot. (The teacher's own
  // report-card average, via getEffectiveAverage on the full classData, still
  // includes everything — this only governs what students see.)
  const withheldTestAssessmentIds = new Set<string>();
  (classData.popQuizzes || []).forEach(qz => {
    if (qz.kind === 'test' && !qz.resultsReleased) {
      (qz.assessmentIds || []).forEach(aid => aid && withheldTestAssessmentIds.add(aid));
    }
  });
  const releasedGradeMap: Record<string, any> = {};
  for (const [key, grade] of Object.entries(classData.grades || {})) {
    if (grade && !withheldTestAssessmentIds.has((grade as any).assessmentId)) {
      releasedGradeMap[key] = grade;
    }
  }
  // Average snapshot must exclude the same withheld tests, so drop them from the
  // classData the average is computed over.
  const studentFacingClassData = withheldTestAssessmentIds.size > 0
    ? { ...classData, assessments: (classData.assessments || []).filter(a => !withheldTestAssessmentIds.has(a.id)) }
    : classData;

  const update: Record<string, any> = {
    releasedGrades: releasedGradeMap,
    gradesReleasedAt: new Date().toISOString(),
  };

  // Store each student's KATC-WEIGHTED average so the student + parent portals
  // show the SAME number the teacher's report card shows. They can't read the
  // owner-only markSet, so they read this stored value instead of recomputing a
  // (different) flat average. Only the released snapshot is stored.
  if (markSet) {
    /**
     * HYDRATE FIRST. `classData` above came from the class doc, where the teacher's
     * Hub-average adjustment has been stripped out of students[] — it is owner-only.
     * Computing the averages off that produces every student's UNADJUSTED mark, with
     * nothing to indicate anything went wrong, and since this map is the only final
     * mark students and parents ever read, the adjustment would land on every
     * teacher-facing surface and none of theirs.
     *
     * Deliberately not caught: if the roster read fails we abort the release rather
     * than publish marks we know might be wrong. Nothing has been written yet.
     */
    const hubOverrides = await readHubOverrides(classId);
    const hydratedStudents = (studentFacingClassData.students || []).map(s => {
      const override = hubOverrides[s.id];
      return override ? ({ ...s, ...override } as typeof s) : s;
    });
    // includePendingDrafts: THIS action promotes the draft to studentHubAverage
    // immediately afterwards (see the release handler), so publishing it here is
    // what keeps the two in step. No other caller may pass true.
    update.releasedAverages = computeReleasedAverages(
      hydratedStudents, studentFacingClassData, markSet, { includePendingDrafts: true },
    );
    // Snapshot the effective category axes (id/name/weight/color) so the student
    // portal can draw its "Category breakdown" radar without reading the owner-only
    // markSet.
    //
    // DECIDED (2026-08-22): the WEIGHTS ship too, knowingly. A student who combines
    // their own category marks by these weights can compare the result against the
    // headline figure and infer that it was adjusted. That is accepted: the effort
    // is deliberate, mark-set weights are usually shared with students anyway, and
    // the alternative costs every student their category breakdown to close a gap
    // that predates the Hub offset and applies equally to older pinned marks.
    // Not an oversight — do not "fix" it by dropping weights without asking. Values are still recomputed client-side by calculateCategoryAverage
    // on releasedGrades — this is only the axis definitions (mirrors the parent
    // portal, which reads the markSet server-side).
    // Guard EVERY field: a legacy markSet category doc missing color/weight must
    // NOT make this updateDoc throw — that would abort the whole release
    // (releasedGrades/releasedAverages/gradesReleasedAt never written) and grades
    // would silently fail to release. Releasing grades must never depend on a
    // category having a colour. Firestore also rejects `undefined`, so default
    // every field to a concrete value.
    update.releasedCategories = (markSet.categories || []).map((cat) => ({
      id: String(cat?.id ?? ''),
      name: String(cat?.name ?? ''),
      weight: cat?.weight ?? 0,
      color: cat?.color ?? '#8b5cf6',
    }));
    /**
     * Snapshot the per-assessment WEIGHTING too, so a later change to it can be detected.
     *
     * `hasUnreleasedGrades` compares grades, Hub drafts and Hub offsets — every per-student
     * value — and nothing compared the weighting, which decides a student's average just as
     * surely as the marks do. That gap became reachable the moment the assessment page grew a
     * weight adjuster: one click moves every average while the student portal still shows the
     * figure snapshotted here, under per-assessment shares computed from the LIVE weights.
     *
     * A snapshot rather than "recompute and compare": an unreleased test's grades are stripped
     * from releasedGrades, so a fresh calculation includes marks the released average
     * legitimately excludes. The two would never agree and the banner would latch on forever.
     * See services/releaseStaleness, and the same warning in hasUnreleasedGrades.
     *
     * Guarded like the categories above — releasing must never fail over a weight.
     */
    update.releasedWeighting = weightingToRelease(
      classData.assessments || [], releasedGradeMap as Record<string, { assessmentId?: string }>);
  }

  await updateDoc(classRef, update);

  /**
   * Mirror into the STUDENT-READABLE subcollection — and only what a student may actually see.
   *
   * `releasedGradeMap` above is the TEACHER's released snapshot and is written to the class
   * document unchanged. It is not the same question as "may a student read a copy of this". A
   * teacher who releases the term's finished work while a draft assessment carries provisional
   * marks has released those marks into their own snapshot, and has NOT decided to publish them
   * to the student — `isDraft` is their "not yet".
   *
   * This used to filter on unreleased tests alone, so draft, hidden and release-condition-gated
   * marks (and their FEEDBACK, which is where teachers write notes to themselves) reached a
   * document the student could query directly. The portal never rendered them, which meant the
   * UI was the only thing honouring the restriction.
   *
   * `splitReleasedGrades` applies the same predicate `buildStudentClassView` uses to decide which
   * assessments a student may see, including per-student release conditions, so the two cannot
   * drift apart.
   */
  const { readable: readableKeys } = splitReleasedGrades(classData, releasedGradeMap as Record<string, Grade>);
  await Promise.allSettled(
    readableKeys.map(gradeKey => dualWriteReleasedGradeDoc(
      classId,
      gradeKey,
      releasedGradeMap[gradeKey] as Grade,
      deriveReaders(authUidsByStudent[(releasedGradeMap[gradeKey] as Grade).studentId]),
    )),
  );

  /**
   * REVOKE the copies that must not exist — the half that handles "previously released, now
   * restricted again".
   *
   * The old purge deleted only keys linked to an unreleased test, so a mark released and then
   * re-drafted or re-hidden kept its readable document indefinitely. This covers all four
   * restrictions instead.
   *
   * SCOPED DELIBERATELY TO THE RESTRICTED SET. `keysToRevoke` also returns keys that are in the
   * live grades but absent from the released snapshot — orphan-adjacent, and deleting those is
   * bulk cleanup rather than a release keeping its own mirror honest. Orphan handling and any
   * other production deletion are deferred to a separately approved cleanup task, so this stops
   * at what this release action itself made restricted.
   *
   * Deleting the COPY, never the record: `classData.grades` and `classData.releasedGrades` keep
   * the mark and its feedback, so nothing is lost and nothing is regraded. Students no longer
   * read this collection at all, so the deletion is defence in depth rather than the control.
   */
  const revokeKeys = splitReleasedGrades(classData, releasedGradeMap as Record<string, Grade>)
    .restricted.map(r => r.key);
  await Promise.allSettled(
    revokeKeys.map(k => deleteDoc(doc(db, 'classes', classId, 'releasedGrades', k))),
  );
};

/** Read the attendance edit trail for a class (optionally one date), newest first. */
export const getAttendanceHistory = async (
  classId: string,
  date?: string,
): Promise<AttendanceHistoryEntry[]> => {
  const col = collection(db, 'classes', classId, 'attendanceHistory');
  const q = date ? query(col, where('date', '==', date)) : col;
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ ...d.data(), id: d.id } as AttendanceHistoryEntry))
    .sort((x, y) => (y.changedAt || '').localeCompare(x.changedAt || ''));
};

// Update attendance for a date (dual-write: class-embedded + school subcollection)
export const updateAttendance = async (
  classId: string,
  date: string,
  records: Record<string, 'P' | 'A' | 'L' | 'E'>,
  lateTimes?: Record<string, string>,
  schoolId?: string,
  notes?: Record<string, string>,
  actor?: { uid?: string; name?: string },
): Promise<void> => {
  const classRef = doc(db, 'classes', classId);
  const classDoc = await getDoc(classRef);

  if (!classDoc.exists()) {
    throw new Error('Class not found');
  }

  const classData = classDoc.data() as ClassData;
  const attendance = [...(classData.attendance || [])];

  const existingIndex = attendance.findIndex(a => a.date === date);
  const attendanceRecord = removeUndefined({
    date,
    records,
    lateTimes: lateTimes || {},
    ...(notes && Object.keys(notes).length > 0 ? { notes } : {}),
  }) as AttendanceDay;

  // Diff BEFORE overwriting the day, so the audit trail captures what changed.
  const previousDay = existingIndex >= 0 ? attendance[existingIndex] : undefined;

  if (existingIndex >= 0) {
    attendance[existingIndex] = attendanceRecord;
  } else {
    attendance.push(attendanceRecord);
  }

  await updateDoc(classRef, { attendance });

  // Audit trail: attendance is stored wholesale-replace, so without this an edit
  // (A → P) leaves no trace of the original mark or who changed it — the thing an
  // attendance inspection asks for. Written to the attendanceHistory SUBCOLLECTION
  // (never embedded — 1 MiB doc limit, same as gradeHistory). Best-effort: the
  // attendance itself is already saved and must not fail on a history hiccup.
  try {
    const entries = diffAttendanceDay(
      date,
      previousDay,
      { records, lateTimes, notes },
      { uid: actor?.uid, name: actor?.name, at: new Date().toISOString() },
    );
    if (entries.length) {
      const batch = writeBatch(db);
      for (const e of entries) {
        const { id, ...rest } = e;
        batch.set(doc(db, 'classes', classId, 'attendanceHistory', id), rest as Record<string, any>);
      }
      // NOT awaited: the attendance itself is already saved, so the audit write must
      // never add a hang window in front of the school dual-write / the caller's UI
      // update if the network drops mid-call (a hang isn't a rejection, so awaiting
      // + catching wouldn't cover it). Failures are logged; the trail is best-effort.
      void batch.commit().catch((err) => {
        console.error(`[updateAttendance] attendance history write failed (non-fatal) for ${classId} ${date}:`, err);
      });
    }
  } catch (err) {
    console.error(`[updateAttendance] attendance history write failed (non-fatal) for ${classId} ${date}:`, err);
  }

  // Dual-write to school-level subcollection (if school context is available)
  if (schoolId) {
    await writeSchoolAttendance(schoolId, classId, date, records, lateTimes);
  }
};

// ─── School-Level Attendance Subcollection ──────────────────────────────────

/** Write per-student attendance records into schools/{schoolId}/attendance/{date}/records/{classId} */
export const writeSchoolAttendance = async (
  schoolId: string,
  classId: string,
  date: string,
  records: Record<string, 'P' | 'A' | 'L' | 'E'>,
  lateTimes?: Record<string, string>,
): Promise<void> => {
  // Store one document per class per date under the school attendance subcollection
  const ref = doc(db, 'schools', schoolId, 'attendance', date, 'records', classId);
  await setDoc(ref, removeUndefined({
    classId,
    records,
    lateTimes: lateTimes || {},
    updatedAt: new Date().toISOString(),
  }));
};

/** Read all attendance records for a school on a given date */
export const getSchoolAttendanceForDate = async (
  schoolId: string,
  date: string,
): Promise<{ classId: string; records: Record<string, 'P' | 'A' | 'L' | 'E'>; lateTimes?: Record<string, string> }[]> => {
  const snap = await getDocs(collection(db, 'schools', schoolId, 'attendance', date, 'records'));
  return snap.docs.map(d => ({
    classId: d.id,
    ...d.data(),
  })) as { classId: string; records: Record<string, 'P' | 'A' | 'L' | 'E'>; lateTimes?: Record<string, string> }[];
};

/** Subscribe to real-time attendance updates for a school on a given date */
export const subscribeToSchoolAttendance = (
  schoolId: string,
  date: string,
  onUpdate: (data: { classId: string; records: Record<string, 'P' | 'A' | 'L' | 'E'> }[]) => void,
): (() => void) => {
  return onSnapshot(
    collection(db, 'schools', schoolId, 'attendance', date, 'records'),
    (snap) => {
      const result = snap.docs.map(d => ({
        classId: d.id,
        ...d.data(),
      })) as { classId: string; records: Record<string, 'P' | 'A' | 'L' | 'E'> }[];
      onUpdate(result);
    },
  );
};

// Update seating chart
export const updateSeatingChart = async (
  classId: string,
  seating: Record<string, { row: number; col: number } | null>,
  rows?: number,
  cols?: number,
  freeformPositions?: Record<string, { x: number; y: number }>,
  /** Grid cells the teacher removed, as "row,col" — real rooms are not
   *  rectangles, and without this an uneven back row cannot be drawn. */
  removedSeats?: string[],
): Promise<void> => {
  const classRef = doc(db, 'classes', classId);
  await updateDoc(classRef, {
    seatingChart: removeUndefined({
      positions: seating,
      rows: rows || 5,
      cols: cols || 6,
      freeformPositions,  // persist the drag-anywhere layout (was silently dropped)
      removedSeats,
      updatedAt: new Date().toISOString()
    })
  });
};

// ==================== DELETION HISTORY ====================

// Get deletion history for a teacher
export const getDeletionHistory = async (teacherId: string, requireServer = false): Promise<DeletionRecord[]> => {
  const q = query(
    collection(db, 'deletionHistory'),
    where('ownerId', '==', teacherId)
  );
  const snapshot = requireServer ? await getDocsFromServer(q) : await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as DeletionRecord));
};

// Add item to deletion history
export const addToDeletionHistory = async (teacherId: string, record: Omit<DeletionRecord, 'id' | 'ownerId'>): Promise<string> => {
  /**
   * BACKSTOP, not the strip itself.
   *
   * The callers sanitise before they get here (see deletionSnapshot.ts). This refuses a record that
   * still carries owner-only PII on a class that promises it holds none, because the failure mode is a
   * NEW delete path written later that does not know to strip — which is exactly how a complete copy of
   * every child's parent contact and OEN ended up in this collection in the first place.
   *
   * Throws rather than silently stripping: a caller that thinks it is storing restorable data and is
   * not should fail loudly, not quietly hand the teacher a snapshot that cannot restore.
   */
  /**
   * VAULT LEAK GUARD, as well as the key-based backstop below.
   *
   * The two catch different things. `snapshotPiiOffenders` watches KEY names — `studentPrivate`,
   * `realFirstName` — which is right for the legacy plaintext scheme. This one watches VALUES, because a
   * decrypted vault name arrives in `firstName` and is indistinguishable by key from a pseudonym. A class
   * snapshot is the single largest object this app persists, so it is the likeliest place for a merged
   * name to escape.
   */
  /**
   * Resolve the class id defensively, and scan the WHOLE record.
   *
   * `context.classId` was simply absent on class deletions, so the guard ran with a blank id — which
   * matches nothing, making it a silent no-op on the largest object this app persists. The call site now
   * sets it, and this falls back to the snapshot's own id so a future caller that forgets cannot quietly
   * disable the guard again.
   *
   * The whole record, not just `.data`: a student deletion also carries `name` (built from the student's
   * first and last name) and `studentResidue`, both outside `.data` and both able to hold a real name.
   */
  const guardClassId = record.context?.classId
    ?? (record.type === 'class' ? (record.data as { id?: string } | undefined)?.id : undefined)
    ?? '';
  assertNoVaultLeak(record, auth.currentUser?.uid ?? '', guardClassId, 'this deletion snapshot');

  const offenders = snapshotPiiOffenders(record as Parameters<typeof snapshotPiiOffenders>[0]);
  if (offenders.length) {
    throw new Error(
      `Refusing to store a deletion snapshot carrying owner-only PII for a class that promises none: `
      + `${offenders.join(', ')}. Sanitise with sanitiseClassSnapshot/sanitiseStudentSnapshot first.`,
    );
  }

  const newRecord = removeUndefined({
    ...record,
    ownerId: teacherId,
    timestamp: new Date().toISOString()
  });
  if (record.type === 'student' && record.observationErasure === 'pending') {
    if (!record.context?.classId) throw new Error('Class required for student deletion.');
    return savePendingObservationDeletionHistory(db, newRecord, record.context.classId);
  }
  const docRef = await addDoc(collection(db, 'deletionHistory'), newRecord);
  return docRef.id;
};

/** Fresh server metadata is required for restore and deletion retries. */
export const getDeletionRecordFromServer = async (recordId: string): Promise<DeletionRecord> => {
  const snapshot = await getDocFromServer(doc(db, 'deletionHistory', recordId));
  if (!snapshot.exists()) throw new Error('This History item no longer exists.');
  return { ...snapshot.data(), id: snapshot.id } as DeletionRecord;
};

export const getDeletionRecordForRestore = async (recordId: string): Promise<DeletionRecord> => {
  const record = await getDeletionRecordFromServer(recordId);
  assertRecordCanBeRestored(record);
  if (record.type === 'student') {
    const siblings = await getDeletionHistory(record.ownerId, true);
    // Another tab may hold a second deletion record for this same student.
    assertHistoryCanBeCleared(siblings.filter(r => r.type === 'student'
      && r.context?.classId === record.context?.classId && r.data?.id === record.data?.id));
  }
  return record;
};

export const markStudentObservationErasureComplete = async (recordId: string): Promise<void> => {
  await updateDoc(doc(db, 'deletionHistory', recordId), { observationErasure: 'complete' });
};

// Clear completed history only; pending items are durable recovery work.
export const clearDeletionHistory = async (teacherId: string): Promise<void> => {
  const q = query(
    collection(db, 'deletionHistory'),
    where('ownerId', '==', teacherId)
  );
  const snapshot = await getDocsFromServer(q);
  assertHistoryCanBeCleared(snapshot.docs.map(d => d.data() as DeletionRecord));
  const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
  await Promise.all(deletePromises);
};

// Delete specific history record
export const deleteHistoryRecord = async (recordId: string): Promise<void> => {
  assertRecordCanBeRestored(await getDeletionRecordFromServer(recordId));
  await deleteDoc(doc(db, 'deletionHistory', recordId));
};

// ==================== USER PROFILE ====================

// Update user profile (any subset of UserProfile fields).
// `removeUndefined` strips undefined values so Firestore accepts the update.
export const updateUserProfile = async (
  userId: string,
  updates: Partial<UserProfile>,
): Promise<void> => {
  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, removeUndefined(updates as unknown as Record<string, unknown>));
};

/**
 * Opt in/out of co-teacher email alerts for ONE class.
 *
 * A field-path update, not a whole-map write. Writing the whole `coTeacherAlerts`
 * object means spreading a possibly-stale local copy: opt into class A on a
 * laptop, then toggle class B on a phone whose cached profile predates A, and A's
 * entry is silently erased along with its alerts.
 */
export const setCoTeacherAlert = async (
  userId: string,
  classId: string,
  enabled: boolean,
): Promise<void> => {
  if (!userId || !classId) return;
  await updateDoc(doc(db, 'users', userId), { [`coTeacherAlerts.${classId}`]: enabled });
};

// recordUserActivity moved to ./userActivity (keeps it out of the landing chunk).
export { recordUserActivity } from './userActivity';

/**
 * Adjust the user's running storage-usage counter by deltaBytes (positive on
 * upload, negative on delete). Atomic via increment() so concurrent uploads
 * can't clobber each other. Best-effort: slight drift is fine here and a nightly
 * reconcile trues it up. Writes only storageBytesUsed on the user's own doc,
 * which firestore.rules permits (isOwner + no touched immutable field).
 */
export const bumpStorageUsage = async (uid: string, deltaBytes: number): Promise<void> => {
  if (!uid || !deltaBytes) return;
  const userRef = doc(db, 'users', uid);
  await updateDoc(userRef, { storageBytesUsed: increment(deltaBytes) });
};

/** Read the user's current storage-usage counter (bytes). 0 if unset. */
export const getStorageUsage = async (uid: string): Promise<number> => {
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? ((snap.data() as UserProfile).storageBytesUsed ?? 0) : 0;
};

/**
 * Rewrite the `attachments` array on a single stream post, atomically. Runs inside
 * a transaction: reads the class fresh, applies `transform` to the TARGET post's
 * current attachments, and writes the whole `stream` back in the same txn — so a
 * concurrent stream edit can't be clobbered by a stale read-modify-write. Used by
 * the Storage panel to remove a hosted file or replace it with an external link.
 * Sibling attachments (and attachments on other posts) are preserved untouched.
 * Throws if the class no longer exists; a no-op transform runs if the post is gone.
 */
export const updatePostAttachments = async (
  classId: string,
  postId: string,
  transform: (current: StreamAttachment[]) => StreamAttachment[],
): Promise<void> => {
  const classRef = doc(db, 'classes', classId);
  await runTransaction(db, async (txn) => {
    const snap = await txn.get(classRef);
    if (!snap.exists()) throw new Error('Class not found');
    const stream = ((snap.data() as ClassData).stream || []) as StreamPost[];
    const nextStream = stream.map(p =>
      p.id === postId ? { ...p, attachments: transform(p.attachments || []) } : p,
    );
    txn.update(classRef, { stream: nextStream });
  });
};

/**
 * Save this teacher's flashcard keys.
 *
 * A FIELD-PATH write, not authService.updateUserSettings, which reads the
 * whole `settings` map and writes it back: a theme change in another tab
 * between that read and that write is silently lost. Nothing here needs the
 * rest of the map, so nothing here should carry it.
 *
 * `settings` is already on the users-doc allowlist in firestore.rules, so this
 * needs no rules change — which is the reason the keys live under it rather
 * than at the top level.
 */
export const saveKinderKeys = async (uid: string, keys: KinderKeys): Promise<void> => {
  const clean = normalizeKinderKeys(keys);
  if (!clean) throw new Error('Those keys cannot be used.');
  await updateDoc(doc(db, 'users', uid), {
    // Written whole rather than per sub-key, so turning "almost" off actually
    // REMOVES it. A merge would leave the old character behind and the deck
    // would keep accepting a key the teacher just unbound.
    'settings.kinderKeys': clean,
  });
};

/**
 * Which gradebook tabs this teacher has hidden from the top bar.
 *
 * Written whole rather than with arrayUnion/arrayRemove, so un-hiding the last
 * tab actually clears the list instead of leaving an entry behind. Normalised
 * first, so a stale or unknown id from an older build cannot be persisted —
 * including 'grades', which must never be hidden.
 */
export const saveHiddenGradebookModes = async (uid: string, modes: string[]): Promise<void> => {
  await updateDoc(doc(db, 'users', uid), {
    'settings.hiddenGradebookModes': normalizeHiddenModes(modes),
  });
};

/** Gradebook display preferences. Normalised so a partial or stale shape cannot
 *  turn the gradebook into something the teacher did not choose. */
export const saveDisplayPrefs = async (uid: string, prefs: unknown): Promise<void> => {
  await updateDoc(doc(db, 'users', uid), {
    'settings.display': normalizeDisplayPrefs(prefs),
  });
};

/** This teacher's own names for gradebook tabs. Normalised so an unknown tab,
 *  a blank, or an over-long name can never be persisted. */
export const saveGradebookTabLabels = async (uid: string, labels: Record<string, string>): Promise<void> => {
  await updateDoc(doc(db, 'users', uid), {
    'settings.gradebookTabLabels': normalizeTabLabels(labels),
  });
};

/**
 * Kindergarten labels (JK/SK…), written TRANSACTIONALLY.
 *
 * These three read the class doc inside the transaction and rewrite from what
 * is actually stored, never from what a screen last rendered. That matters more
 * here than anywhere else in the app: assigning labels is a rapid-fire job — a
 * teacher tabs down twenty children picking JK or SK — and the whole-array
 * write these replaced was built from a prop that stays stale for a full round
 * trip after every click. On school Wi-Fi, pick four children quickly and the
 * later writes silently reverted the earlier ones, with no error to show it.
 *
 * The same staleness could drop a student who joined by code while the modal
 * was open, because the write was a snapshot of one teacher's local roster.
 *
 * Writing `students` here bypasses updateClass's PII strip, which is safe and
 * deliberate: the array comes from the doc, so it is already public-only, and
 * only `kinderLabelId` is touched.
 */
/**
 * RETURNS THE RESULTING ROSTER, and callers must apply it to their in-memory copy.
 *
 * Not decoration — it is the fix for a silent data-loss bug. This writes one field into
 * `students[]` straight on the class doc, outside the client's own `classData`. There is no
 * onSnapshot on the teacher side, so the in-memory roster never learned the field existed —
 * and two paths write the WHOLE students array back from that memory
 * (`handleUpdateAttendance` and `handleReleaseGrades` in TeacherApp). Taking attendance
 * therefore erased every JK/SK tag in the class from the database.
 *
 * So the authoritative post-write roster goes back to the caller. Any future out-of-band
 * roster writer must do the same, or it will be clobbered the same way.
 */
export const setStudentKinderLabel = async (
  classId: string,
  studentId: string,
  labelId: string,
): Promise<Student[] | null> => {
  const classRef = doc(db, 'classes', classId);
  let result: Student[] | null = null;
  await runTransaction(db, async (txn) => {
    const snap = await txn.get(classRef);
    if (!snap.exists()) throw new Error('Class not found');
    const students = ((snap.data() as ClassData).students || []) as Student[];
    const next = applyStudentKinderLabel(students, studentId, labelId);
    // Already there — don't rewrite the roster, but still hand back the server's copy so
    // the caller's memory converges even when this write was a no-op.
    result = next;
    if (next === students) return;
    txn.update(classRef, { students: next });
  });
  return result;
};

/**
 * One child's JK/SK, written TRANSACTIONALLY — for the same reason the label
 * writer above is.
 *
 * Tagging a room JK/SK is THE rapid-fire job in a kindergarten class: a teacher
 * goes down twenty nameplates tapping one of two buttons, faster than any of the
 * writes complete. A whole-array write built from a prop would send a roster that
 * is stale by several clicks, so the later writes would silently revert the
 * earlier ones with nothing on screen to show it — which is precisely what
 * happened with labels before they were moved into a transaction.
 *
 * Writing `students` bypasses updateClass's PII strip, which is safe and
 * deliberate for the same reason as the label writer: the array comes from the
 * doc, so it is already public-only, and only `kinderYear` is touched.
 */
/** Returns the resulting roster — see setStudentKinderLabel for why that matters. */
export const setStudentKinderYear = async (
  classId: string,
  studentId: string,
  year: KinderYear | '',
): Promise<Student[] | null> => {
  const classRef = doc(db, 'classes', classId);
  let result: Student[] | null = null;
  await runTransaction(db, async (txn) => {
    const snap = await txn.get(classRef);
    if (!snap.exists()) throw new Error('Class not found');
    const students = ((snap.data() as ClassData).students || []) as Student[];
    const next = applyStudentKinderYear(students, studentId, year);
    result = next;
    if (next === students) return;
    txn.update(classRef, { students: next });
  });
  return result;
};

/** Add a label, rejecting a duplicate or a fifth against the STORED list. */
export const addKinderLabel = async (
  classId: string,
  name: string,
): Promise<{ ok: boolean; reason?: 'cap' | 'duplicate' | 'blank' }> => {
  const classRef = doc(db, 'classes', classId);
  let outcome: { ok: boolean; reason?: 'cap' | 'duplicate' | 'blank' } = { ok: true };
  await runTransaction(db, async (txn) => {
    const snap = await txn.get(classRef);
    if (!snap.exists()) throw new Error('Class not found');
    const labels = normalizeKinderLabels((snap.data() as ClassData).kinderLabels);
    const r = appendKinderLabel(labels, name, newKinderId());
    outcome = { ok: r.ok, reason: r.reason };
    if (!r.ok) return;
    txn.update(classRef, { kinderLabels: r.labels });
  });
  return outcome;
};

/** Rename a label in place. Students point at the id, so nothing else moves. */
export const renameKinderLabel = async (
  classId: string,
  labelId: string,
  name: string,
): Promise<{ ok: boolean; reason?: 'duplicate' | 'blank' }> => {
  const clean = name.trim();
  if (!clean) return { ok: false, reason: 'blank' };
  const classRef = doc(db, 'classes', classId);
  let outcome: { ok: boolean; reason?: 'duplicate' | 'blank' } = { ok: true };
  await runTransaction(db, async (txn) => {
    const snap = await txn.get(classRef);
    if (!snap.exists()) throw new Error('Class not found');
    const labels = normalizeKinderLabels((snap.data() as ClassData).kinderLabels);
    if (labels.some(l => l.id !== labelId && l.name.toLowerCase() === clean.toLowerCase())) {
      outcome = { ok: false, reason: 'duplicate' };
      return;
    }
    txn.update(classRef, { kinderLabels: labels.map(l => (l.id === labelId ? { ...l, name: clean } : l)) });
  });
  return outcome;
};

/**
 * Delete a label and take it off every child, in ONE write.
 *
 * Separately would leave children pointing at a dead id: they read as
 * unlabelled everywhere, then come back mislabelled the moment that id is
 * reused.
 */
export const deleteKinderLabel = async (classId: string, labelId: string): Promise<void> => {
  const classRef = doc(db, 'classes', classId);
  await runTransaction(db, async (txn) => {
    const snap = await txn.get(classRef);
    if (!snap.exists()) throw new Error('Class not found');
    const data = snap.data() as ClassData;
    const r = removeKinderLabel(
      normalizeKinderLabels(data.kinderLabels),
      (data.students || []) as Student[],
      labelId,
    );
    txn.update(classRef, { kinderLabels: r.labels, students: r.students });
  });
};

/**
 * Remove one inline image (identified by its Storage object path) from a stream
 * post, atomically. Drops the matching entry from BOTH imageUrls and imagePaths
 * (index-aligned), inside a transaction so a concurrent stream edit isn't clobbered.
 * Used by the Storage panel to cull an inline image. No-op if the post or path is
 * already gone. Throws if the class no longer exists.
 */
export const removePostImagePath = async (
  classId: string,
  postId: string,
  path: string,
): Promise<void> => {
  const classRef = doc(db, 'classes', classId);
  await runTransaction(db, async (txn) => {
    const snap = await txn.get(classRef);
    if (!snap.exists()) throw new Error('Class not found');
    const stream = ((snap.data() as ClassData).stream || []) as StreamPost[];
    const nextStream = stream.map(p => {
      if (p.id !== postId) return p;
      const urls = p.imageUrls || [];
      const paths = p.imagePaths || [];
      const idx = paths.indexOf(path);
      if (idx === -1) return p; // already gone
      return {
        ...p,
        imageUrls: urls.filter((_, i) => i !== idx),
        imagePaths: paths.filter((_, i) => i !== idx),
      };
    });
    txn.update(classRef, { stream: nextStream });
  });
};

/** Live listener for user profile — updates whenever Firestore changes */
export const subscribeToUserProfile = (
  userId: string,
  callback: (profile: import('../types').UserProfile | null) => void
): (() => void) => {
  const userRef = doc(db, 'users', userId);
  return onSnapshot(userRef, (snap) => {
    callback(snap.exists() ? (snap.data() as import('../types').UserProfile) : null);
  });
};

// Delete all user data (classes, mark sets, deletion history, user profile)
export const deleteAllUserData = async (userId: string): Promise<void> => {
  // Delete all classes owned by this user — via the server cascade so their
  // subcollections (grades, gradeHistory, studentAuth PII, private roster PII)
  // are actually erased, not orphaned. This runs BEFORE the user profile is
  // deleted so the caller's token + role lookup are still valid for each call.
  const classesQuery = query(collection(db, 'classes'), where('ownerId', '==', userId));
  const classesSnapshot = await getDocs(classesQuery);
  const failedClassIds: string[] = [];
  for (const classDoc of classesSnapshot.docs) {
    const res = await callAuthedFunction('delete-class-data', { classId: classDoc.id });
    // Attempt every class (best effort), but track failures — a failed cascade
    // leaves the class doc ALIVE with its PII intact. That is NOT an orphan, so
    // the sweep can never reach it; the only correct thing is to surface it and
    // abort before wiping the profile, so the account still exists to retry
    // against (an erasure that silently left data behind would be worse).
    if (!res.ok) {
      const msg = await res.text().catch(() => '');
      console.error('deleteAllUserData: class cascade failed', classDoc.id, res.status, msg);
      failedClassIds.push(classDoc.id);
    }
  }
  if (failedClassIds.length > 0) {
    throw new Error(
      `Account erasure incomplete: ${failedClassIds.length} class(es) could not be deleted `
      + `(${failedClassIds.join(', ')}). No data was left silently — nothing else was removed. `
      + `Please retry; contact support if it persists.`,
    );
  }

  // Delete all mark sets owned by this user
  const markSetsQuery = query(collection(db, 'markSets'), where('ownerId', '==', userId));
  const markSetsSnapshot = await getDocs(markSetsQuery);
  for (const markSetDoc of markSetsSnapshot.docs) {
    await deleteDoc(markSetDoc.ref);
  }

  // Delete all deletion history for this user
  const historyQuery = query(collection(db, 'deletionHistory'), where('ownerId', '==', userId));
  const historySnapshot = await getDocs(historyQuery);
  for (const historyDoc of historySnapshot.docs) {
    await deleteDoc(historyDoc.ref);
  }

  // Disconnect Google Classroom: revoke the OAuth grant with Google and delete the
  // stored credential. Firestore does NOT delete a document's subcollections when
  // the document is deleted, so without this the refresh token at
  // users/{uid}/integrations/googleClassroom would be orphaned by the profile
  // delete below — a live Google grant with no account behind it. Best-effort: a
  // third-party API hiccup must not strand someone trying to erase their account
  // (the token expires on its own, and the user can also revoke it at
  // myaccount.google.com). Self-deletion only, so the caller's token IS this user.
  try {
    const { getAuth } = await import('firebase/auth');
    const idToken = await getAuth().currentUser?.getIdToken();
    if (idToken) {
      await fetch('/.netlify/functions/gclassroom-connection', {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${idToken}` },
      });
    }
  } catch (e) {
    console.error('deleteAllUserData: Google Classroom disconnect failed (continuing)', e);
  }

  /**
   * Delete the VAULT WRAPS, for the same reason as the Classroom token directly above: Firestore does
   * not delete a document's subcollections when the document is deleted.
   *
   * `users/{uid}/private/vaultKeys` holds the wrapped class keys and the KDF salt. They are useless
   * without the passphrase — which is the point of the design — but "useless to an attacker" is not the
   * standard. A teacher who asks us to erase their account is entitled to have us erase it, and a
   * key-material document outliving the account it belonged to is exactly the kind of orphan a privacy
   * review asks about. The encrypted rosters themselves go with their classes, via the server cascade at
   * the top of this function.
   *
   * Best-effort, like the Classroom disconnect: a failure here must not strand someone trying to erase
   * their account, and what is left behind opens nothing.
   */
  try {
    await deleteDoc(doc(db, 'users', userId, 'private', 'vaultKeys'));
  } catch (e) {
    console.error('deleteAllUserData: could not delete the vault keys document (continuing)', e);
  }

  // Delete the user profile document
  const userRef = doc(db, 'users', userId);
  await deleteDoc(userRef);
};

// Find a teacher by email for class transfer
export const findTeacherByEmail = async (email: string): Promise<{ id: string; displayName: string; email: string } | null> => {
  const usersQuery = query(collection(db, 'users'), where('email', '==', email.toLowerCase().trim()));
  const snapshot = await getDocs(usersQuery);

  if (snapshot.empty) {
    return null;
  }

  const userDoc = snapshot.docs[0];
  const userData = userDoc.data();

  // Only return if they're a teacher
  if (userData.role !== 'teacher') {
    return null;
  }

  return {
    id: userDoc.id,
    displayName: userData.displayName || userData.email,
    email: userData.email
  };
};

// Transfer class ownership to another teacher
export const transferClass = async (classId: string, newOwnerId: string): Promise<void> => {
  const classRef = doc(db, 'classes', classId);
  // Add the new owner to classMembers (alongside existing members) so the new
  // owner can read the class under the tightened classes/{classId} read rule.
  // We deliberately don't remove the old owner — they may need a brief tail of
  // read access while they finish exporting / archiving.
  // Read the current owner before overwriting it, so the handover can be recorded.
  const beforeTransfer = await getDoc(classRef);
  const outgoingOwner = beforeTransfer.exists()
    ? (beforeTransfer.data() as { ownerId?: string }).ownerId
    : undefined;
  await updateDoc(classRef, {
    ownerId: newOwnerId,
    classMembers: arrayUnion(newOwnerId),
    /**
     * RECORD WHO HANDED IT OVER, because the old owner stays in `classMembers` by design.
     *
     * That tail of read access is deliberate (see above) and nothing ever removes it. Once the vault
     * started reconciling `classMembers` against the roster, the old owner became a uid with portal
     * access and no student row — so the conversion banner reported "1 person can open this class but
     * we cannot match them to a student", forever, on every transferred class, with a Review button
     * that could never clear it.
     *
     * Kept as its own field rather than pruned from `classMembers`, so the tail of access survives
     * and both the banner and `unmappedOf` can exclude them by name.
     */
    ...(outgoingOwner && outgoingOwner !== newOwnerId
      ? { formerOwners: arrayUnion(outgoingOwner) }
      : {}),
    /**
     * Clear the removal-transfer marks.
     *
     * If this class arrived via an admin off-boarding, it still carries
     * `previousOwnerId` / `transferredTo` / `transferredAt` naming that handover.
     * Handing it on voluntarily makes those a description of an older, no-longer-
     * current state — and a later "reclaim classes" reading them would take this
     * class from the person receiving it now, who had nothing to do with the
     * original removal.
     */
    previousOwnerId: deleteField(),
    transferredTo: deleteField(),
    transferredAt: deleteField(),
    transferredBy: deleteField(),
  });
};

// Subscribe to real-time updates for a class document
// Returns an unsubscribe function to stop listening
export const subscribeToClass = (
  classId: string,
  onUpdate: (classData: ClassData) => void
): (() => void) => {
  const classRef = doc(db, 'classes', classId);
  // The `await hydrateStudentPrivate` below means two snapshots firing close
  // together (e.g. an auto-apply write followed immediately by another edit) can
  // resolve OUT OF ORDER — an older snapshot landing last would revert the newer
  // one (this is how a just-granted re-write silently disappeared). Stamp each
  // snapshot and drop any whose result is superseded before it finishes.
  let latestSeq = 0;
  return onSnapshot(classRef, async (snapshot) => {
    if (!snapshot.exists()) return;
    const mySeq = ++latestSeq;
    const cd = { ...snapshot.data(), id: snapshot.id } as ClassData;
    // Hydrate owner-only PII (getDoc is denied + falls back to {} for students).
    await hydrateStudentPrivate(cd);
    await hydrateSubmissionPrivate(cd);
    // Re-hydrate each test's answer key from the private doc — a raw snapshot has the
    // answers stripped off the class doc, so without this the teacher's live class
    // would lose them (results, answer-key editor, re-grade). Owner-only (students'
    // private read fails → no-op).
    await hydrateQuizAnswerKeys(cd);
    if (mySeq !== latestSeq) return; // a newer snapshot already superseded this one
    onUpdate(cd);
  });
};

// Nickname session management (Firebase Anonymous Auth + Firestore)
export interface NicknameSession {
  classId: string;
  className: string;
  classCode: string;
  studentId: string;
  nickname: string;
  isNicknameOnly: true;
}

export const getNicknameSessionFromFirestore = async (uid: string): Promise<NicknameSession | null> => {
  const sessionRef = doc(db, 'nicknameSessions', uid);
  const snap = await getDoc(sessionRef);
  if (!snap.exists()) return null;
  return snap.data() as NicknameSession;
};

export const deleteNicknameSession = async (uid: string): Promise<void> => {
  const sessionRef = doc(db, 'nicknameSessions', uid);
  await deleteDoc(sessionRef);
};

// ─── Curriculum Alignment ─────────────────────────────────────────────────────

/**
 * Set or clear one of a class's two curriculum slots.
 *
 * REFUSES to point both slots at the same course. `curriculumLink2` is for a split or combined
 * class — one room, two courses — and the same course in both shows every expectation twice in
 * the picker and in the Assessment Lab, with no way to tell the copies apart. Two "Junior Drama"
 * classes in production carry the same Arts document in both slots, so this is live data rather
 * than a hypothesis.
 *
 * Guarded HERE rather than only in the modal because four call sites write these fields
 * (TeacherApp, CurriculumAlignmentView and ClassSettingsModal twice). A check in one of them is
 * a check the other three do not have.
 *
 * TRANSACTIONAL, because the comparison needs the other slot's current value: read-then-write
 * outside a transaction would let two tabs each see an empty slot 2 and both fill it.
 *
 * The same DOCUMENT in both slots stays legal — a split class teaching ENG3U and ENG4U is one
 * English document and two real courses. See `sameCurriculumCourse`.
 */
export const setClassCurriculumLink = async (
  classId: string,
  link: CurriculumLink | null,
  isSecond = false
): Promise<void> => {
  const field = isSecond ? 'curriculumLink2' : 'curriculumLink';
  const otherField = isSecond ? 'curriculumLink' : 'curriculumLink2';
  const classRef = doc(db, 'classes', classId);

  if (link === null) {
    await updateDoc(classRef, { [field]: deleteField() });
    return;
  }

  await runTransaction(db, async (txn) => {
    const snap = await txn.get(classRef);
    if (!snap.exists()) throw new Error('Class not found');
    const other = (snap.data() as ClassData | undefined)?.[otherField as 'curriculumLink'] as CurriculumLink | undefined;
    if (sameCurriculumCourse(link, other)) throw new Error(DUPLICATE_LINK_MESSAGE);
    txn.update(classRef, { [field]: removeUndefined(link as unknown as Record<string, unknown>) });
  });
};

// Set (or clear) a class's grade OUTPUT scale — the DISPLAY layer only (internal
// grades stay numeric). Reverting to Percent CLEARS the field with deleteField()
// so a percent class stays byte-identical to one that never had a scale, rather
// than storing an empty/percent object. Direct updateDoc (not updateClass) so the
// deleteField() sentinel isn't corrupted by removeUndefined's object recursion.
export const setClassGradeScale = async (
  classId: string,
  scale: GradeScale | null,
): Promise<void> => {
  const classRef = doc(db, 'classes', classId);
  if (scale === null) {
    await updateDoc(classRef, { gradeScale: deleteField() });
  } else {
    await updateDoc(classRef, { gradeScale: removeUndefined(scale as unknown as Record<string, unknown>) });
  }
};

/**
 * Move a class onto a different grading framework, in ONE write.
 *
 * The mark set and the remapped assessments go together or not at all. Written
 * separately, a failure between them leaves a class pointing at a framework
 * whose categories none of its assessments belong to — and
 * `calculateAverage` returns null for every student in that state, with all
 * the marks still sitting in the document and nothing on screen to say why.
 *
 * `customMarkSet` is CLEARED, because it overrides categories entirely (see
 * activeMarkSet in TeacherApp): leaving it would make the teacher's change do
 * nothing visible at all.
 */
export const setClassMarkSet = async (
  classId: string,
  markSetId: string,
  mapping: Record<string, string>,
): Promise<Assessment[]> => {
  const classRef = doc(db, 'classes', classId);
  /**
   * Reads the SERVER's assessments inside the transaction and remaps those.
   *
   * It used to take the client's array and write it wholesale, which was wrong
   * twice over. It was a stale read-modify-write — a co-teacher's assessment
   * added since the last snapshot was erased, and a class edited from the
   * dashboard has no live subscription at all, so its copy can be as old as
   * the last full load. And the client's copy is HYDRATED: the owner-private
   * `aiGradingInstructions` are merged onto it in memory, so writing it
   * published them onto a document every classmate can read. Every other
   * assessment writer goes through `updateClass`, which strips them; this was
   * the one that did not.
   */
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(classRef);
    if (!snap.exists()) throw new Error('Class no longer exists');
    const current = (snap.data()?.assessments || []) as Assessment[];
    const next = remapAssessments(current, mapping);
    tx.update(classRef, {
      markSetId,
      assessments: stripAiInstructions(next).map(a => removeUndefined(a as unknown as Record<string, unknown>)),
      customMarkSet: deleteField(),
    });
    return next;
  });
};

// ─── Assessment Templates ─────────────────────────────────────────────────────

export const saveAssessmentTemplate = async (
  template: Omit<AssessmentTemplate, 'id'>
): Promise<string> => {
  const colRef = collection(db, 'assessmentTemplates');
  const docRef = await addDoc(colRef, removeUndefined(template as unknown as Record<string, unknown>));
  return docRef.id;
};

export const getAssessmentTemplates = async (ownerId: string): Promise<AssessmentTemplate[]> => {
  const q = query(
    collection(db, 'assessmentTemplates'),
    where('ownerId', '==', ownerId)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as AssessmentTemplate));
};

export const deleteAssessmentTemplate = async (templateId: string): Promise<void> => {
  await deleteDoc(doc(db, 'assessmentTemplates', templateId));
};

/** Patch an existing Library template (e.g. update title/content after the teacher
 *  edits a generation that was already auto-saved — so we update in place instead of
 *  creating a duplicate). Stamps updatedAt; strips undefined (Firestore rejects it). */
export const updateAssessmentTemplate = async (
  templateId: string,
  patch: Partial<Omit<AssessmentTemplate, 'id' | 'ownerId' | 'createdAt'>>,
): Promise<void> => {
  await updateDoc(
    doc(db, 'assessmentTemplates', templateId),
    removeUndefined({ ...patch, updatedAt: new Date().toISOString() } as Record<string, unknown>),
  );
};

// ─── Pathway Templates ────────────────────────────────────────────────────────

export const savePathwayTemplate = async (
  template: Omit<PathwayTemplate, 'id'>
): Promise<string> => {
  const colRef = collection(db, 'pathwayTemplates');
  const docRef = await addDoc(colRef, removeUndefined(template as unknown as Record<string, unknown>));
  return docRef.id;
};

export const getPathwayTemplates = async (ownerId: string): Promise<PathwayTemplate[]> => {
  const q = query(
    collection(db, 'pathwayTemplates'),
    where('ownerId', '==', ownerId)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as PathwayTemplate));
};

export const deletePathwayTemplate = async (templateId: string): Promise<void> => {
  await deleteDoc(doc(db, 'pathwayTemplates', templateId));
};

// ─── Community / Public Templates ─────────────────────────────────────────────

export const publishToCommunity = async (
  template: Omit<PublicTemplate, 'id'>
): Promise<string> => {
  const colRef = collection(db, 'publicTemplates');
  const docRef = await addDoc(colRef, removeUndefined(template as unknown as Record<string, unknown>));
  return docRef.id;
};

export const getPublicTemplates = async (filters?: {
  subject?: string;
  gradeLevel?: string;
  courseCode?: string;
  type?: string;
}): Promise<PublicTemplate[]> => {
  let q = query(collection(db, 'publicTemplates'));
  // Firestore compound queries require composite indexes; do client-side filtering
  // for the multi-filter case to avoid index requirements
  const snap = await getDocs(q);
  let results = snap.docs.map(d => ({ id: d.id, ...d.data() } as PublicTemplate));
  if (filters?.subject) results = results.filter(t => t.subject === filters.subject);
  if (filters?.gradeLevel) results = results.filter(t => t.gradeLevel === filters.gradeLevel);
  if (filters?.courseCode) results = results.filter(t =>
    t.courseCode?.toLowerCase().includes(filters.courseCode!.toLowerCase())
  );
  if (filters?.type) results = results.filter(t => t.type === filters.type);
  return results;
};

// Save a public template to the caller's personal assessment library.
// Creates a full independent copy — deleting the original does NOT affect the copy.
export const savePublicTemplateToLibrary = async (
  template: PublicTemplate,
  ownerId: string,
): Promise<string> => {
  const copy: Omit<AssessmentTemplate, 'id'> = {
    ownerId,
    title: template.title,
    categoryId: template.categoryId,
    outOf: template.outOf,
    weight: template.weight,
    type: template.type,
    description: template.description,
    rubric: template.rubric,
    curriculumExpectations: template.curriculumExpectations,
    // The jurisdiction travels with the expectations, or the recipient's export credits the wrong
    // authority. This object is an explicit allowlist, so a new field is dropped unless named.
    curriculumFilename: template.curriculumFilename,
    // Carry the real generated body + its kind so the saved copy is fully usable
    // (recall/post), not just a rubric/description husk as before.
    content: template.content || undefined,
    contentKind: template.contentKind
      ?? (template.contentType as AssessmentTemplate['contentKind'])
      ?? undefined,
    savedFromPublicId: template.id,
    createdAt: new Date().toISOString(),
  };
  const colRef = collection(db, 'assessmentTemplates');
  const docRef = await addDoc(colRef, removeUndefined(copy as unknown as Record<string, unknown>));
  // Increment the saves counter on the public template (best-effort)
  try {
    await updateDoc(doc(db, 'publicTemplates', template.id), {
      saves: (await getDoc(doc(db, 'publicTemplates', template.id))).data()!.saves + 1,
    });
  } catch { /* non-critical */ }
  return docRef.id;
};

export const ratePublicTemplate = async (
  templateId: string,
  uid: string,
  stars: number, // 1–5
  previousStars: number | null, // null if first-time rating
): Promise<void> => {
  const ref = doc(db, 'publicTemplates', templateId);
  const snap = await getDoc(ref);
  const data = snap.data()!;
  const currentSum: number = data.ratingSum ?? 0;
  const currentCount: number = data.ratingCount ?? 0;
  const newSum = currentSum - (previousStars ?? 0) + stars;
  const newCount = previousStars !== null ? currentCount : currentCount + 1;
  await updateDoc(ref, {
    ratingSum: newSum,
    ratingCount: newCount,
    [`ratedBy.${uid}`]: stars,
  });
};

export const incrementTemplateViews = async (templateId: string): Promise<void> => {
  const ref = doc(db, 'publicTemplates', templateId);
  const snap = await getDoc(ref);
  const currentViews: number = snap.data()?.views ?? 0;
  await updateDoc(ref, { views: currentViews + 1 });
};

export const unpublishTemplate = async (templateId: string): Promise<void> => {
  await deleteDoc(doc(db, 'publicTemplates', templateId));
};

// ─── Board & School Services (Phase 1) ───────────────────────────────────────

/** Generate a unique 6–8 char uppercase alphanumeric school code */
export const generateSchoolCode = (): string => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no I/O/0/1 (ambiguous)
  return Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
};

/** Detect SIS profile from country/province */
export const detectSisProfile = (country: string, province: string): SisProfile => {
  if (country === 'CA' && province === 'ON') return 'aspen_arc';
  return 'manual';
};

// --- Boards ---

export const getBoard = async (boardId: string): Promise<BoardDocument | null> => {
  const snap = await getDoc(doc(db, 'boards', boardId));
  return snap.exists() ? (snap.data() as BoardDocument) : null;
};

/**
 * Get the board keyed by this owner's uid, creating a minimal one if it doesn't
 * exist yet. A board admin elevated without a board doc previously left the
 * board views stuck on "Loading board…" forever — this backfills it on demand.
 */
export const ensureBoard = async (boardId: string, name: string): Promise<BoardDocument> => {
  const ref = doc(db, 'boards', boardId);
  const snap = await getDoc(ref);
  if (snap.exists()) return { id: boardId, ...snap.data() } as BoardDocument;
  const board = {
    name: name || 'My Board',
    createdAt: new Date().toISOString(),
    createdBy: boardId,
    schoolIds: [] as string[],
    country: 'CA',
    province: 'ON',
  };
  await setDoc(ref, board);
  return { id: boardId, ...board } as unknown as BoardDocument;
};

export const updateBoard = async (boardId: string, updates: Partial<BoardDocument>): Promise<void> => {
  await updateDoc(doc(db, 'boards', boardId), removeUndefined(updates as unknown as Record<string, unknown>));
};

// --- Schools ---

export const createSchool = async (data: Omit<SchoolDocument, 'id'>): Promise<string> => {
  // Inherit the parent board's subscriptionStatus so billing checks downstream work.
  // If the board doesn't exist yet, we create it below and the school will default
  // its own status at creation time (caller can override).
  let inheritedStatus: 'active' | 'past_due' | 'canceled' | undefined = data.subscriptionStatus;
  const boardRef = doc(db, 'boards', data.boardId);
  const boardSnap = await getDoc(boardRef);
  if (boardSnap.exists() && !inheritedStatus) {
    const boardData = boardSnap.data() as BoardDocument;
    inheritedStatus = boardData.subscriptionStatus;
  }

  const schoolDataWithStatus = inheritedStatus
    ? { ...data, subscriptionStatus: inheritedStatus }
    : data;

  const ref = await addDoc(collection(db, 'schools'), removeUndefined(schoolDataWithStatus as unknown as Record<string, unknown>));
  await updateDoc(doc(db, 'schools', ref.id), { id: ref.id });
  // Add schoolId to board.schoolIds — create board doc if it doesn't exist yet
  if (boardSnap.exists()) {
    await updateDoc(boardRef, { schoolIds: arrayUnion(ref.id) });
  } else {
    await setDoc(boardRef, {
      id: data.boardId,
      name: '',
      schoolIds: [ref.id],
      createdAt: new Date().toISOString(),
      createdBy: data.createdBy || '',
    });
  }
  return ref.id;
};

export const getSchool = async (schoolId: string): Promise<SchoolDocument | null> => {
  const snap = await getDoc(doc(db, 'schools', schoolId));
  return snap.exists() ? (snap.data() as SchoolDocument) : null;
};

export const getSchoolsForBoard = async (boardId: string): Promise<SchoolDocument[]> => {
  const q = query(collection(db, 'schools'), where('boardId', '==', boardId));
  const snap = await getDocs(q);
  return snap.docs.map(d => d.data() as SchoolDocument);
};

/**
 * Every school on the platform, for the owner's "view as school admin" picker.
 *
 * `getSchoolsForBoard` filters on boardId == the caller's uid, which is why the
 * picker can't be built from it: the schools the owner most needs to inspect
 * (an enterprise customer's, e.g. OEO's) belong to somebody else's board and
 * never appear. Firestore rules permit the list for any board_admin; the CALLER
 * must still gate this behind isPlatformOwner, since a customer board admin
 * seeing every school on the platform would be a tenancy leak.
 *
 * Reads the doc id rather than trusting a stored `id` field — school docs
 * written before `updateDoc(..., { id })` landed don't carry one, and a blank id
 * would make the picker silently switch context to nowhere.
 */
export const getAllSchools = async (): Promise<SchoolDocument[]> => {
  const snap = await getDocs(collection(db, 'schools'));
  return snap.docs.map(d => ({ ...(d.data() as SchoolDocument), id: d.id }));
};

export const updateSchool = async (schoolId: string, updates: Partial<SchoolDocument>): Promise<void> => {
  await updateDoc(doc(db, 'schools', schoolId), removeUndefined(updates as unknown as Record<string, unknown>));
};

/**
 * Delete a school document and remove it from the parent board's schoolIds.
 * Subcollections (classes, students, etc.) are intentionally left in place;
 * this is a "test cleanup" operation, not a full cascade. Production-grade
 * cascade deletion is a separate task that needs a Cloud Function.
 */
export const deleteSchool = async (schoolId: string): Promise<void> => {
  const schoolRef = doc(db, 'schools', schoolId);
  const schoolSnap = await getDoc(schoolRef);
  if (!schoolSnap.exists()) return;
  const { boardId } = schoolSnap.data() as SchoolDocument;
  if (boardId) {
    const boardRef = doc(db, 'boards', boardId);
    try {
      await updateDoc(boardRef, { schoolIds: arrayRemove(schoolId) });
    } catch {
      // Board doc may not exist or rules may block — non-fatal for the school delete itself
    }
  }
  await deleteDoc(schoolRef);
};

/** Look up a school by its join code (case-insensitive) */
export const getSchoolByCode = async (code: string): Promise<SchoolDocument | null> => {
  const q = query(collection(db, 'schools'), where('schoolCode', '==', code.toUpperCase().trim()));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return snap.docs[0].data() as SchoolDocument;
};

// --- School Staff & Teachers ---

export const getSchoolTeacher = async (schoolId: string, uid: string): Promise<SchoolTeacherRecord | null> => {
  const snap = await getDoc(doc(db, 'schools', schoolId, 'teachers', uid));
  return snap.exists() ? (snap.data() as SchoolTeacherRecord) : null;
};

export const getSchoolTeachers = async (schoolId: string): Promise<SchoolTeacherRecord[]> => {
  const snap = await getDocs(collection(db, 'schools', schoolId, 'teachers'));
  return snap.docs.map(d => d.data() as SchoolTeacherRecord);
};

/** Live subscription to the school's teacher records. Use this when
 *  a UI surface needs to react to displayName / homeDepartment edits
 *  from another tab without a manual refresh — e.g. the manifest
 *  picker in the course catalog re-sorting on rename. Returns an
 *  unsubscribe function. */
export const subscribeToSchoolTeachers = (
  schoolId: string,
  callback: (teachers: SchoolTeacherRecord[]) => void,
): (() => void) => {
  return onSnapshot(collection(db, 'schools', schoolId, 'teachers'), snap => {
    callback(snap.docs.map(d => d.data() as SchoolTeacherRecord));
  });
};

// ─── School Groups (departments + custom cohorts) ───────────────────────────

export const getSchoolGroups = async (schoolId: string): Promise<import('../types').SchoolGroup[]> => {
  const snap = await getDocs(collection(db, 'schools', schoolId, 'groups'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as import('../types').SchoolGroup));
};

export const createSchoolGroup = async (
  schoolId: string,
  data: Omit<import('../types').SchoolGroup, 'id' | 'schoolId' | 'createdAt'>,
): Promise<string> => {
  const ref = await addDoc(collection(db, 'schools', schoolId, 'groups'), removeUndefined({
    ...data,
    schoolId,
    createdAt: new Date().toISOString(),
  }));
  return ref.id;
};

export const deleteSchoolGroup = async (schoolId: string, groupId: string): Promise<void> => {
  await deleteDoc(doc(db, 'schools', schoolId, 'groups', groupId));
};

/** Add a member uid to a group (idempotent — arrayUnion). */
export const addMemberToSchoolGroup = async (
  schoolId: string,
  groupId: string,
  memberUid: string,
): Promise<void> => {
  await updateDoc(doc(db, 'schools', schoolId, 'groups', groupId), {
    memberUids: arrayUnion(memberUid),
  });
};

/** Remove a member uid from a group. */
export const removeMemberFromSchoolGroup = async (
  schoolId: string,
  groupId: string,
  memberUid: string,
): Promise<void> => {
  await updateDoc(doc(db, 'schools', schoolId, 'groups', groupId), {
    memberUids: arrayRemove(memberUid),
  });
};

export const getSchoolStaff = async (schoolId: string): Promise<SchoolStaffRecord[]> => {
  const snap = await getDocs(collection(db, 'schools', schoolId, 'staff'));
  return snap.docs.map(d => d.data() as SchoolStaffRecord);
};

export const addTeacherToSchool = async (
  schoolId: string,
  uid: string,
  record: Omit<SchoolTeacherRecord, 'uid'>,
): Promise<void> => {
  await setDoc(doc(db, 'schools', schoolId, 'teachers', uid), { ...record, uid });
};

export const updateTeacherSchoolRecord = async (
  schoolId: string,
  uid: string,
  updates: Partial<SchoolTeacherRecord>,
): Promise<void> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  await updateDoc(doc(db, 'schools', schoolId, 'teachers', uid), updates as any);
};

// (addStaffToSchool removed — staff docs are created ONLY server-side via
//  accept-invitation / remove-school-member; the client can no longer mint them.)

export const getStaffRecord = async (schoolId: string, uid: string): Promise<SchoolStaffRecord | null> => {
  const snap = await getDoc(doc(db, 'schools', schoolId, 'staff', uid));
  return snap.exists() ? (snap.data() as SchoolStaffRecord) : null;
};

export const updateStaffRecord = async (
  schoolId: string,
  uid: string,
  updates: Partial<SchoolStaffRecord>,
): Promise<void> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  await updateDoc(doc(db, 'schools', schoolId, 'staff', uid), removeUndefined(updates as any));
};

export const updateStaffPermissions = async (
  schoolId: string,
  uid: string,
  delegatedPermissions: string[],
): Promise<void> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  await updateDoc(doc(db, 'schools', schoolId, 'staff', uid), { delegatedPermissions } as any);
};

// --- User School Associations ---

/**
 * Adds a school association + schoolRoles entry on the user's OWN doc. Since schoolRoles
 * is now client-immutable EXCEPT for board_admin (firestore.rules carve-out), this only
 * works for the board_admin school-creation path (CreateSchoolModal). Do NOT reuse it to
 * seat teachers/staff — those are granted server-side (join-school-by-code,
 * accept-invitation). All other membership grants/revocations go through Netlify functions.
 */
export const addSchoolAssociation = async (uid: string, association: SchoolAssociation): Promise<void> => {
  await updateDoc(doc(db, 'users', uid), {
    schoolAssociations: arrayUnion(association),
    // Parallel map field that firestore.rules reads for membership checks.
    [`schoolRoles.${association.schoolId}`]: association.role,
  });
};

// (removeSchoolAssociation removed — clearing another user's schoolRoles/schoolAssociations
//  is impossible from the client (users update is isOwner-only) and is now handled
//  server-side by remove-school-member / deny-teacher-join / nightly-cleanup.)

export const getSchoolsForUser = async (uid: string): Promise<SchoolDocument[]> => {
  const snap = await getDoc(doc(db, 'users', uid));
  if (!snap.exists()) return [];
  const associations: SchoolAssociation[] = snap.data().schoolAssociations ?? [];
  if (associations.length === 0) return [];
  const schools = await Promise.all(associations.map(a => getSchool(a.schoolId)));
  return schools.filter(Boolean) as SchoolDocument[];
};

// --- Invitations ---

export const createInvitation = async (data: Omit<InvitationDocument, 'id'>): Promise<string> => {
  // Use the (random, unguessable) token as the doc ID so AcceptInvitePage can
  // do a single-doc get() against firestore.rules' "allow get" rule, instead of
  // a list query that would leak every invitation's email + token.
  const id = data.token;
  await setDoc(doc(db, 'invitations', id), {
    ...removeUndefined(data as unknown as Record<string, unknown>),
    id,
  });
  return id;
};

export const getInvitationByToken = async (token: string): Promise<InvitationDocument | null> => {
  // Fetch directly by token-as-doc-id. firestore.rules denies `list` on this
  // collection so a where-query would fail.
  const snap = await getDoc(doc(db, 'invitations', token));
  if (!snap.exists()) return null;
  return snap.data() as InvitationDocument;
};

/** Pending invitations this user personally sent. Used by the
 *  "My pending invites" strip atop the Teachers / Staff panels so
 *  admins can revoke invites they sent by mistake without having to
 *  see the rest of the school's outstanding invitations. */
export const getPendingInvitationsByInviter = async (uid: string): Promise<InvitationDocument[]> => {
  const q = query(
    collection(db, 'invitations'),
    where('invitedBy', '==', uid),
    where('status', '==', 'pending'),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => d.data() as InvitationDocument);
};

/** Revoke a pending invitation. Marks status=revoked so the accept
 *  page shows the expired/revoked message instead of letting the
 *  recipient through. Bidirectional with status: the accept page
 *  filters on `pending` so anything else dead-ends. */
export const revokeInvitation = async (id: string): Promise<void> => {
  await updateDoc(doc(db, 'invitations', id), { status: 'revoked' });
};

// (acceptInvitation removed — invitation acceptance is handled ENTIRELY server-side by
//  the accept-invitation Netlify function, which writes the staff/teacher record + the
//  schoolRoles/schoolAssociations via the Admin SDK. Those fields are now client-immutable,
//  so acceptance can no longer run client-side.)

// (requestJoinSchoolByCode removed — replaced by the joinSchoolByCode() wrapper around the
//  join-school-by-code function, which writes the pending record + a no-access 'pending'
//  schoolRoles sentinel server-side.)

/** Approve a pending teacher join request */
/** Stub uids come from CSV import for teachers who haven't signed up yet
 *  ({@link addTeacherToSchool} is called with `stub_<email-slug>_<rand>`).
 *  Skipping notification + association reads/writes for these avoids
 *  failures against non-existent users/{uid} docs. When the real teacher
 *  signs up, the reconciliation step (Tier 3 publish-path work) will
 *  match them by email. */
// Stub-uid prefixes: CSV-imported teachers awaiting sign-up
// ('stub_'), and guest placeholder records the admin spawned for
// capacity planning ('guest_'). Neither has a real Firebase Auth
// user, so we skip the user-profile / association cleanup steps
// for them (those steps would 404 / permission-deny otherwise).
const isStubTeacherUid = (uid: string) => uid.startsWith('stub_') || uid.startsWith('guest_');

/** Resolve a pending join-by-code request → active teacher (server-side: flips the
 *  joiner's schoolRoles 'pending' → 'teacher', which is client-immutable). */
export const approveTeacherJoin = async (schoolId: string, uid: string): Promise<void> => {
  const res = await callAuthedFunction('approve-teacher-join', { schoolId, targetUid: uid });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Could not approve the request.');
};

/** Deny a pending join / remove a pending teacher (server-side: clears their
 *  schoolRoles + schoolAssociations, which the old client version could never do). */
export const denyTeacherJoin = async (schoolId: string, uid: string): Promise<void> => {
  const res = await callAuthedFunction('deny-teacher-join', { schoolId, targetUid: uid });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Could not deny the request.');
};

/**
 * Off-board an active teacher from a school (server-side). Clears schoolRoles +
 * schoolAssociations, strips them from class membership, keeps their personal
 * account. Routed through the same remove-school-member function that handles
 * staff — it detects a teacher-only target and gates on manage_teachers.
 *
 * `successorUid` names the colleague who takes over their school classes. Without
 * one those classes are ORPHANED — kept on file with no owner, so nobody can mark
 * them, post to them or take attendance until an admin reassigns each by hand.
 * That is the right default for a placeholder or a mistaken add, and the wrong one
 * for a teacher leaving mid-semester, which is why the caller is asked.
 *
 * The server verifies the successor is an active teacher at this school and
 * refuses the whole removal if not — a removal that half-applied is worse than
 * one that didn't happen.
 */
export const removeTeacherFromSchool = async (
  schoolId: string,
  teacherUid: string,
  successorUid?: string,
): Promise<{ classesTransferred: number; classesOrphaned: number }> => {
  const res = await callAuthedFunction('remove-school-member', {
    schoolId, targetUid: teacherUid, ...(successorUid ? { successorUid } : {}),
  });
  const body = await res.json().catch(() => ({} as Record<string, unknown>));
  if (!res.ok) throw new Error((body as { error?: string }).error || 'Could not remove the teacher.');
  return {
    classesTransferred: Number((body as { classesTransferred?: number }).classesTransferred ?? 0),
    classesOrphaned: Number((body as { classesOrphaned?: number }).classesOrphaned ?? 0),
  };
};

/**
 * Give a restored teacher back the classes that were handed to a colleague when
 * they were removed.
 *
 * Restoring someone does NOT undo the transfer, on purpose: their classes have
 * been taught by somebody else in the meantime, and pulling a live class out from
 * under whoever has been marking it is worse than leaving it. This is the
 * separate, deliberate second step, taken by an admin who knows what happened in
 * between.
 */
export const reclaimTeacherClasses = async (
  schoolId: string,
  teacherUid: string,
): Promise<{ reclaimed: number; skipped: number; classes: Array<{ id: string; name: string }> }> => {
  const res = await callAuthedFunction('reclaim-teacher-classes', { schoolId, teacherUid });
  const body = await res.json().catch(() => ({} as Record<string, unknown>));
  if (!res.ok) throw new Error((body as { error?: string }).error || 'Could not reclaim the classes.');
  return {
    reclaimed: Number((body as { reclaimed?: number }).reclaimed ?? 0),
    skipped: Number((body as { skipped?: number }).skipped ?? 0),
    classes: ((body as { classes?: Array<{ id: string; name: string }> }).classes) || [],
  };
};

/**
 * Set which course a student is earning credit in, inside a combined class.
 *
 * SERVER operation. A school admin can technically update a class document, but
 * that rule allows changing any field including grades, and it counts any active
 * office_staff member whatever has been delegated to them. This narrows the
 * operation to one student's course code, validated against the courses the
 * class actually runs, and audits it.
 *
 * Teachers cannot call it — including the class owner. A course code is a
 * credit-granting decision and a school record.
 */
export const setStudentCourseCode = async (
  classId: string,
  studentId: string,
  courseCode: string,
): Promise<void> => {
  const res = await callAuthedFunction('set-student-course-code', { classId, studentId, courseCode });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Could not change the course code.');
};

/**
 * Undo a removal, within the 30-day recovery window.
 *
 * SERVER operation for the same reason removal is: restoring the school in
 * users/{uid}.schoolRoles is impossible from a browser (the users update rule is
 * isOwner-only), and that field is what every client rule keys school access
 * off. A client-side restore would put someone back in the lists while leaving
 * them locked out of everything.
 */
export const restoreSchoolMember = async (schoolId: string, targetUid: string): Promise<{
  classesReturned: number; coTeachRestored: number; classesSkipped: number;
}> => {
  const res = await callAuthedFunction('restore-school-member', { schoolId, targetUid });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || 'Could not restore that person.');
  return {
    classesReturned: body.classesReturned ?? 0,
    coTeachRestored: body.coTeachRestored ?? 0,
    classesSkipped: body.classesSkipped ?? 0,
  };
};

/**
 * Everyone currently removed from a school and still inside the recovery window.
 *
 * `teachersOnly` narrows the query to non-staff removals. It exists because the
 * rule is scoped the same way: an office-staffer with manage_teachers may undo
 * a TEACHER removal but not a principal's, so they may not read one either.
 * Firestore evaluates rules per document even inside a list, so an unfiltered
 * query from that caller fails ENTIRELY the moment one staff record exists —
 * the panel would go from complete to empty with a permission error, which
 * reads as "nobody was removed".
 */
export const getRemovedMembers = async (
  schoolId: string,
  opts?: { teachersOnly?: boolean },
): Promise<RemovedMember[]> => {
  const base = collection(db, 'schools', schoolId, 'removedMembers');
  const snap = await getDocs(
    opts?.teachersOnly ? query(base, where('wasStaff', '==', false)) : base,
  );
  return snap.docs
    .map(d => ({ ...(d.data() as RemovedMember), uid: d.id }))
    .sort((a, b) => (b.removedAt || '').localeCompare(a.removedAt || ''));
};

/**
 * Change a member's role at ONE school (promote/demote between teacher / office_staff /
 * vp / principal). SERVER operation: the authoritative role in users/{uid}.schoolRoles is
 * isOwner-only from a client. The change-school-role function moves the staff/teacher
 * records safely (never arming the retention purge or orphaning classes), keeps
 * schoolRoles + schoolAssociations in sync, and enforces the hierarchy guards.
 */
export const changeSchoolRole = async (
  schoolId: string,
  targetUid: string,
  newRole: 'principal' | 'vp' | 'office_staff' | 'teacher',
): Promise<void> => {
  const res = await callAuthedFunction('change-school-role', { schoolId, targetUid, newRole });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Could not change the role.');
};

/**
 * Change what an ACCOUNT IS — teacher or student — for someone in your school.
 *
 * Not the same as changeSchoolRole above, which sets a role WITHIN a school
 * (principal / vp / office_staff / teacher). This decides which app they sign in
 * to, and exists for staff who joined a class with their board email to see the
 * student view, were created as students, and then sat on teacher seats still
 * flagged `student`.
 *
 * Server-side it shares one guard with the platform owner's route, so the two
 * cannot drift on who may do what. Class enrolments are deliberately KEPT.
 */
export const changeAccountRole = async (
  schoolId: string,
  targetUid: string,
  role: 'teacher' | 'student',
): Promise<void> => {
  const res = await callAuthedFunction('change-account-role', { schoolId, targetUid, role });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Could not change that account’s role.');
};

/** Teacher self-request to join a school by its code (server-side: writes the pending
 *  teacher record + a 'pending' schoolRoles sentinel that grants NO access until an
 *  admin approves). Returns the resolved school. */
export const joinSchoolByCode = async (schoolCode: string): Promise<{ schoolId: string; schoolName: string; already?: boolean }> => {
  const res = await callAuthedFunction('join-school-by-code', { schoolCode });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || 'Could not join the school.');
  // NOTE: a NEW join writes a PENDING request (schoolRoles:'pending', no data access
  // until an admin approves) — callers must message "request sent", not "joined".
  // `already` = the caller is already a teacher/associated with the school.
  return { schoolId: body.schoolId, schoolName: body.schoolName || '', already: body.already === true };
};

/**
 * Deactivate a school STAFF member (principal / VP / office staff), off-boarding them
 * from THIS school only — school access + school-linked classes are removed, but their
 * LUC account and personal classes are untouched, so they continue as an independent user.
 *
 * This is a SERVER operation: the key step — clearing the target's users/{uid}.schoolRoles,
 * which every client rule keys school access off — is impossible from an admin's browser
 * (the users update rule is isOwner-only). The remove-school-member function does it with
 * the Admin SDK, plus the class/teacher/notify/audit cleanup and the authorization +
 * hierarchy guards (can't remove yourself; a VP can't remove a principal; can't strand the
 * school's last principal).
 */
export const removeStaffFromSchool = async (schoolId: string, uid: string): Promise<void> => {
  const { getAuth } = await import('firebase/auth');
  const currentUser = getAuth().currentUser;
  if (!currentUser) throw new Error('You must be signed in.');
  const idToken = await currentUser.getIdToken();
  const res = await fetch('/.netlify/functions/remove-school-member', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
    body: JSON.stringify({ schoolId, targetUid: uid }),
  });
  if (!res.ok) {
    const msg = await res.json().catch(() => ({ error: 'Could not remove the staff member.' }));
    throw new Error(msg.error || 'Could not remove the staff member.');
  }
};

// ─── Phase 3: School Student Directory ───────────────────────────────────────

/**
 * Everyone who might OWN a class at this school — active teachers AND active
 * admins.
 *
 * A principal or VP teaches like anyone else, but holds a staff record rather
 * than a teacher record. Every admin panel that fanned out over the teacher list
 * alone therefore had a blind spot exactly the size of "classes owned by the
 * people running the school": their report-card submissions produced no rows,
 * their attendance showed nothing, and their classes sat unassigned in the
 * Classes tab. At a school where the admins do most of the teaching — which is
 * every small independent school — that isn't an edge case, it's most of the
 * data.
 *
 * `isAdmin` is carried so callers can label them rather than silently presenting
 * a principal as a teacher.
 */
export const getSchoolClassOwners = async (
  schoolId: string,
): Promise<Array<{ uid: string; displayName: string; email: string; isAdmin: boolean }>> => {
  // A failure in ONE source degrades (an admin-only school still lists its
  // admins); a failure in BOTH throws rather than rendering an empty panel that
  // looks like a school with no staff. Silent missing data is the trap.
  const [teachersR, staffR] = await Promise.allSettled([
    getSchoolTeachers(schoolId),
    getSchoolStaff(schoolId),
  ]);
  if (teachersR.status === 'rejected' && staffR.status === 'rejected') {
    throw teachersR.reason instanceof Error ? teachersR.reason : new Error('Could not load school staff.');
  }
  const teachers = teachersR.status === 'fulfilled' ? teachersR.value : [];
  const staff = staffR.status === 'fulfilled' ? staffR.value : [];
  const out = new Map<string, { uid: string; displayName: string; email: string; isAdmin: boolean }>();
  teachers.filter(t => t.status === 'active').forEach(t => out.set(t.uid, {
    uid: t.uid, displayName: t.displayName || t.email || t.uid, email: t.email || '', isAdmin: false,
  }));
  // Admins second: someone holding both keeps their teacher entry, which is the
  // more specific of the two for a class-ownership heading.
  staff.filter(m => m.status === 'active').forEach(m => {
    if (!out.has(m.uid)) {
      out.set(m.uid, {
        uid: m.uid, displayName: m.displayName || m.email || m.uid, email: m.email || '', isAdmin: true,
      });
    }
  });
  return [...out.values()];
};

export const getSchoolStudents = async (schoolId: string): Promise<SchoolStudent[]> => {
  const snap = await getDocs(collection(db, 'schools', schoolId, 'students'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as SchoolStudent));
};

export const subscribeToSchoolStudents = (
  schoolId: string,
  callback: (students: SchoolStudent[]) => void,
  onError?: (e: unknown) => void,
): (() => void) => {
  return onSnapshot(collection(db, 'schools', schoolId, 'students'), snap => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() } as SchoolStudent)));
  }, err => { onError?.(err); });
};

export const addSchoolStudent = async (
  schoolId: string,
  data: Omit<SchoolStudent, 'id' | 'schoolId' | 'createdAt' | 'updatedAt'>,
): Promise<SchoolStudent> => {
  const now = new Date().toISOString();
  const payload = removeUndefined({ ...data, schoolId, createdAt: now, updatedAt: now });
  const ref = await addDoc(collection(db, 'schools', schoolId, 'students'), payload);
  return { id: ref.id, ...payload } as SchoolStudent;
};

export const updateSchoolStudent = async (
  schoolId: string,
  studentId: string,
  updates: Partial<SchoolStudent>,
): Promise<void> => {
  const ref = doc(db, 'schools', schoolId, 'students', studentId);
  await updateDoc(ref, removeUndefined({ ...updates, updatedAt: new Date().toISOString() }));
};

/**
 * Archive (soft-withdraw) a directory student — a mid-year leave (moved,
 * transferred out) that keeps every record. Sets archivedAt + reason; the student
 * drops out of active directory listings but courseHistory, earnedCredits and mark
 * snapshots are untouched. Distinct from graduation and from soft-delete.
 */
export const archiveSchoolStudent = async (
  schoolId: string,
  studentId: string,
  opts?: { reason?: string; by?: string },
): Promise<void> => {
  await updateDoc(doc(db, 'schools', schoolId, 'students', studentId), removeUndefined({
    archivedAt: new Date().toISOString(),
    archiveReason: opts?.reason,
    archivedBy: opts?.by,
    updatedAt: new Date().toISOString(),
  }));
};

/** Reinstate an archived directory student — clears the archive flags. */
export const unarchiveSchoolStudent = async (schoolId: string, studentId: string): Promise<void> => {
  const { deleteField } = await import('firebase/firestore');
  await updateDoc(doc(db, 'schools', schoolId, 'students', studentId), {
    archivedAt: deleteField(),
    archiveReason: deleteField(),
    archivedBy: deleteField(),
    updatedAt: new Date().toISOString(),
  });
};

/**
 * Soft-delete a directory student. OnSIS must never lose an active record, so
 * this NO LONGER hard-deletes — it marks the student deleted, hides them, and
 * schedules a permanent purge 365 days out. All records (courseHistory,
 * earnedCredits, mark snapshots) are preserved until then and can be restored.
 * A true hard delete happens only after dataRetentionExpiry, via an owner tool.
 */
export const deleteSchoolStudent = async (
  schoolId: string,
  studentId: string,
  opts?: { by?: string },
): Promise<void> => {
  const now = new Date();
  const expiry = new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000);
  await updateDoc(doc(db, 'schools', schoolId, 'students', studentId), removeUndefined({
    deletedAt: now.toISOString(),
    deletedBy: opts?.by,
    dataRetentionExpiry: expiry.toISOString(),
    // Also archive so existing archivedAt-based directory filters hide it immediately.
    archivedAt: now.toISOString(),
    updatedAt: now.toISOString(),
  }));
};

// ─── Phase 3: Student Identity Matching ──────────────────────────────────────

/**
 * Given the teacher's UID and a student's email/studentNumber,
 * looks up the teacher's school associations and queries each school's
 * student directory for a match. Returns { schoolId, schoolStudentId } or null.
 * Intended to be called in the background after a student joins a class.
 */
export const findSchoolStudentMatch = async (
  teacherUid: string,
  email: string,
  studentNumber: string,
): Promise<{ schoolId: string; schoolStudentId: string } | null> => {
  const userSnap = await getDoc(doc(db, 'users', teacherUid));
  if (!userSnap.exists()) return null;

  const associations: SchoolAssociation[] = userSnap.data().schoolAssociations ?? [];
  if (associations.length === 0) return null;

  for (const assoc of associations) {
    const studentsRef = collection(db, 'schools', assoc.schoolId, 'students');

    // Try to match by email first (most reliable)
    if (email.trim()) {
      const emailQ = query(studentsRef, where('email', '==', email.trim().toLowerCase()));
      const emailSnap = await getDocs(emailQ);
      if (!emailSnap.empty) {
        return { schoolId: assoc.schoolId, schoolStudentId: emailSnap.docs[0].id };
      }
    }

    // Fall back to student number
    if (studentNumber.trim()) {
      const numQ = query(studentsRef, where('studentNumber', '==', studentNumber.trim()));
      const numSnap = await getDocs(numQ);
      if (!numSnap.empty) {
        return { schoolId: assoc.schoolId, schoolStudentId: numSnap.docs[0].id };
      }
    }
  }

  return null;
};

// ─── Phase 3: Period / Bell Schedule ─────────────────────────────────────────

export const updateSchoolPeriods = async (schoolId: string, periods: SchoolPeriod[]): Promise<void> => {
  await updateDoc(doc(db, 'schools', schoolId), { periods });
};

// ─── Phase 3: Reporting Periods ───────────────────────────────────────────────

export const createReportingPeriod = async (
  schoolId: string,
  data: Omit<ReportingPeriod, 'id' | 'schoolId' | 'createdAt'>,
): Promise<ReportingPeriod> => {
  const now = new Date().toISOString();
  const payload = { ...data, schoolId, createdAt: now, status: data.status ?? 'active' };
  const ref = await addDoc(collection(db, 'schools', schoolId, 'reportingPeriods'), payload);
  return { id: ref.id, ...payload } as ReportingPeriod;
};

/** Set the term + terminal-period flags used by the academic ledger to decide a
 *  course's FINAL mark. Admin-gated by the reportingPeriods rule. */
export const updateReportingPeriodMeta = async (
  schoolId: string,
  periodId: string,
  updates: { term?: 'S1' | 'S2' | 'FY'; isFinalForTerm?: boolean },
): Promise<void> => {
  await updateDoc(
    doc(db, 'schools', schoolId, 'reportingPeriods', periodId),
    removeUndefined(updates as Record<string, unknown>),
  );
};

export const getReportingPeriods = async (schoolId: string): Promise<ReportingPeriod[]> => {
  const snap = await getDocs(collection(db, 'schools', schoolId, 'reportingPeriods'));
  const periods = snap.docs.map(d => ({ id: d.id, ...d.data() } as ReportingPeriod));
  return periods.sort((a, b) => b.deadlineDate.localeCompare(a.deadlineDate));
};

export const getActiveReportingPeriod = async (schoolId: string): Promise<ReportingPeriod | null> => {
  const q = query(
    collection(db, 'schools', schoolId, 'reportingPeriods'),
    where('status', '==', 'active'),
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  // Return the one with the soonest deadline
  const periods = snap.docs.map(d => ({ id: d.id, ...d.data() } as ReportingPeriod));
  return periods.sort((a, b) => a.deadlineDate.localeCompare(b.deadlineDate))[0];
};

export const closeReportingPeriod = async (schoolId: string, periodId: string): Promise<void> => {
  await updateDoc(doc(db, 'schools', schoolId, 'reportingPeriods', periodId), { status: 'closed' });
};

/**
 * Release report cards as a FROZEN SNAPSHOT. Routes through the server-side
 * snapshot-and-release-report-cards function, which captures one snapshot doc
 * per class (idempotent — the snapshot is built once and reused when the second
 * audience is released later) and only then sets the release flag(s). The
 * student/parent portals read that snapshot, so grades/comments stop changing
 * after release. Replaces the raw flag-flip release path in the UI.
 *
 * `emailsTriggered` is the half that can fail on its own: the release commits first, then a background
 * worker is triggered to email. If that trigger fails the release still succeeded and NOBODY was
 * emailed — so the caller must surface it rather than reporting plain success. Re-calling this endpoint
 * RESUMES delivery and cannot double-send (per-recipient `emailLog` markers).
 */
export const snapshotAndReleaseReportCards = async (
  schoolId: string,
  periodId: string,
  audience: 'students' | 'parents' | 'both',
): Promise<{ ok: true; classes: number; alreadySnapshotted: boolean; emailsTriggered: boolean; emailRunInFlight: boolean }> => {
  const res = await callAuthedFunction('snapshot-and-release-report-cards', { schoolId, periodId, audience });
  if (!res.ok) {
    let message = 'Could not release report cards.';
    try {
      const data = await res.json();
      if (data?.error) message = data.error as string;
    } catch { /* keep default */ }
    throw new Error(message);
  }
  return res.json();
};

export const submitTeacherReport = async (
  schoolId: string,
  periodId: string,
  teacherUid: string,
  classIds: string[],
  /** Per-class mark snapshots to freeze for this period. Submitting is the moment
   *  the marks become "the marks for this cycle", so this is where the period
   *  record is captured — see ClassMarkSnapshot in types.ts. Optional so existing
   *  callers keep working; without it the submission is a workflow record only. */
  snapshots?: ClassMarkSnapshot[],
): Promise<{ snapshotFailures: string[] }> => {
  const ref = doc(db, 'schools', schoolId, 'reportSubmissions', periodId, 'teachers', teacherUid);
  const existing = await getDoc(ref);
  const now = new Date().toISOString();

  // Determine if submission is late
  const periodRef = doc(db, 'schools', schoolId, 'reportingPeriods', periodId);
  const periodSnap = await getDoc(periodRef);
  const isLate = periodSnap.exists()
    ? now > periodSnap.data().deadlineDate
    : false;

  const payload: Omit<ReportSubmission, 'id'> = {
    periodId,
    schoolId,
    teacherUid,
    submittedAt: now,
    isLate,
    classIds,
    status: existing.exists() ? 'resubmitted' : 'submitted',
  };
  await setDoc(ref, payload);

  // Freeze the marks for this period, one doc per class. Written AFTER the
  // submission record so a snapshot never exists without its submission. A
  // resubmission overwrites its class's snapshot — the latest submitted marks are
  // the period's marks.
  const snapshotFailures: string[] = [];
  for (const snap of snapshots || []) {
    try {
      await setDoc(doc(ref, 'marks', snap.classId), removeUndefined(snap as unknown as Record<string, unknown>));
    } catch (err) {
      // The submission itself succeeded, so this isn't fatal — but it IS returned
      // to the caller (and logged), because a period silently missing its frozen
      // record is the exact failure this whole spine exists to prevent.
      console.error(`[submitTeacherReport] FAILED to write mark snapshot for class ${snap.classId} (period ${periodId}) — this period will have no frozen record for it:`, err);
      snapshotFailures.push(snap.classId);
    }
  }

  // Drop snapshots for classes no longer in this submission. Without this, a
  // narrower resubmission (teacher removes a class) leaves the previous run's
  // frozen marks behind, and they'd be served as this period's official record.
  // Only prune when the caller is snapshot-aware, so a legacy caller passing just
  // classIds can never wipe an existing record.
  if (snapshots) {
    try {
      const keep = new Set(classIds);
      const existing = await getDocs(collection(ref, 'marks'));
      await Promise.all(existing.docs.filter(d => !keep.has(d.id)).map(d => deleteDoc(d.ref)));
    } catch (err) {
      console.error(`[submitTeacherReport] failed to prune stale mark snapshots (period ${periodId}):`, err);
    }
  }

  return { snapshotFailures };
};

/** Read back a period's frozen marks for one teacher (admin or the teacher). */
export const getMarkSnapshots = async (
  schoolId: string,
  periodId: string,
  teacherUid: string,
): Promise<ClassMarkSnapshot[]> => {
  const snap = await getDocs(collection(db, 'schools', schoolId, 'reportSubmissions', periodId, 'teachers', teacherUid, 'marks'));
  return snap.docs.map(d => d.data() as ClassMarkSnapshot);
};

export const getReportSubmissions = async (
  schoolId: string,
  periodId: string,
): Promise<Record<string, ReportSubmission>> => {
  const snap = await getDocs(collection(db, 'schools', schoolId, 'reportSubmissions', periodId, 'teachers'));
  const result: Record<string, ReportSubmission> = {};
  snap.docs.forEach(d => {
    result[d.id] = { id: d.id, ...d.data() } as ReportSubmission;
  });
  return result;
};

/** Admin sends feedback on a teacher's report submission */
export const sendReportFeedback = async (
  schoolId: string,
  periodId: string,
  teacherUid: string,
  feedback: string,
  status: 'approved' | 'needs_revision',
  adminUid: string,
): Promise<void> => {
  const ref = doc(db, 'schools', schoolId, 'reportSubmissions', periodId, 'teachers', teacherUid);
  await updateDoc(ref, {
    status,
    adminFeedback: feedback,
    adminFeedbackBy: adminUid,
    adminFeedbackAt: new Date().toISOString(),
  });

  // Audit log
  import('./auditLog').then(({ logAuditEvent }) => {
    logAuditEvent(schoolId, {
      action: `report_${status}`,
      actorUid: adminUid,
      actorName: '',
      details: `Teacher ${teacherUid} report ${status}${feedback ? ': ' + feedback : ''}`,
      category: 'reporting',
    });
  }).catch(() => {});
};

export const updateReportingPeriodTemplate = async (
  schoolId: string,
  periodId: string,
  templateFields: ReportTemplateField[],
): Promise<void> => {
  await updateDoc(doc(db, 'schools', schoolId, 'reportingPeriods', periodId), { templateFields });
};

// ─── Class Transfers ─────────────────────────────────────────────────────────

export const createClassTransfer = async (
  data: Omit<ClassTransferDocument, 'id' | 'createdAt' | 'resolvedAt' | 'status'>,
): Promise<ClassTransferDocument> => {
  const now = new Date().toISOString();
  const payload = { ...data, status: 'pending' as const, createdAt: now, resolvedAt: null };
  const ref = await addDoc(collection(db, 'classTransfers'), payload);
  // Notify the receiving teacher
  await createNotification({
    recipientUid: data.toTeacherUid,
    schoolId: data.schoolId,
    title: 'Class transfer request',
    message: 'A class has been transferred to you. Please review and accept or decline.',
    type: 'transfer_initiated',
  });
  return { id: ref.id, ...payload };
};

export const getClassTransfer = async (transferId: string): Promise<ClassTransferDocument | null> => {
  const snap = await getDoc(doc(db, 'classTransfers', transferId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as ClassTransferDocument;
};

export const getPendingTransfersForTeacher = async (teacherUid: string): Promise<ClassTransferDocument[]> => {
  const q = query(
    collection(db, 'classTransfers'),
    where('toTeacherUid', '==', teacherUid),
    where('status', '==', 'pending'),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as ClassTransferDocument));
};

export const getPendingTransfersForSchool = async (schoolId: string): Promise<ClassTransferDocument[]> => {
  const q = query(
    collection(db, 'classTransfers'),
    where('schoolId', '==', schoolId),
    where('status', '==', 'pending'),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as ClassTransferDocument));
};

export const acceptClassTransfer = async (transferId: string): Promise<void> => {
  const transferRef = doc(db, 'classTransfers', transferId);
  const transferSnap = await getDoc(transferRef);
  if (!transferSnap.exists()) throw new Error('Transfer not found');
  const transfer = transferSnap.data() as Omit<ClassTransferDocument, 'id'>;
  if (transfer.status !== 'pending') throw new Error('Transfer is no longer pending');

  await runTransaction(db, async (txn) => {
    // Update class ownership
    const classRef = doc(db, 'classes', transfer.classId);
    txn.update(classRef, {
      ownerId: transfer.toTeacherUid,
      // Same reason as transferClass: a class that arrived via an admin
      // off-boarding still names that handover, and once it moves on again those
      // fields describe a state that is no longer current.
      previousOwnerId: deleteField(),
      transferredTo: deleteField(),
      transferredAt: deleteField(),
      transferredBy: deleteField(),
    });
    // Mark transfer as accepted
    txn.update(transferRef, { status: 'accepted', resolvedAt: new Date().toISOString() });
  });
};

export const declineClassTransfer = async (transferId: string): Promise<void> => {
  await updateDoc(doc(db, 'classTransfers', transferId), {
    status: 'declined',
    resolvedAt: new Date().toISOString(),
  });
};

// ─── Notifications ───────────────────────────────────────────────────────────

export const subscribeToNotifications = (
  uid: string,
  onUpdate: (notifications: NotificationDocument[]) => void,
): (() => void) => {
  const q = query(
    collection(db, 'notifications'),
    where('recipientUid', '==', uid),
  );
  return onSnapshot(
    q,
    (snap) => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() } as NotificationDocument));
      onUpdate(docs.sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
    },
    (err) => {
      // Defensive: if the listener errors (permission denied, network),
      // call onUpdate with an empty list so the consumer can stop
      // showing a loading spinner. Without this, the Notifications tab
      // spun forever when the rule was missing.
      console.warn('[notifications] listener error — surfacing empty list', err);
      onUpdate([]);
    },
  );
};

export const markNotificationRead = async (notificationId: string): Promise<void> => {
  await updateDoc(doc(db, 'notifications', notificationId), { read: true });
};

export const markAllNotificationsRead = async (uid: string): Promise<void> => {
  const q = query(
    collection(db, 'notifications'),
    where('recipientUid', '==', uid),
    where('read', '==', false),
  );
  const snap = await getDocs(q);
  const promises = snap.docs.map(d => updateDoc(d.ref, { read: true }));
  await Promise.all(promises);
};

export const createNotification = async (
  data: Omit<NotificationDocument, 'id' | 'createdAt' | 'read' | 'senderUid'>,
): Promise<NotificationDocument> => {
  // ATTRIBUTION, stamped here so no caller can forget it.
  //
  // The rules previously admitted any authenticated user creating a
  // notification for ANY recipient with any title and body — so a student could
  // send their teacher "School Admin: your account has been suspended" and it
  // would render exactly like a real one. Pinning senderUid to the caller makes
  // every notification attributable, and the rules additionally refuse students
  // outright, since no student flow creates one.
  const payload = {
    ...data,
    senderUid: auth.currentUser?.uid ?? '',
    read: false,
    createdAt: new Date().toISOString(),
  };
  const ref = await addDoc(collection(db, 'notifications'), payload);
  return { id: ref.id, ...payload };
};

export const deleteNotification = async (notificationId: string): Promise<void> => {
  await deleteDoc(doc(db, 'notifications', notificationId));
};

// ─── Intervention Actions ───────────────────────────────────────────────────

export interface InterventionActionRecord {
  id: string;
  studentId: string;
  classId: string;
  teacherUid: string;
  schoolId: string;
  actionType: 'parent_contact' | 'iep_review' | 'assessment_referral';
  tierLevel: 1 | 2 | 3;
  aiSuggestionTitle: string;
  createdAt: string;
  notes: string;
  status: 'logged' | 'in_progress' | 'resolved';
}

export const createInterventionAction = async (
  schoolId: string,
  data: Omit<InterventionActionRecord, 'id' | 'createdAt' | 'status'>,
): Promise<InterventionActionRecord> => {
  const payload = { ...data, createdAt: new Date().toISOString(), status: 'logged' as const };
  const ref = await addDoc(collection(db, 'schools', schoolId, 'interventionActions'), payload);
  return { id: ref.id, ...payload };
};

export const getInterventionActionsForStudent = async (
  schoolId: string,
  studentId: string,
): Promise<InterventionActionRecord[]> => {
  const q = query(
    collection(db, 'schools', schoolId, 'interventionActions'),
    where('studentId', '==', studentId),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() } as InterventionActionRecord))
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
};

export const updateInterventionAction = async (
  schoolId: string,
  actionId: string,
  updates: Partial<Pick<InterventionActionRecord, 'status' | 'notes'>>,
): Promise<void> => {
  await updateDoc(doc(db, 'schools', schoolId, 'interventionActions', actionId), updates);
};

// ─── School Email System ────────────────────────────────────────────────────

export const getSchoolEmails = async (schoolId: string): Promise<SchoolEmail[]> => {
  const snap = await getDocs(collection(db, `schools/${schoolId}/emails`));
  return snap.docs.map(d => ({ id: d.id, ...d.data() })) as SchoolEmail[];
};

export const createSchoolEmail = async (schoolId: string, email: Omit<SchoolEmail, 'id'>): Promise<string> => {
  const ref = await addDoc(collection(db, `schools/${schoolId}/emails`), removeUndefined(email as unknown as Record<string, unknown>));
  return ref.id;
};

export const updateSchoolEmail = async (schoolId: string, emailId: string, updates: Partial<SchoolEmail>): Promise<void> => {
  await updateDoc(doc(db, `schools/${schoolId}/emails/${emailId}`), removeUndefined(updates as unknown as Record<string, unknown>));
};

export const deleteSchoolEmail = async (schoolId: string, emailId: string): Promise<void> => {
  await deleteDoc(doc(db, `schools/${schoolId}/emails/${emailId}`));
};

export const getSchoolEmailTemplates = async (schoolId: string): Promise<SchoolEmailTemplate[]> => {
  const snap = await getDocs(collection(db, `schools/${schoolId}/emailTemplates`));
  return snap.docs.map(d => ({ id: d.id, ...d.data() })) as SchoolEmailTemplate[];
};

export const createSchoolEmailTemplate = async (schoolId: string, template: Omit<SchoolEmailTemplate, 'id'>): Promise<string> => {
  const ref = await addDoc(collection(db, `schools/${schoolId}/emailTemplates`), removeUndefined(template as unknown as Record<string, unknown>));
  return ref.id;
};

export const deleteSchoolEmailTemplate = async (schoolId: string, templateId: string): Promise<void> => {
  await deleteDoc(doc(db, `schools/${schoolId}/emailTemplates/${templateId}`));
};

// ─── Internal Messaging (Admin ↔ Teacher) ───────────────────────────────────

/** Get or create a conversation between two users */
export const getOrCreateConversation = async (
  schoolId: string,
  user1Uid: string, user1Name: string,
  user2Uid: string, user2Name: string,
  subject: string,
): Promise<string> => {
  // Check if conversation already exists between these two
  const q = query(
    collection(db, `schools/${schoolId}/conversations`),
    where('participantUids', 'array-contains', user1Uid),
  );
  const snap = await getDocs(q);
  const existing = snap.docs.find(d => {
    const data = d.data();
    return data.participantUids?.includes(user2Uid);
  });

  if (existing) return existing.id;

  // Create new conversation
  const ref = await addDoc(collection(db, `schools/${schoolId}/conversations`), {
    schoolId,
    participantUids: [user1Uid, user2Uid],
    participantNames: { [user1Uid]: user1Name, [user2Uid]: user2Name },
    subject,
    lastMessageAt: new Date().toISOString(),
    lastMessagePreview: '',
    unreadCount: { [user1Uid]: 0, [user2Uid]: 0 },
    createdAt: new Date().toISOString(),
  });
  return ref.id;
};

/** Send a message in a conversation */
export const sendMessage = async (
  schoolId: string,
  conversationId: string,
  message: Omit<InternalMessage, 'id' | 'createdAt' | 'read'>,
): Promise<string> => {
  const now = new Date().toISOString();
  const ref = await addDoc(
    collection(db, `schools/${schoolId}/conversations/${conversationId}/messages`),
    removeUndefined({ ...message, read: false, createdAt: now } as unknown as Record<string, unknown>),
  );

  // Update conversation metadata
  await updateDoc(doc(db, `schools/${schoolId}/conversations/${conversationId}`), {
    lastMessageAt: now,
    lastMessagePreview: message.body.substring(0, 100),
    [`unreadCount.${message.recipientUid}`]: (await getDoc(doc(db, `schools/${schoolId}/conversations/${conversationId}`))).data()?.unreadCount?.[message.recipientUid] + 1 || 1,
  });

  return ref.id;
};

/** Subscribe to conversations for a user */
export const subscribeToConversations = (
  schoolId: string,
  userUid: string,
  onUpdate: (conversations: Conversation[]) => void,
): (() => void) => {
  const q = query(
    collection(db, `schools/${schoolId}/conversations`),
    where('participantUids', 'array-contains', userUid),
  );
  return onSnapshot(q, (snap) => {
    const convos = snap.docs
      .map(d => ({ id: d.id, ...d.data() } as Conversation))
      .sort((a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime());
    onUpdate(convos);
  });
};

/** Subscribe to messages in a conversation */
export const subscribeToMessages = (
  schoolId: string,
  conversationId: string,
  onUpdate: (messages: InternalMessage[]) => void,
): (() => void) => {
  const q = query(
    collection(db, `schools/${schoolId}/conversations/${conversationId}/messages`),
  );
  return onSnapshot(q, (snap) => {
    const msgs = snap.docs
      .map(d => ({ id: d.id, ...d.data() } as InternalMessage))
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    onUpdate(msgs);
  });
};

/** Mark all messages in a conversation as read for a user */
export const markConversationRead = async (
  schoolId: string,
  conversationId: string,
  userUid: string,
): Promise<void> => {
  // Reset unread count
  await updateDoc(doc(db, `schools/${schoolId}/conversations/${conversationId}`), {
    [`unreadCount.${userUid}`]: 0,
  });

  // Mark individual messages as read
  const q = query(
    collection(db, `schools/${schoolId}/conversations/${conversationId}/messages`),
    where('recipientUid', '==', userUid),
    where('read', '==', false),
  );
  const snap = await getDocs(q);
  for (const msgDoc of snap.docs) {
    await updateDoc(msgDoc.ref, { read: true });
  }
};


/**
 * Append a cross-posted copy (and its assessment) to one class.
 *
 * arrayUnion, NOT a read-modify-write of the whole array: a co-teacher posting
 * at the same moment would otherwise be clobbered by whichever write landed
 * second. addAssessment already works this way; the legacy whole-array stream
 * writes are the pattern to avoid.
 *
 * The elements are stripped of `undefined` FIRST. Firestore rejects undefined
 * inside an arrayUnion, and removeUndefined deliberately does not recurse into
 * FieldValue sentinels — so cleaning after wrapping would do nothing.
 */
export const appendCrossPost = async (
  classId: string,
  post: StreamPost,
  assessment?: Assessment,
): Promise<void> => {
  /**
   * aiGradingInstructions is TEACHER-PRIVATE and must never reach the class
   * doc, which every classmate can read. getTeacherClasses hydrates it onto the
   * in-memory assessment from classes/{id}/private/aiGrading, so a plain spread
   * of a live assessment carries it — and writing it here would have published
   * a teacher's marking notes ("be generous on conventions for ELLs") to every
   * student in the target class.
   *
   * Every other writer strips it: addAssessment, updateAssessment, createClass.
   * This one now does too, and re-homes it to the TARGET's own private doc so
   * the copy's AI grading is steered rather than silently unsteered.
   */
  const instructions = assessment
    ? (assessment as { aiGradingInstructions?: string }).aiGradingInstructions
    : undefined;
  const safeAssessment = assessment
    ? (() => { const { aiGradingInstructions: _drop, ...rest } = assessment as Assessment & { aiGradingInstructions?: string }; return rest as Assessment; })()
    : undefined;

  await updateDoc(doc(db, 'classes', classId), {
    stream: arrayUnion(stripUndefinedDeep(post)),
    ...(safeAssessment ? { assessments: arrayUnion(stripUndefinedDeep(safeAssessment)) } : {}),
  });

  if (safeAssessment && instructions) {
    // Best-effort: the post and its gradebook column are already in place, and
    // losing the steering must not look like the whole copy failed.
    try {
      await setDoc(aiInstructionsRef(classId), { [safeAssessment.id]: instructions }, { merge: true });
    } catch (e) {
      console.error('cross-post: could not carry AI grading instructions to', classId, e);
    }
  }
};
